<template>
  <div class="container-fluid sourcing-board">
    <!-- ① 상단 바 — 제목 · 검색 · 밀도 · 담기/저장.
         chrome 은 이 줄과 아래 깔때기 줄까지 2줄뿐이다. 패널을 더 붙이지 말 것(정본 §1). -->
    <div class="sb-bar">
      <div class="sb-bar__head">
        <h4 class="sb-bar__title">소싱관리</h4>
        <p class="sb-bar__desc">{{ description }}</p>
        <p class="sb-rate" v-if="integratedRate">
          적용 통합통관환율 <b>{{ integratedRate }}</b>원/元
          <span class="sb-rate__sep">·</span> 검수 {{ formatNumber(FEE_INSPECT) }}원
          <span class="sb-rate__sep">·</span> 바코드 {{ formatNumber(FEE_BARCODE) }}원
          <span class="sb-rate__sep">·</span> 합계 × 1.1(부가세)
        </p>
        <p class="sb-rate" v-else-if="rateMissing">통합통관환율 없음 — 저장된 환율이 없어 매입가를 계산하지 않아요</p>
      </div>
      <div class="sb-bar__tools">
        <input type="search" class="sb-search" v-model="query" placeholder="상품·키워드·offerId 검색">
        <!-- 밀도 = 행 높이가 아니라 하위 컬럼 개수를 바꾼다(정본 §2) -->
        <div class="sb-dens">
          <button v-for="d in DENSITIES" :key="d.key" type="button" class="sb-dens__b"
                  :class="{ 'sb-dens__b--on': density === d.key }"
                  :title="d.hint" @click="setDensity(d.key)">{{ d.text }}</button>
        </div>
        <!-- 조건서 칩 — 시트는 여기서만 열린다. 상시 노출하면 chrome 이 늘어나
             정본 §1 이 실패한 그 모양(패널 sprawl)으로 돌아간다. -->
        <button type="button" class="sb-spec" :aria-expanded="specOpen ? 'true' : 'false'"
                aria-controls="sb-spec-pane" title="소싱 조건서 열기" @click="openSpec">
          <i class="fas fa-sliders-h mr-1"></i>조건서<b v-if="specChipText">{{ specChipText }}</b>
        </button>
        <!-- 승인 대기 — 에이전트가 요청한 쓰기(쿠팡·1688 보내기)를 링크 없이 여기서 승인·반려. 칩 하나만 두고 목록은 옆 시트로(chrome 늘리지 않음) -->
        <ApprovalPendingPanel ref="aprPanel" @loaded="onApprovalsLoaded" />
        <b-button variant="outline-danger" size="sm" :disabled="!selected.length" @click="deleteSelected">
          <i class="fas fa-trash mr-1"></i>선택삭제
        </b-button>
        <b-button variant="outline-secondary" size="sm" @click="addRow">
          <i class="fas fa-plus mr-1"></i>담기
        </b-button>
        <b-button variant="primary" size="sm" :disabled="saving" @click="saveRows">
          <i class="fas fa-save mr-1"></i>{{ saving ? '저장중…' : '저장' }}
        </b-button>
      </div>
    </div>

    <!-- ② 깔때기 한 줄 — 세그먼트 폭이 건수에 비례한다. 넓은 칸이 곧 병목이다.
         클릭하면 그 단계만 남는다(좌측 레일 대신 한 줄로). -->
    <div class="sb-funnel">
      <button type="button" class="sb-funnel__seg sb-funnel__seg--all"
              :class="{ 'sb-funnel__seg--on': stage === 'all' }" @click="setStage('all')">
        <span class="sb-funnel__t">전체</span><b>{{ rows.length }}</b>
      </button>
      <div class="sb-funnel__track">
        <button v-for="b in funnelSegments" :key="b.key" type="button"
                class="sb-funnel__seg"
                :class="['sb-funnel__seg--' + b.key,
                         { 'sb-funnel__seg--on': stage === b.key, 'sb-funnel__seg--empty': !b.count }]"
                :style="{ flexGrow: b.count }"
                :title="b.text + ' ' + b.count + '건'"
                @click="setStage(b.key)">
          <span class="sb-funnel__t">{{ b.text }}</span><b>{{ b.count }}</b>
        </button>
      </div>
    </div>

    <!-- ②-b 승격 배너 — 같은 조건 사유 3회. **한 줄이다.** 패널을 만들지 않는다(정본 §5).
         chrome 2줄 규칙의 유일한 예외 — 조건부라 평소에는 없다. -->
    <div v-if="promo" class="sb-promo">
      <span class="sb-promo__tag">규칙 후보</span>
      <span class="sb-promo__t">「{{ promo.rule }}」를 조건서에 추가할까요?</span>
      <span class="sb-promo__w">같은 사유({{ promo.reason }})로 {{ promo.count }}건 반려 · 적용범위 「{{ promo.scope }}」</span>
      <span class="sb-promo__a">
        <button type="button" class="sb-btn sb-btn--ok" :disabled="promoSaving" @click="acceptPromo">추가</button>
        <button type="button" class="sb-btn" :disabled="promoSaving" @click="promo = null">아니</button>
      </span>
    </div>
    <!-- 조건서 API 가 실패했을 때. 조용히 넘기면 반려는 쌓이는데 승격이 영원히 안 뜬다. -->
    <div v-if="notice" class="sb-notice" role="alert">
      <span>{{ notice }}</span>
      <button type="button" class="sb-notice__x" aria-label="알림 닫기" @click="notice = ''">&times;</button>
    </div>

    <!-- ②-c 조건서가 가린 행 — **없애지 않고 접는다.** 조용히 빠지면 왜 안 보이는지
         영영 모른다(정본 §3.2 「표본 부족을 숨기지 않고 이름 붙여 남긴다」와 같은 원칙). -->
    <div v-if="maskedCount && !chokeRule" class="sb-mask">
      <span class="sb-mask__t">조건서로 {{ maskedCount }}건 가려짐</span>
      <button type="button" class="sb-btn" :aria-pressed="showMasked ? 'true' : 'false'"
              @click="showMasked = !showMasked">{{ showMasked ? '접기' : '보기' }}</button>
    </div>
    <!-- 0건 경고 — 되돌리는 길이다. 승격만 있고 끄는 길이 없으면 조건서는 과적합한다(정본 §5). -->
    <div v-if="chokeRule" class="sb-choke" role="alert">
      <span class="sb-choke__t">
        보이는 행 0건 — 「{{ chokeRule.rule.text }}」가
        {{ rows.length }}건 중 {{ chokeRule.dropped }}건을 떨어뜨렸다.
      </span>
      <span class="sb-choke__a">
        <button type="button" class="sb-btn sb-btn--ok" :disabled="ruleSaving"
                @click="toggleRule(chokeRule.rule)">이 규칙 잠시 끄기</button>
        <button type="button" class="sb-btn" @click="showMasked = true">가려진 행 보기</button>
      </span>
    </div>

    <!-- ③ 표 본체. 컬럼 그룹 = 깔때기 단계, 행은 1줄.
         v-data-table 로는 그룹을 통째로 합친 「색 덩어리」를 만들 수 없어(td 마다 쪼개진다)
         CSS grid 로 짰다. 같은 --sb-tmpl 을 헤더 2단과 모든 행이 공유해 열이 맞는다. -->
    <div class="sb-scroll">
     <div class="sb-table" role="table" aria-label="소싱관리 작업 큐"
          :aria-colcount="colCount" :style="{ '--sb-tmpl': gridTemplate }">
      <!-- 헤더 2단은 한 덩어리로 묶어 sticky 로 띄운다. 깔때기 줄까지 붙이면
           chrome 이 두꺼워져 정본 §1 이 실패한 그 모양이 된다 — 표 헤더만 붙인다. -->
      <div class="sb-head" role="rowgroup">
        <!-- 1단 헤더: 그룹 -->
        <div class="sb-hg" role="row">
          <span class="sb-hg__c sb-c--stick0" role="columnheader"></span>
          <span v-for="(g, gi) in groups" :key="'g-' + g.name"
                class="sb-hg__c" :class="{ 'sb-gs': gi > 0 }"
                role="columnheader" :aria-colspan="g.keys.length"
                :style="{ gridColumn: 'span ' + g.keys.length }">{{ g.name }}</span>
        </div>
        <!-- 2단 헤더: 컬럼. 클릭하면 정렬 -->
        <div class="sb-hf" role="row">
          <span class="sb-hf__c sb-c--stick0" role="columnheader">
            <input type="checkbox" :checked="allChecked" title="전체 선택" aria-label="전체 선택"
                   @change="toggleAll($event.target.checked)">
          </span>
          <template v-for="(g, gi) in groups">
            <button v-for="(k, ki) in g.keys" :key="'h-' + k" type="button"
                    class="sb-hf__c sb-hf__b"
                    :class="{ 'sb-c--n': !!COLS[k].num, 'sb-gs': gi > 0 && ki === 0,
                              'sb-hf__b--on': sortKey === k }"
                    role="columnheader" :aria-sort="ariaSort(k)"
                    :disabled="!SORT_KEYS[k]" @click="setSort(k)">
              {{ COLS[k].text }}<i v-if="sortKey === k" class="fas ml-1"
                                   :class="sortDesc ? 'fa-caret-down' : 'fa-caret-up'"></i>
            </button>
          </template>
        </div>
      </div>

      <div class="sb-body" role="rowgroup">
      <template v-for="row in pageRows">
      <div :key="row._uid" class="sb-row" role="row"
           :class="{ 'sb-row--on': isSelected(row), 'sb-row--open': expandedUid === row._uid,
                     'sb-row--masked': !!maskedUids[row._uid] }">
        <span class="sb-c sb-c--sel sb-c--stick0" role="cell" :title="maskedReason(row)">
          <input type="checkbox" :checked="isSelected(row)" :aria-label="(row.상품명 || '이름 없는 행') + ' 선택'"
                 @change="toggleRow(row, $event.target.checked)">
        </span>
        <template v-for="(g, gi) in groups">
          <!-- 막힌 단계 = 그룹 통째로 색 덩어리 + 행동 문구 하나.
               그 뒤 단계는 빈칸이 아니라 「앞이 먼저」로 이유를 적는다. -->
          <!-- 덩어리는 클릭 대상이라 키보드로도 닿아야 한다 — grid span 이 필요해 button 으로
               못 바꾸는 자리라, focus 를 받을 수 있게 tabindex + Enter/Space 를 직접 단다. -->
          <span v-if="blockOf(row, g)" :key="'b-' + g.name"
                class="sb-blk" :class="['sb-blk--' + blockOf(row, g).tone, { 'sb-gs': gi > 0 }]"
                role="cell" :aria-colspan="g.keys.length"
                :style="{ gridColumn: 'span ' + g.keys.length }"
                :title="blockOf(row, g).hint"
                :tabindex="blockOf(row, g).tone === 'todo' ? 0 : null"
                :aria-label="blockOf(row, g).tone === 'todo'
                  ? (row.상품명 || '이름 없는 행') + ' — ' + blockOf(row, g).text : null"
                @click="onBlockClick(row, g)"
                @keydown.enter.prevent="onBlockClick(row, g)"
                @keydown.space.prevent="onBlockClick(row, g)">{{ blockOf(row, g).text }}</span>
          <template v-else>
            <span v-for="(k, ki) in g.keys" :key="'c-' + k"
                  class="sb-c" role="cell"
                  :class="{ 'sb-c--n': !!COLS[k].num, 'sb-gs': gi > 0 && ki === 0,
                            'sb-c--stick1': k === 'nm' }">

              <!-- 상품 -->
              <template v-if="k === 'nm'">
                <span class="sb-thumb" :style="thumbStyle(row.썸네일)"></span>
                <span class="sb-nm" :title="row.상품명 || ''">{{ row.상품명 || '(이름 없음)' }}</span>
                <button type="button" class="sb-mini" title="상세 보기" @click.stop="openDetail(row)">
                  <i class="fas fa-list-ul"></i>
                </button>
              </template>

              <!-- 진입점수 = 기회 × ROI × 시장파이. ROI·시장파이 중 하나라도 미산출이면 「잠정」 -->
              <template v-else-if="k === 'sc'">
                <span v-if="sig(row).score" class="sb-score"
                      :class="['sb-score--' + sig(row).score.tone, { 'sb-score--prov': !sig(row).score.firm }]"
                      :title="sig(row).score.hint">
                  <b>{{ sig(row).score.text }}</b><s v-if="!sig(row).score.firm">잠정</s>
                </span>
                <span v-else class="sb-mut">-</span>
              </template>

              <!-- 1p 가격위치 — **다이얼이다.** 막대를 끌면 그 자리 가격이 판매가로 들어가고,
                   판매가를 고치면 막대가 따라 움직인다(양방향).
                   분포 출처는 보드마다 다르다 — 로켓그로스는 1페이지 min~max, 판매/분석은
                   비즈니스인사이트의 25~75% 구간. 다이얼은 양쪽에서 똑같이 쓸모가 있다.
                   판매가를 아직 안 정한 행에서도 분포만 있으면 뜬다 — 그때가 제일 필요한 순간이다.
                   분포가 없으면 다이얼을 감춘다: 범위를 모르는데 슬라이더를 보여주면
                   아무 뜻도 없는 값을 넣게 된다. -->
              <template v-else-if="k === 'ppos'">
                <span v-if="sig(row).pdist" class="sb-pos"
                      :class="['sb-pos--' + (sig(row).ppos ? sig(row).ppos.tone : 'flat'),
                               { 'sb-pos--unset': !sig(row).ppos }]"
                      :title="pposTitle(row)">
                  <span class="sb-dial">
                    <svg width="44" height="14" viewBox="0 0 44 14" aria-hidden="true">
                      <line x1="1" y1="7" x2="43" y2="7" stroke="#dfe3e8" stroke-width="1"></line>
                      <rect v-if="sig(row).ppos && sig(row).ppos.box"
                            x="11.5" y="3.5" width="21" height="7" rx="1.5" fill="#eef1f5"></rect>
                      <line x1="22" y1="2.5" x2="22" y2="11.5" stroke="#c3cad6" stroke-width="1"></line>
                      <!-- 판매가가 없으면 눈금선을 안 그린다. 0% 에 선을 그으면 「제일 싼 값으로
                           정했다」로 읽힌다 — 안 정한 것과 제일 싸게 정한 것은 다른 상태다. -->
                      <line v-if="sig(row).ppos" :x1="sig(row).ppos.x" y1="1" :x2="sig(row).ppos.x" y2="13"
                            stroke="currentColor" stroke-width="2"></line>
                    </svg>
                    <!-- range 를 막대 위에 투명하게 겹친다. 드래그·키보드(←/→)·포커스를
                         브라우저가 준다 — SVG 에 포인터 핸들러를 달면 마우스로만 된다. -->
                    <input type="range" class="sb-dial__r" min="0" max="100" step="1"
                           :value="sig(row).ppos ? Math.round(sig(row).ppos.pct) : 0"
                           :aria-label="(row.상품명 || '이름 없는 행') + ' 1페이지 가격위치'"
                           :aria-valuetext="pposTitle(row)"
                           @input="setPriceByPct(row, $event.target.value)">
                  </span>
                  <b>{{ sig(row).ppos ? sig(row).ppos.label : '미정' }}</b>
                  <s v-if="priceRangeStale(row)">옛 키워드</s>
                  <button v-if="isGrowth" type="button" class="sb-mini"
                          :disabled="priceRangeLoadingUid === row._uid"
                          title="키워드 1페이지 가격대 다시 불러오기"
                          @click.stop="fetchPriceRange(row)">
                    <i class="fas" :class="priceRangeLoadingUid === row._uid ? 'fa-spinner fa-spin' : 'fa-sync-alt'"></i>
                  </button>
                </span>
                <span v-else class="sb-mut" :title="pposEmptyHint()">
                  <button v-if="isGrowth" type="button" class="sb-mini"
                          :disabled="priceRangeLoadingUid === row._uid"
                          title="키워드 1페이지 가격대 불러오기"
                          @click.stop="fetchPriceRange(row)">
                    <i class="fas" :class="priceRangeLoadingUid === row._uid ? 'fa-spinner fa-spin' : 'fa-download'"></i>
                  </button>
                  <template v-else>-</template>
                </span>
              </template>

              <!-- 1p 내 위치 막대 2종 — 같은 모양, 색 규칙만 다르다(정본 §3.3) -->
              <template v-else-if="k === 'rpos' || k === 'ipos'">
                <span v-if="sig(row)[k]" class="sb-pos" :class="'sb-pos--' + sig(row)[k].tone"
                      :title="sig(row)[k].hint">
                  <svg width="44" height="14" viewBox="0 0 44 14" aria-hidden="true">
                    <line x1="1" y1="7" x2="43" y2="7" stroke="#dfe3e8" stroke-width="1"></line>
                    <!-- IQR 박스는 사분위가 실제로 올 때만. RPI·노출은 min/median/max 3점뿐이다 -->
                    <rect v-if="sig(row)[k].box" x="11.5" y="3.5" width="21" height="7" rx="1.5" fill="#eef1f5"></rect>
                    <line x1="22" y1="2.5" x2="22" y2="11.5" stroke="#c3cad6" stroke-width="1"></line>
                    <line :x1="sig(row)[k].x" y1="1" :x2="sig(row)[k].x" y2="13"
                          stroke="currentColor" stroke-width="2"></line>
                  </svg>
                  <b>{{ sig(row)[k].label }}</b>
                </span>
                <span v-else class="sb-mut" :title="POS_EMPTY[k]">-</span>
              </template>

              <!-- 키워드 — 판매/분석 보드에서는 담기가 실어 보낸 꼬리표라 읽기 전용이지만,
                   로켓그로스에서는 사람이 직접 넣는 작업 입력이다(1페이지 가격대 조회의 기준). -->
              <template v-else-if="k === 'kw'">
                <input v-if="isGrowth" type="text" class="sb-in sb-in--t" v-model="row.키워드"
                       placeholder="키워드" title="이 키워드의 쿠팡 1페이지 가격으로 가격대를 잡아요">
                <span v-else :title="row.키워드 || ''">{{ row.키워드 || '-' }}</span>
              </template>

              <!-- 기회 — 담기가 실어 보낸 라벨을 읽기만 한다. 보드는 판정하지 않는다(정본 §4) -->
              <template v-else-if="k === 'opp'">
                <span v-if="sig(row).opp" class="sb-opp" :class="'sb-opp--' + sig(row).opp.tone"
                      :title="sig(row).opp.hint">{{ sig(row).opp.label }}</span>
                <span v-else class="sb-mut" title="기회 라벨이 안 실린 경로에서 담긴 행이에요.">-</span>
              </template>

              <!-- 공급 -->
              <template v-else-if="k === 'cny'">
                <input type="number" class="sb-in sb-in--n" v-model.number="row.구매가"
                       title="1688 구매가(CNY)" @input="changeCost(row)">
                <button type="button" class="sb-mini sb-mini--1688" title="1688 상품매칭"
                        @click.stop="openPicker(row)">
                  <i class="fas fa-link"></i>
                  <b v-if="row.상품매칭 && row.상품매칭.length">{{ row.상품매칭.length }}</b>
                </button>
              </template>
              <template v-else-if="k === 'off'">
                <a v-if="row.alibabaLink" :href="row.alibabaLink" target="_blank" rel="noopener"
                   class="sb-off" :title="row.alibabaLink">{{ row.offerId || '1688' }}</a>
                <span v-else-if="row.offerId" class="sb-off">{{ row.offerId }}</span>
                <span v-else class="sb-mut">-</span>
              </template>

              <!-- 스펙 -->
              <template v-else-if="k === 'vol'">
                <input type="number" class="sb-in sb-in--xs" title="가로(mm)" v-model.number="row.sizeX"
                       placeholder="가로" @input="changeCost(row)">
                <input type="number" class="sb-in sb-in--xs" title="세로(mm)" v-model.number="row.sizeY"
                       placeholder="세로" @input="changeCost(row)">
                <input type="number" class="sb-in sb-in--xs" title="높이(mm)" v-model.number="row.sizeZ"
                       placeholder="높이" @input="changeCost(row)">
              </template>
              <template v-else-if="k === 'wt'">
                <input type="number" class="sb-in sb-in--n" v-model.number="row.weight">
              </template>
              <template v-else-if="k === 'mr'">
                <span :title="milkrunTitle(row)">
                  {{ formatNumber(row.밀크런예상) }}<s v-if="row.밀크런계수추정">추정</s>
                </span>
              </template>

              <!-- 원가 -->
              <template v-else-if="k === 'buy'">
                <span class="sb-strong" :title="costBreakdownText(row)">{{ formatNumber(row.매입가) }}</span>
              </template>
              <template v-else-if="k === 'sell'">
                <input type="number" class="sb-in sb-in--n" v-model.number="row.판매가" @input="changeCost(row)">
              </template>
              <template v-else-if="k === 'frate'">
                <input type="number" class="sb-in sb-in--n" v-model.number="row.수수료율" @input="changeCost(row)">
              </template>
              <template v-else-if="k === 'feeC'">
                <span>{{ formatNumber(row.수수료) }}</span>
                <button type="button" class="sb-mini" :disabled="feeLoadingUid === row._uid"
                        title="wing 실수수료 불러오기" @click.stop="getCommission(row)">
                  <i class="fas" :class="feeLoadingUid === row._uid ? 'fa-spinner fa-spin' : 'fa-sync-alt'"></i>
                </button>
              </template>
              <template v-else-if="k === 'mg'">
                <span :class="signClass(row.마진)">{{ formatNumber(row.마진) }}</span>
              </template>
              <template v-else-if="k === 'mgr'">
                <span :class="signClass(row.마진율)">{{ row.마진율 != null ? row.마진율 + '%' : '-' }}</span>
              </template>
              <template v-else-if="k === 'roi'">
                <span :class="signClass(row.ROI)">{{ row.ROI != null ? row.ROI + '%' : '-' }}</span>
              </template>
              <template v-else-if="k === 'opts'">
                <button type="button" class="sb-chipbtn" @click.stop="openOptions(row)">
                  <i class="fas fa-layer-group mr-1"></i>{{ (row.옵션 && row.옵션.length) ? row.옵션.length + '개' : '옵션' }}
                </button>
              </template>

              <!-- 판정 -->
              <!-- 판정 칸이 곧 심사 펼침 트리거다. 행 전체를 클릭 핸들러로 덮으면
                   안에 든 입력칸·버튼으로 이벤트가 새므로 명시적인 트리거 하나만 둔다. -->
              <template v-else-if="k === 'act'">
                <button type="button" class="sb-verdict" :class="'sb-verdict--' + sig(row).verdict.tone"
                        :title="sig(row).verdict.hint"
                        :aria-expanded="expandedUid === row._uid ? 'true' : 'false'"
                        :aria-controls="'sb-exp-' + row._uid"
                        @click.stop="toggleExpand(row)">
                  {{ sig(row).verdict.text }}
                </button>
                <!-- 1688 협상 메모 작은 표 — 펼치기 전에도 협상·대화가 있는 행을 알아보게(cpl-nego-negomemo-1) -->
                <span v-if="hasNego(row)" class="sb-nego-tag"
                      :title="'1688 협상 기록' + (negoThreadCount(row) ? ' · 대화 ' + negoThreadCount(row) + '건' : '') + ' — 판정 칸을 눌러 펼치면 보여요'">
                  협상<span v-if="negoThreadCount(row)"> · {{ negoThreadCount(row) }}</span>
                </span>
                <!-- 등록 대기 행 ↔ 쿠팡 등록(Open API) 승인 대기 — 상품명으로 잇는다. 누르면 승인 시트를 그 건으로 연다 -->
                <button v-if="sig(row).bucket === 'toReg' && approvalFor(row)" type="button"
                        class="sb-apr" :class="'sb-apr--' + (approvalFor(row).ambiguous ? 'ambig' : approvalFor(row).it.status)"
                        :title="approvalFor(row).ambiguous
                          ? '같은 이름의 행이나 승인 건이 여럿이라 어느 건인지 정하지 못했어요 — 클릭하면 승인 대기 목록을 열어요'
                          : '쿠팡 등록 승인 ' + approvalLabel(approvalFor(row)) + ' — 클릭하면 승인 대기 목록에서 열어요'"
                        @click.stop="openApproval(row)">{{ approvalLabel(approvalFor(row)) }}</button>
              </template>
              <template v-else-if="k === 'stg'">
                <span class="sb-prog">
                  <span v-for="(s, si) in STAGES" :key="si" class="sb-prog__step"
                        :class="['sb-prog__step--' + stageState(row, si),
                                 { 'sb-prog__step--clickable': si >= STAGES.length - 2 }]"
                        :title="stageTitle(si)"
                        :tabindex="si >= STAGES.length - 2 ? 0 : null"
                        :role="si >= STAGES.length - 2 ? 'button' : null"
                        @click.stop="onStageClick(row, si)"
                        @keydown.enter.prevent="onStageClick(row, si)"
                        @keydown.space.prevent="onStageClick(row, si)">{{ s }}</span>
                </span>
              </template>
              <template v-else-if="k === 'memo'">
                <input type="text" class="sb-in sb-in--t" v-model="row.메모" placeholder="메모">
              </template>

              <!-- 벤치마킹(쿠팡 링크) — 상세 밀도에서만. 상세 모달에도 같은 입력이 있다 -->
              <template v-else-if="k === 'bench'">
                <input type="text" class="sb-in sb-in--t" v-model="row.벤치마킹링크"
                       :placeholder="row.벤치마킹상품 || '쿠팡 상품링크'" :title="row.벤치마킹상품 || ''">
                <button type="button" class="sb-mini" :disabled="benchLoadingUid === row._uid"
                        title="쿠팡 상품정보 불러오기" @click.stop="fetchBenchmark(row)">
                  <i class="fas" :class="benchLoadingUid === row._uid ? 'fa-spinner fa-spin' : 'fa-download'"></i>
                </button>
              </template>

              <template v-else>{{ cellText(row, k) }}</template>
            </span>
          </template>
        </template>
      </div>

      <!-- ③-b 인라인 심사 — 패널을 새로 만들지 않는다. 표 안에서 행 아래로 펼쳐지고
           한 번에 하나만 열린다(정본 §1 「첫 시도가 실패한 이유」). -->
      <div v-if="expandedUid === row._uid" :key="row._uid + '-exp'" class="sb-exp" role="row">
        <div class="sb-exp__cell" role="cell" :aria-colspan="colCount" :id="'sb-exp-' + row._uid">
          <div class="sb-exp__in">
            <!-- 위: 왼쪽 레퍼런스(쿠팡) ↔ 오른쪽 1688 후보 타일 -->
            <div class="sb-exp__sheet">
              <div class="sb-ref">
                <span class="sb-cap">레퍼런스 · 쿠팡</span>
                <span class="sb-ref__img" :style="thumbStyle(row.벤치마킹썸네일 || row.썸네일)"></span>
                <span class="sb-ref__m">
                  <b>{{ formatNumber(row.벤치마킹판매가 != null ? row.벤치마킹판매가 : row.판매가) }}원</b><br>
                  RPI {{ cellText(row, 'rpi') }} · CTR {{ cellText(row, 'ctr') }} · CVR {{ cellText(row, 'cvr') }}
                </span>
                <a v-if="row.벤치마킹링크" :href="row.벤치마킹링크" target="_blank" rel="noopener"
                   class="sb-ref__lnk">쿠팡 상품 열기</a>
              </div>
              <div class="sb-cands">
                <div class="sb-cap sb-cands__cap">
                  <span>1688 후보 {{ candidatesOf(row).length }}건</span>
                  <button type="button" class="sb-chipbtn" @click="openPicker(row)">1688 찾기</button>
                </div>
                <div v-if="candidatesOf(row).length" class="sb-cands__grid">
                  <!-- 이미지가 죽어도 타일을 빼지 않는다. 번호가 밀리면 사람이 잘못 고른다(정본 §6). -->
                  <button v-for="(c, ci) in candidatesOf(row)" :key="ci" type="button"
                          class="sb-cand" :class="{ 'sb-cand--on': (row.currentindex || 0) === ci }"
                          :aria-label="(ci + 1) + '번 후보 적용'"
                          :aria-pressed="(row.currentindex || 0) === ci ? 'true' : 'false'"
                          @click="applyCandidate(row, ci)">
                    <img v-if="c.pic_url && !imgFail[row._uid + '|' + ci]" class="sb-cand__im"
                         :src="c.pic_url" alt="" @error="markImgFail(row._uid, ci)">
                    <span v-else class="sb-cand__im sb-cand__im--x">불러오기 실패</span>
                    <span class="sb-cand__lb">
                      <span>{{ ci + 1 }}</span>
                      <b>{{ c.price != null && c.price !== '' ? c.price + '¥' : '-' }}</b>
                    </span>
                    <span v-if="fitOf(c) != null" class="sb-cand__cf">일치 {{ fitOf(c) }}%</span>
                  </button>
                </div>
                <p v-else class="sb-exp__none">
                  아직 1688 후보가 없어요. 「1688 찾기」로 링크를 넣으면 이미지·옵션을 가져와요.
                </p>
              </div>
            </div>
            <!-- 가운데: 1688 협상 메모 — 정리(사람·도구) + 대화 원본(도구가 쌓음 · 읽기만) · 판 #300 -->
            <div class="sb-nego">
              <div class="sb-nego__hd">
                <span class="sb-cap">1688 협상 메모</span>
                <span v-if="row.negotiation && row.negotiation.negotiatedAt" class="sb-nego__at">협상 {{ negoTime(row.negotiation.negotiatedAt) }}</span>
              </div>
              <template v-if="hasNego(row)">
                <div v-if="negoChips(row).length" class="sb-nego__chips">
                  <span v-for="c in negoChips(row)" :key="c.k" class="sb-nego__chip"><b>{{ c.label }}</b> {{ c.v }}</span>
                </div>
              </template>
              <p v-else class="sb-exp__none">협상 기록 없음 — 1688 판매자 협상 도구로 채워집니다</p>
              <label class="sb-nego__lb" :for="'sb-nego-sum-' + row._uid">
                협상 정리
                <span v-if="row.negotiation && row.negotiation.summaryAt" class="sb-nego__at">
                  {{ row.negotiation.summaryBy === 'tool' ? '도구' : '사람' }} · {{ negoTime(row.negotiation.summaryAt) }}
                </span>
              </label>
              <textarea :id="'sb-nego-sum-' + row._uid" class="sb-in sb-nego__ta" rows="3"
                        v-model="row._negoSummary" maxlength="2000"
                        placeholder="합의한 단가·MOQ·납기·샘플 등을 정리해 두세요"></textarea>
              <div class="sb-nego__bar">
                <span class="sb-nego__n">{{ (row._negoSummary || '').length }}/2000</span>
                <button type="button" class="sb-btn"
                        :disabled="negoSavingUid === row._uid || !(row._negoSummary || '').trim() || (row._negoSummary || '').trim() === row._negoSummarySaved"
                        @click="saveNegoSummary(row)">{{ negoSavingUid === row._uid ? '저장 중…' : '정리 저장' }}</button>
              </div>
              <template v-if="negoThreadCount(row)">
                <button type="button" class="sb-chipbtn sb-nego__tg" :aria-expanded="row._negoOpen ? 'true' : 'false'"
                        @click="row._negoOpen = !row._negoOpen">
                  <template v-if="row._negoOpen">원본 대화 접기</template><template v-else>원본 대화 {{ negoThreadCount(row) }}건 보기</template>
                </button>
                <div v-if="row._negoOpen" class="sb-nego__th">
                  <div v-for="g in negoThreadGroups(row)" :key="g.sellerId" class="sb-nego__grp">
                    <span class="sb-nego__seller">{{ g.sellerId }} · {{ g.total }}건</span>
                    <button v-if="g.hidden" type="button" class="sb-chipbtn" @click="row._negoMore = true">이전 {{ g.hidden }}건 더 보기</button>
                    <div v-for="m in g.msgs" :key="m.id" class="sb-nego__msg" :class="m.fromUs ? 'sb-nego__msg--us' : 'sb-nego__msg--them'">
                      <span class="sb-nego__who">{{ m.fromUs ? '우리' : '판매자' }} · {{ negoTime(m.ts) }}</span>
                      <span class="sb-nego__tx">{{ m.text }}</span>
                    </div>
                  </div>
                </div>
              </template>
            </div>
            <!-- 아래: 왼쪽 「왜 이 점수인가」 ↔ 오른쪽 판정 -->
            <div class="sb-exp__vd">
              <div class="sb-why">
                <span class="sb-cap">왜 이 점수인가</span>
                <ul>
                  <li v-for="(w, wi) in whyLines(row)" :key="wi" :class="{ 'sb-why__fl': w.warn }">{{ w.text }}</li>
                </ul>
              </div>
              <div class="sb-acts">
                <div class="sb-acts__rw">
                  <button type="button" class="sb-btn sb-btn--ok" @click="judge(row, '확정')">소싱확정</button>
                  <button type="button" class="sb-btn" @click="judge(row, '보류')">보류</button>
                  <button type="button" class="sb-btn sb-btn--bad"
                          :aria-expanded="rejectOpenUid === row._uid ? 'true' : 'false'"
                          :aria-controls="'sb-rs-' + row._uid"
                          @click="toggleReject(row)">반려</button>
                </div>
                <!-- 확정된 행의 등록 표시. 진도 체크리스트(상세 밀도)와 같은 토글이다 -->
                <div class="sb-acts__rw" v-if="row.confirmed">
                  <button type="button" class="sb-btn" @click="toggleRegister(row)">
                    {{ row.registered ? '등록 표시 해제' : '상품등록 표시' }}
                  </button>
                </div>
                <p v-if="row.심사판정" class="sb-acts__cur">
                  현재 판정 <b>{{ row.심사판정 }}</b><span v-if="row.심사사유"> · {{ row.심사사유 }}</span>
                </p>
                <!-- 반려 사유는 두 무리로 갈라 보여준다. 이 구분이 화면에 보여야
                     「상품 불일치」가 조건서로 새지 않는다(정본 §5). -->
                <div v-if="rejectOpenUid === row._uid" class="sb-rs" :id="'sb-rs-' + row._uid">
                  <div>
                    <span class="sb-gl">조건 <span>— 조건서로 승격될 수 있다</span></span>
                    <div class="sb-rbs">
                      <button v-for="r in REJECT_COND" :key="r[0]" type="button" class="sb-rb"
                              :title="'3건 쌓이면 「' + r[1] + '」 를 조건서에 넣자고 제안해요'"
                              @click="reject(row, r[0], 'cond')">{{ r[0] }}</button>
                    </div>
                  </div>
                  <div>
                    <span class="sb-gl">품질 신고 <span>— 조건서로 가지 않는다</span></span>
                    <div class="sb-rbs">
                      <button v-for="r in REJECT_QA" :key="r[0]" type="button" class="sb-rb sb-rb--qa"
                              :title="r[1] + ' 방식을 고칠 신호예요. 조건이 아니라서 조건서로 안 보내요'"
                              @click="reject(row, r[0], 'qa')">
                        {{ r[0] }}<span class="sb-rb__k">{{ r[1] }}</span>
                      </button>
                    </div>
                  </div>
                  <div class="sb-rs__etc">
                    <input type="text" class="sb-in sb-in--t" v-model="rejectEtc"
                           placeholder="기타 — 직접 입력" aria-label="기타 반려 사유">
                    <button type="button" class="sb-btn" :disabled="!rejectEtc.trim()"
                            @click="reject(row, rejectEtc.trim(), 'etc')">반려</button>
                  </div>
                  <input type="text" class="sb-in sb-in--t" v-model="row.심사메모"
                         placeholder="코멘트 (선택)" aria-label="심사 코멘트">
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </template>
      </div>

      <!-- 빈 상태는 rowgroup 밖이다 — 행이 아닌 것을 표 구조 안에 넣으면 읽는 쪽이 헷갈린다 -->
      <!-- 비어 있는 이유를 골라 말한다. 조건서가 다 가려놓고 「이 단계는 비어 있어요」라고
           하면 단계 필터를 탓하게 돼 엉뚱한 데를 고치러 간다. -->
      <div v-if="!pageRows.length" class="sb-empty">
        <template v-if="!rows.length">아직 담긴 상품이 없어요. 「담기」로 행을 추가하거나 키워드소싱분석에서 담아 주세요.</template>
        <template v-else-if="maskedCount">조건서가 이 단계의 행을 전부 가렸어요. 위 줄의 「보기」로 다시 볼 수 있어요.</template>
        <template v-else>이 단계는 비어 있어요.</template>
      </div>
     </div>
    </div>

    <!-- 페이지 — 표 밖이라 chrome 2줄 규칙에 들어가지 않는다 -->
    <div class="sb-foot" v-if="pageCount > 1">
      <button type="button" class="sb-page" :disabled="page <= 1" @click="page -= 1">이전</button>
      <span class="sb-foot__n">{{ page }} / {{ pageCount }}</span>
      <button type="button" class="sb-page" :disabled="page >= pageCount" @click="page += 1">다음</button>
    </div>

    <!-- 행 상세 모달: 출처별 raw 필드를 클러스터 섹션으로
         modal-class="sourcing-board" — b-modal 은 body 로 옮겨 그려져 .sourcing-board 밖에 놓인다.
         모달 스타일(.sb-dt·.sb-chipbtn…)이 .sourcing-board 아래에 있어 이 class 가 없으면 하나도 안 먹는다(세 모달 같음) -->
    <b-modal id="sb-detail-modal" modal-class="sourcing-board" size="lg" scrollable ok-only ok-title="닫기" :title="detailTitle">
      <div v-if="detailItem">
        <!-- 벤치마킹 상품 — 표에서는 상세 밀도에만 나오지만, 여기서는 항상 손댈 수 있어야 한다 -->
        <div class="sb-dt sb-dtbench">
          <div class="sb-dt__title">벤치마킹 상품</div>
          <div class="sb-dtbench__row" v-if="detailItem.벤치마킹상품">
            <span class="sb-thumb" :style="thumbStyle(detailItem.벤치마킹썸네일)"></span>
            <a v-if="detailItem.벤치마킹링크" :href="detailItem.벤치마킹링크" target="_blank" rel="noopener">
              {{ detailItem.벤치마킹상품 }}</a>
            <span v-else>{{ detailItem.벤치마킹상품 }}</span>
            <b v-if="detailItem.벤치마킹판매가 != null">{{ formatNumber(detailItem.벤치마킹판매가) }}원</b>
          </div>
          <div class="sb-dtbench__row">
            <input type="text" class="sb-in sb-in--t" v-model="detailItem.벤치마킹링크"
                   placeholder="쿠팡 상품링크 붙여넣기">
            <button type="button" class="sb-chipbtn" :disabled="benchLoadingUid === detailItem._uid"
                    @click="fetchBenchmark(detailItem)">
              <i class="fas mr-1" :class="benchLoadingUid === detailItem._uid ? 'fa-spinner fa-spin' : 'fa-download'"></i>
              불러오기
            </button>
          </div>
        </div>
        <!-- AI 등록 초안 — 수강생 소싱관리만(로켓그로스 보드는 안 보인다). AI 로켓등록 단계 부품을 그대로 연다 · 쿠팡 보내기 없음 -->
        <div v-if="showRegSection" class="sb-dt sb-dtreg">
          <div class="sb-dt__title">AI 등록 초안</div>
          <p v-if="!canRocketReg" class="text-muted mb-0">AI 로켓등록이 개통된 계정에서 쓸 수 있어요.</p>
          <template v-else>
            <p v-if="regBusy" class="mb-2"><i class="fas fa-spinner fa-spin mr-1"></i>{{ regBusy }}</p>
            <p v-if="regError" class="text-danger mb-2">{{ regError }}</p>
            <div v-if="!regDraft && !regBusy" class="sb-dtbench__row">
              <span v-if="!detailItem._id">먼저 「저장」으로 이 행을 저장해 주세요.</span>
              <span v-else-if="!(detailItem.alibabaLink || detailItem.offerId)">1688 상품을 먼저 매칭해 주세요.</span>
              <span v-else-if="!detailItem.키워드">키워드를 먼저 넣어 주세요.</span>
              <template v-else-if="!detailItem.rocketRegId">
                <span>쿠팡 카테고리를 고르면 1688 상품·키워드로 상품명부터 견적서까지 채울 초안을 만들어요(카테고리는 초안 뒤에 못 바꿔요).</span>
                <button type="button" class="sb-chipbtn" @click="pickRegCategory(detailItem)">
                  <i class="fas fa-magic mr-1"></i>초안 만들기
                </button>
              </template>
            </div>
            <template v-if="regDraft">
              <table class="sb-dt__table">
                <tr><th>등록번호</th><td>{{ regDraft.registrationId }}</td></tr>
                <tr><th>카테고리</th><td>{{ regCategoryText }}</td></tr>
                <tr><th>옵션</th><td>{{ (regDraft.options || []).length }}개</td></tr>
                <tr v-for="s in regSteps" :key="s.workType">
                  <th>{{ s.label }}</th>
                  <td>
                    <b-badge :variant="getStatusVariant(regDraft[s.statusKey])">{{ getStatusText(regDraft[s.statusKey]) }}</b-badge>
                    <span v-if="s.workType === 'seo' && regDraft.seo && regDraft.seo.productName" class="ml-1">{{ regDraft.seo.productName }}</span>
                  </td>
                </tr>
              </table>
              <div class="sb-dtbench__row mt-2">
                <button type="button" class="sb-chipbtn" @click="openRegWizard('seo')">
                  <i class="fas fa-list-ol mr-1"></i>상품명부터 차례로 채우기
                </button>
                <button type="button" class="sb-chipbtn" @click="openRegOptions">
                  <i class="fas fa-th-list mr-1"></i>옵션별 작업 보기
                </button>
              </div>
            </template>
          </template>
        </div>
        <div v-for="(sec, si) in clusterSections(detailItem)" :key="si" class="sb-dt">
          <div class="sb-dt__title">{{ sec.title }}</div>
          <table class="sb-dt__table">
            <tr v-for="(f, fi) in sec.fields" :key="fi">
              <th>{{ f.label }}</th>
              <td>{{ f.value }}</td>
            </tr>
          </table>
        </div>
        <!-- 출처별 원본 칸이 비었다는 뜻 — AI 등록 초안 칸 밑에 붙으면 «초안이 비었다» 로 읽혀서 그 칸이 보일 때는 안 띄운다 -->
        <p v-if="!clusterSections(detailItem).length && !showRegSection" class="text-muted mb-0">수집한 원본 정보(키워드·1688 등)가 없어요.</p>
      </div>
    </b-modal>

    <!-- AI 등록 초안의 옵션·단계 모달 — AI 로켓등록 화면과 같은 부품 -->
    <!-- AI 등록 초안 카테고리 고르기 — AI 로켓등록 화면과 같은 부품(한 화면에 둘이 떠도 안 섞이게 id 따로) -->
    <CategorySelectModal v-if="!isGrowth" ref="regCategory" modal-id="sbRegCategoryModal"
                         @selected="onRegCategorySelected" @responded="onRegCategoryResponded" />
    <template v-if="!isGrowth && regDraft">
      <RocketOptionsModal v-model="showRegOptions" @option-deleted="onRegOptionDeleted" @open-wizard="openRegWizardFromOptions" />
      <ProductWizardModal ref="regWizard" @closed="onRegWizardClosed" />
    </template>

    <!-- 1688 상품매칭 picker -->
    <b-modal id="sb-picker-modal" modal-class="sourcing-board" size="md" scrollable title="1688 상품매칭"
             ok-title="적용" cancel-title="닫기" @ok="applyPicker">
      <div v-if="pickerItem">
        <div v-if="pickerItem.상품매칭 && pickerItem.상품매칭.length" class="sb-pick">
          <button type="button" class="btn btn-outline-dark btn-sm" @click="pickerPrev"><b>&lt;</b></button>
          <div class="sb-pick__body">
            <img v-if="pickerCand.pic_url" :src="pickerCand.pic_url" class="sb-pick__img" alt="후보 이미지">
            <div class="sb-pick__price">CNY {{ pickerCand.price }}</div>
            <pre class="sb-pick__opt" v-if="pickerOptions(pickerCand)">{{ pickerOptions(pickerCand) }}</pre>
            <a v-if="pickerCand.detail_url" :href="pickerCand.detail_url" target="_blank" rel="noopener" class="sb-pick__link">상품 페이지 열기</a>
            <div class="sb-pick__idx">{{ (pickerItem.currentindex || 0) + 1 }} / {{ pickerItem.상품매칭.length }}</div>
          </div>
          <button type="button" class="btn btn-outline-dark btn-sm" @click="pickerNext"><b>&gt;</b></button>
        </div>
        <p v-else class="text-muted">
          아직 불러온 상품이 없어요. 아래에 1688 링크를 넣고 「상품정보 불러오기」를 누르면
          이미지·옵션을 자동으로 가져와요.
        </p>
        <hr>
        <div class="sb-pick__manual">
          <label class="sb-pick__lbl">1688 링크</label>
          <input type="text" class="form-control form-control-sm mb-1" placeholder="1688 상품 주소 · 공유 링크 · 상품번호" v-model="pickerLink">
          <button type="button" class="btn btn-success btn-sm mb-2 mr-1"
                  :disabled="alibabaLoadingUid === (pickerItem && pickerItem._uid)"
                  @click="fetchAlibaba(pickerItem)">
            <i class="fas mr-1" :class="alibabaLoadingUid === (pickerItem && pickerItem._uid) ? 'fa-spinner fa-spin' : 'fa-download'"></i>
            상품정보 불러오기
          </button>
          <small class="d-block text-muted mb-2">
            1688 상품 주소(PC·모바일), 1688 앱의 «공유» 링크, 상품번호 모두 넣을 수 있어요. 이미지·옵션·옵션별 구매가를 가져와요.
          </small>
          <label class="sb-pick__lbl">구매가(CNY) — 자동으로 못 채우면 직접 입력</label>
          <input type="number" class="form-control form-control-sm mb-2" placeholder="구매가(CNY)" v-model.number="pickerPrice">
          <button type="button" class="btn btn-primary btn-sm" @click="applyManualLink(pickerItem)">링크 적용</button>
        </div>
      </div>
    </b-modal>

    <!-- 옵션별 마진: 1688 옵션마다 구매가(CNY)가 달라 옵션 단위로 판매가·마진을 따로 본다.
         행을 쪼개지 않는 이유 = 한 행 = 등록될 상품 1개 라는 보드 원칙(§8.1) 유지 -->
    <b-modal v-if="showOptions" id="sb-option-modal" modal-class="sourcing-board" size="xl" scrollable ok-only ok-title="닫기" :title="optionTitle">
      <div v-if="optionItem">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <small class="text-muted">
            수수료율 {{ optionItem.수수료율 != null ? optionItem.수수료율 + '%' : '-' }} ·
            풀필먼트 {{ formatNumber(optionItem.fullfillmentFee) }}원 ·
            보관 {{ formatNumber(optionItem.warehouseFee) }}원 ·
            밀크런 {{ formatNumber(optionItem.밀크런예상) }}원 — 옵션 공통으로 적용돼요
          </small>
          <div>
            <b-button size="sm" variant="outline-secondary" class="mr-1" @click="importOptionsFrom1688(optionItem)"
                      :disabled="!(optionItem.상품매칭 && optionItem.상품매칭.length)">
              1688 옵션 가져오기
            </b-button>
            <b-button size="sm" variant="outline-secondary" @click="addOption(optionItem)">옵션 추가</b-button>
          </div>
        </div>
        <!-- 중국 내 운임 — 행 전체(옵션 포함)에 같이 적용된다. 비우면 0 으로 계산(실제 매입가는 더 높을 수 있다) -->
        <div class="d-flex align-items-center flex-wrap mb-2" style="gap:6px">
          <small class="text-muted">중국 내 운임(발주 전체)</small>
          <input type="number" min="0" class="sb-input" style="width:90px" v-model.number="optionItem.중국내운임" placeholder="0"
                 title="중국 내 운임(CNY) — 그 발주 전체 금액" @input="changeCost(optionItem)">
          <small class="text-muted">CNY ÷ 발주 수량</small>
          <input type="number" min="1" class="sb-input" style="width:60px" v-model.number="optionItem.발주수량" placeholder="2"
                 title="운임이 붙은 발주 수량(개). 비우면 2 로 나눠요(운임 견적 기본)" @input="changeCost(optionItem)">
          <small class="text-muted">개 → 개당 {{ formatNumber(shipKrw(optionItem)) }}원(환율·부가세 포함)</small>
        </div>
        <table class="sb-opt__table">
          <thead>
            <tr>
              <th style="width:22%">옵션명</th>
              <th style="width:11%">구매가(CNY)</th>
              <th style="width:11%">매입가</th>
              <th style="width:11%">판매가</th>
              <th style="width:11%">수수료</th>
              <th style="width:11%">마진</th>
              <th style="width:8%">마진율</th>
              <th style="width:8%">ROI</th>
              <th style="width:7%"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(o, oi) in (optionItem.옵션 || [])" :key="oi">
              <td><input type="text" class="sb-input sb-input--sm" v-model="o.옵션명" placeholder="옵션명"></td>
              <td><input type="number" class="sb-input" v-model.number="o.구매가" @input="changeOptionCost(optionItem, o)"></td>
              <td class="text-right">{{ formatNumber(o.매입가) }}</td>
              <td><input type="number" class="sb-input" v-model.number="o.판매가" @input="changeOptionCost(optionItem, o)"></td>
              <td class="text-right">{{ formatNumber(o.수수료) }}</td>
              <td class="text-right"><span :class="signClass(o.마진)">{{ formatNumber(o.마진) }}</span></td>
              <td class="text-right"><span :class="signClass(o.마진율)">{{ o.마진율 != null ? o.마진율 + '%' : '-' }}</span></td>
              <td class="text-right"><span :class="signClass(o.ROI)">{{ o.ROI != null ? o.ROI + '%' : '-' }}</span></td>
              <td class="text-center">
                <button class="sb-opt__del" @click="removeOption(optionItem, oi)" title="삭제">
                  <i class="fas fa-times"></i>
                </button>
              </td>
            </tr>
            <tr v-if="!(optionItem.옵션 && optionItem.옵션.length)">
              <td colspan="9" class="text-center text-muted py-3">
                옵션이 없어요. 1688 상품을 매칭했다면 「1688 옵션 가져오기」로 불러올 수 있어요.
              </td>
            </tr>
          </tbody>
        </table>
        <small class="text-muted d-block mt-2">
          옵션 수수료는 판매가에 수수료율을 적용하고 풀필먼트·보관비를 더해 옵션마다 다시 계산해요.
          매입가는 (구매가 + 개당 중국 내 운임)(CNY) × 통합통관환율 × 1.1 + 검수 + 바코드 + 예상 밀크런비 입니다(운임은 이 창 위의 «중국 내 운임»·«발주 수량» 칸, 비우면 0).
        </small>
      </div>
    </b-modal>

    <!-- ④ 조건서 시트 — 평소엔 닫혀 있다가 상단 바 칩으로 열린다(정본 §1·§5).
         b-sidebar 를 쓰는 이유 = ESC·backdrop·포커스 처리를 직접 짜지 않으려고. -->
    <b-sidebar id="sb-spec-pane" v-model="specOpen" right shadow backdrop
               title="조건서" width="430px" bg-variant="white">
      <div class="sb-spec__in">
        <p v-if="sheetLoading" class="sb-spec__note">조건서를 불러오는 중이에요…</p>
        <template v-else-if="ruleSheet">
          <label class="sb-spec__pick">
            <span>조건서</span>
            <select class="sb-in sb-in--t" :value="ruleSheet._id"
                    @change="selectSheet($event.target.value)">
              <option v-for="s in ruleSheets" :key="s._id" :value="s._id">
                {{ s.name || '이름 없는 조건서' }}
              </option>
            </select>
          </label>

          <ul class="sb-rules">
            <li v-for="r in (ruleSheet.rules || [])" :key="r.id"
                class="sb-rule" :class="{ 'sb-rule--off': !r.on }">
              <div class="sb-rule__a">
                <span class="sb-rule__tx">{{ r.text }}</span>
                <!-- 실제 button + aria-pressed. 보기만 스위치다 -->
                <button type="button" class="sb-sw" :class="{ 'sb-sw--on': r.on }"
                        :aria-pressed="r.on ? 'true' : 'false'" :disabled="ruleSaving"
                        :aria-label="r.text + (r.on ? ' 끄기' : ' 켜기')"
                        @click="toggleRule(r)"><s></s></button>
              </div>
              <div class="sb-rule__b">
                <span class="sb-rchip" :class="'sb-rchip--' + (r.kind || 'none')"
                      :title="kindHint(r)">{{ kindText(r) }}</span>
                <span class="sb-rchip sb-rchip--sc" :title="scopeHint(r)">{{ r.scope || SCOPE_ALL }}</span>
                <!-- 문장만 있는 규칙은 표를 못 거른다. 거르는 척하지 않는다 -->
                <span v-if="condText(r)" class="sb-rchip sb-rchip--cond"
                      title="이 조건으로 표를 걸러요. 값이 없는 행은 거르지 않아요.">{{ condText(r) }}</span>
                <span v-else-if="r.kind === 'hard'" class="sb-rchip sb-rchip--mute"
                      title="숫자 조건이 없어 표를 거르지 못해요. 문장은 에이전트가 읽어요. 「수정」으로 조건을 넣을 수 있어요.">에이전트만 읽음</span>
                <span v-else-if="r.kind === 'soft'" class="sb-rchip sb-rchip--mute"
                      title="순위에 쓸 규칙인데 아직 순위 계산에 넣지 않았어요.">순위용 — 아직 반영 안 됨</span>
                <b v-if="r.on && ruleDrops[r.id]" class="sb-rule__dp"
                   :title="'지금 보드 ' + rows.length + '건 중 이 규칙 하나로 떨어지는 건수예요.'">−{{ ruleDrops[r.id] }}건</b>
              </div>
              <div class="sb-rule__c">
                <span class="sb-rule__og">{{ r.origin || '출처 없음' }}</span>
                <button type="button" class="sb-rule__x" :disabled="ruleSaving"
                        @click="startEditRule(r)">수정</button>
                <button type="button" class="sb-rule__x" :disabled="ruleSaving"
                        @click="deleteRule(r)">삭제</button>
              </div>
            </li>
          </ul>
          <p v-if="!(ruleSheet.rules || []).length" class="sb-spec__note">아직 규칙이 없어요.</p>

          <!-- 규칙 추가 / 수정 — 폼은 하나를 돌려 쓴다 -->
          <div v-if="ruleDraft" class="sb-draft">
            <span class="sb-cap">{{ ruleDraft.id ? '규칙 수정' : '규칙 추가' }}</span>
            <input type="text" class="sb-in sb-in--t" v-model="ruleDraft.text"
                   aria-label="규칙 문장" placeholder="규칙 문장 — 예: 마진율 25% 이상">
            <div class="sb-draft__rw">
              <label class="sb-draft__f">종류
                <select class="sb-in sb-in--t" v-model="ruleDraft.kind">
                  <option v-for="k in RULE_KINDS" :key="k.kind" :value="k.kind">{{ k.text }}</option>
                </select>
              </label>
              <label class="sb-draft__f">범위
                <input type="text" class="sb-in sb-in--t" v-model="ruleDraft.scope" :placeholder="SCOPE_ALL">
              </label>
            </div>
            <p class="sb-draft__h">
              범위는 「{{ SCOPE_ALL }}」·「이번 탐색만」이면 모든 행에, 그 밖의 값은
              카테고리 이름으로 보고 그 카테고리 행에만 걸어요.
            </p>
            <label v-if="ruleDraft.kind === 'hard'" class="sb-draft__use">
              <input type="checkbox" v-model="ruleDraft.useCond"> 표에서도 거르기 (숫자 조건)
            </label>
            <div v-if="ruleDraft.kind === 'hard' && ruleDraft.useCond" class="sb-draft__rw">
              <select class="sb-in sb-in--t" v-model="ruleDraft.field" aria-label="조건 항목">
                <option v-for="f in COND_FIELDS" :key="f.field" :value="f.field">{{ f.label }}</option>
              </select>
              <input type="number" class="sb-in sb-in--n" v-model="ruleDraft.value" aria-label="조건 값">
              <select class="sb-in sb-in--t sb-draft__op" v-model="ruleDraft.op" aria-label="조건 비교">
                <option v-for="o in COND_OPS" :key="o.op" :value="o.op">{{ o.text }}</option>
              </select>
            </div>
            <p v-if="ruleDraft.kind === 'hard' && ruleDraft.useCond" class="sb-draft__h">
              값이 아직 없는 행은 거르지 않아요 — 「모른다」는 「조건 미달」이 아니에요.
            </p>
            <p v-else-if="ruleDraft.kind === 'soft'" class="sb-draft__h">
              소프트 규칙은 순위용이라 표를 거르지 않아요. 순위 반영은 아직 안 됐어요.
            </p>
            <p v-else-if="ruleDraft.kind === 'calc'" class="sb-draft__h">
              계산 기준은 후보를 떨어뜨리지 않아요. 지금 쓰이는 값은 아래에 있어요.
            </p>
            <p v-else class="sb-draft__h">
              숫자 조건을 안 넣으면 표를 거르지 않고 에이전트가 문장으로만 읽어요.
            </p>
            <div class="sb-draft__rw">
              <button type="button" class="sb-btn sb-btn--ok" :disabled="ruleSaving"
                      @click="submitRuleDraft">{{ ruleSaving ? '저장중…' : '저장' }}</button>
              <button type="button" class="sb-btn" :disabled="ruleSaving"
                      @click="cancelRuleDraft">취소</button>
            </div>
          </div>
          <button v-else type="button" class="sb-btn sb-spec__add" @click="startAddRule">
            <i class="fas fa-plus mr-1"></i>규칙 추가
          </button>

          <!-- calc 규칙이 말하는 값의 **지금 값**. 하드코딩해 적으면 코드와 갈라지는
               순간 거짓이 되므로 sourcingSignals 가 export 한 상수를 그대로 읽는다. -->
          <div class="sb-calc">
            <span class="sb-cap">지금 쓰이는 계산 기준 <span>— 읽기 전용 · 편집은 아직 안 됨</span></span>
            <dl class="sb-calc__dl">
              <template v-for="(c, ci) in CALC_CONSTANTS">
                <dt :key="'ck' + ci">{{ c.label }}</dt>
                <dd :key="'cv' + ci">{{ c.value }}</dd>
              </template>
            </dl>
          </div>
        </template>
        <p v-else class="sb-spec__note">조건서를 불러오지 못했어요. 시트를 닫았다 다시 열어 주세요.</p>
      </div>
    </b-sidebar>
  </div>
</template>

<script>
/**
 * 소싱관리 보드 — 표 본체가 CDataTable 이 아니다. 여기만 그렇다.
 *
 * ⚠ **이 파일을 새 화면의 본보기로 삼지 말 것.** 표를 만들 때의 기본은 여전히
 *    `CDataTable` 이고 규약은 `CDATATABLE_README.md` 다. 이 화면만 예외인 이유:
 *
 *   ① **그룹을 통째로 덮는 색 덩어리** — 막힌 단계를 「1688 찾기」 한 덩어리로 보여주려면
 *      컬럼 여러 개를 가로로 합쳐야 한다. `v-data-table` 은 컬럼마다 `<td>` 를 쪼개고
 *      셀 슬롯에 colspan 을 줄 방법이 없다.
 *   ② **스팬되는 2단 그룹 헤더** — CDataTable 의 `headers[].group` 은 컬럼마다 붙는
 *      뱃지라 그룹 경계가 안 생긴다. 이 화면은 컬럼 그룹이 곧 깔때기 단계다.
 *   ③ **29px 1줄 행** — 행 수십 개를 세로로 훑어 병목을 찾는 화면이라 행 높이가 지표다.
 *
 *   ⇒ 셋 다 표의 뼈대에 걸려 래핑으로 못 푼다. 그래서 여기만 CSS grid 로 짰고,
 *      CDataTable 이 얹어주던 정렬·페이저·좌측고정·sticky 헤더는 이 파일 안에 다시 구현했다.
 *      **다른 화면에 이 중 어느 것도 필요 없으면 CDataTable 을 쓰는 게 맞다.**
 *
 * ⚠ **열 필터(CDataTable 의 깔때기 패널)는 여기 없다. 조건서가 그 자리다.**
 *    정본 docs/sourcing-board-redesign.md §5 가 조건서 `hard` 규칙으로 옮기기로 한 자리다
 *    (「마진율 25% 이상」이 그 예시로 적혀 있다). 표에 따로 만들면 같은 판정이 두 곳에 생기고,
 *    두 층이 갈린다.
 *    ⇒ 상단 바 「조건서」 칩 → `cond` 가 붙은 `hard` 규칙이 표를 거른다.
 *      **거른 행은 없애지 않고 접는다**(「조건서로 N건 가려짐 [보기]」) — 조용히 빠지면
 *      왜 안 보이는지 영영 모른다. 값이 `null` 인 행은 절대 거르지 않는다(§8-④).
 *
 * 다중 정렬(최대 3키)과 페이지 크기 셀렉트도 안 만들었다 — 진입점수 내림차순 기본 +
 * 20행 페이저로 충분하다는 판단이다. 필요해지면 그때 만든다.
 *
 * 정본: docs/sourcing-board-redesign.md §1(왜 이 모양인가) §2(표 구조) §3(지표) §8(함정)
 */
import axios from 'axios'
import ApprovalPendingPanel from '@/components/common/ApprovalPendingPanel'
import ProductWizardModal from '@/components/AIRocket/ProductWizardModal.vue'
import CategorySelectModal from '@/components/AIRocket/CategorySelectModal.vue'
import RocketOptionsModal from '@/components/AIRocket/RocketOptionsModal.vue'
import rocketRegDraftMixin from '@/mixins/rocketRegDraftMixin'
import { FEE_INSPECT, FEE_BARCODE, FEE_INSPECT_VAT, FEE_BARCODE_VAT, customsOnly, perUnitShipCny, recalcRow, recalcOption } from '@/utils/purchaseCost'
// 진입점수·1p 내 위치는 화면이 아니라 이 유틸이 정본이다. 식을 여기 복제하지 말 것.
import {
  OPP, OPP_WEIGHT, DEFAULT_OPP_WEIGHT, ROI_CAP, MARKET_PIE_LOG_BASE, SCORE_SCALE,
  ONE_PAGE_PRICE_FIELD, valueAtPct, priceDist,
  rpiPosition, pricePosition, impressionPosition, opportunityOf, entryScore
} from '@/utils/sourcingSignals'

// 분석 소싱관리와 로켓그로스 소싱관리가 SourcingBoard 컬렉션 하나를 공유한다.
// boardType 으로 갈라 서로 안 섞이게 한다 (기존 행은 boardType 이 없어 분석 보드로 남는다).
// 소싱 진행 6단계 — 행의 데이터 충족도로 자동 판정 (M5에서 본격 구현)
const STAGES = ['키워드선정', '벤치마킹', '1688확보', '스펙입력', '마진계산', '소싱확정', '상품등록']
// 진도 단계 정의 — 순서 무관 독립 판정. 카테고리/키워드/벤치마킹은 선택, 1688/스펙/마진은 필수.
const STAGE_DEFS = [
  { essential: false, filled: r => !!r.키워드 },
  { essential: false, filled: r => !!(r.벤치마킹상품 || r.월매출) },
  { essential: true, filled: r => !!(r.offerId || r.alibabaLink) },
  { essential: true, filled: r => !!(r.sizeX && r.weight) },
  { essential: true, filled: r => r.마진 != null && !!r.판매가 && !!r.구매가 }
]

// 상세 모달 클러스터 — 출처별 raw 필드를 의미 단위로 묶음. 값 없는 필드는 표시 단계에서 숨김.
const CLUSTERS = [
  {
    title: '시장수요',
    keys: [
      '카테고리적합도', '쿠팡키워드CPC', '쿠팡중간판매가', '쿠팡판매가범위', '쿠팡평균판매가',
      '상위그로스비율', '상위로켓비율', '상위윙비율',
      '평균월조회수', '중간월조회수', '최대월조회수', '최소월조회수',
      '평균월판매량', '중간월판매량', '최대월판매량', '최소월판매량',
      '평균월매출', '중간월매출', '최대월매출', '최소월매출',
      '평균전환율', '중간전환율', '최대전환율', '최소전환율',
      '키워드트렌드지난달네이버', '검색트렌드지난달', '검색트렌드이번달', '검색트렌드+3개월', '검색트렌드+6개월'
    ]
  },
  {
    title: '카테고리노출',
    keys: [
      '카테고리_avgPrice', '카테고리_priceRange', '카테고리_totalImpression', '카테고리_organicImpression',
      '카테고리_adsImpression', '카테고리_adsImpressionRate', '카테고리_searchVolume',
      '카테고리_searchClick', '카테고리_clickRate'
    ]
  },
  {
    title: 'RPI·검색질',
    keys: [
      'maxRpi', 'avgRpi', 'medianRpi', 'minRpi',
      'topRankMaxRpi', 'topRankAvgRpi', 'topRankMedianRpi', 'topRankMinRpi',
      'maxCtr', 'avgCtr', 'medianCtr', 'minCtr',
      'maxCvr', 'avgCvr', 'medianCvr', 'minCvr',
      'accessibilityScore', 'revenueShare', 'revenueShareBasedRevenueRange', 'priceRange25to75',
      '상품_calculatedRPI', '상품_ctr', '상품_cvr', '상품_vendorItemId'
    ]
  },
  {
    title: 'SEO',
    keys: ['seo_productNameKeywords', 'seo_tagKeywords', 'seo_suggestions']
  },
  {
    title: '출처',
    keys: ['진도출처']
  }
]

// 키 → 사람이 읽기 좋은 라벨. 매핑에 없으면 키 원문 사용.
const LABELS = {
  쿠팡키워드CPC: '쿠팡 키워드 CPC',
  상위그로스비율: '상위 그로스비율', 상위로켓비율: '상위 로켓비율', 상위윙비율: '상위 윙비율',
  키워드트렌드지난달네이버: '네이버 키워드트렌드(지난달)',
  카테고리_avgPrice: '카테고리 평균가', 카테고리_priceRange: '카테고리 가격범위',
  카테고리_totalImpression: '카테고리 총노출', 카테고리_organicImpression: '카테고리 자연노출',
  카테고리_adsImpression: '카테고리 광고노출', 카테고리_adsImpressionRate: '카테고리 광고노출비율',
  카테고리_searchVolume: '카테고리 검색량', 카테고리_searchClick: '카테고리 검색클릭', 카테고리_clickRate: '카테고리 클릭률',
  maxRpi: '최대 RPI', avgRpi: '평균 RPI', medianRpi: '중간 RPI', minRpi: '최소 RPI',
  topRankMaxRpi: '상위 최대 RPI', topRankAvgRpi: '상위 평균 RPI', topRankMedianRpi: '상위 중간 RPI', topRankMinRpi: '상위 최소 RPI',
  maxCtr: '최대 CTR', avgCtr: '평균 CTR', medianCtr: '중간 CTR', minCtr: '최소 CTR',
  maxCvr: '최대 CVR', avgCvr: '평균 CVR', medianCvr: '중간 CVR', minCvr: '최소 CVR',
  accessibilityScore: '접근성 점수', revenueShare: '매출 점유율',
  revenueShareBasedRevenueRange: '매출점유 기반 매출범위', priceRange25to75: '가격범위(25~75%)',
  상품_calculatedRPI: '상품 RPI', 상품_ctr: '상품 CTR', 상품_cvr: '상품 CVR', 상품_vendorItemId: '벤더아이템ID',
  seo_productNameKeywords: '상품명 키워드', seo_tagKeywords: '태그 키워드', seo_suggestions: 'SEO 제안'
}

/* ------------------------------------------------------------------ *
 * 표 구조 — 컬럼 정의 · 그룹(=깔때기 단계) · 밀도
 *
 * 컬럼 28개가 문제가 아니라 「모든 행에 같은 28칸」이 문제였다(정본 §1).
 * 그래서 컬럼을 그룹으로 묶어 단계로 만들고, 막힌 단계는 그룹 통째로 덮는다.
 * ------------------------------------------------------------------ */

// [라벨, 폭, 숫자정렬]. 폭 합이 기본 밀도에서 가로 스크롤 0 을 결정한다.
const COLS = {
  nm: { text: '상품명', w: '140px' },
  kw: { text: '키워드', w: '100px' },
  cat: { text: '카테고리', w: '92px' },
  src: { text: '담긴 경로', w: '104px' },
  bench: { text: '벤치마킹', w: '160px' },
  sc: { text: '진입점수', w: '88px', num: true },
  pie: { text: '시장 파이', w: '64px', num: true },
  sv: { text: '검색량', w: '70px', num: true },
  rpi: { text: 'RPI', w: '52px', num: true },
  rpos: { text: '1p RPI위치', w: '86px' },
  imp: { text: '노출수', w: '70px', num: true },
  ipos: { text: '1p 노출위치', w: '86px' },
  opp: { text: '기회', w: '70px' },
  sal: { text: '월판매량', w: '74px', num: true },
  ctr: { text: 'CTR', w: '50px', num: true },
  cvr: { text: 'CVR', w: '50px', num: true },
  mkt: { text: '시장 중앙가', w: '84px', num: true },
  bp: { text: '벤치마킹가', w: '84px', num: true },
  cny: { text: 'CNY', w: '84px', num: true },
  off: { text: 'offerId', w: '72px' },
  vol: { text: '부피(mm)', w: '108px' },
  wt: { text: '무게(g)', w: '64px', num: true },
  mr: { text: '밀크런', w: '68px', num: true },
  buy: { text: '매입가', w: '76px', num: true },
  sell: { text: '판매가', w: '72px', num: true },
  ppos: { text: '1p 가격위치', w: '86px' },
  frate: { text: '수수료율', w: '66px', num: true },
  feeC: { text: '수수료', w: '86px', num: true },
  mg: { text: '마진', w: '62px', num: true },
  mgr: { text: '마진율', w: '52px', num: true },
  roi: { text: 'ROI', w: '48px', num: true },
  rev: { text: '월수익', w: '84px', num: true },
  opts: { text: '옵션', w: '52px' },
  act: { text: '다음', w: '84px' },
  stg: { text: '진도', w: '320px' },
  memo: { text: '메모', w: '150px' }
}

// 그룹 = 깔때기 단계. level 은 「이 그룹을 보려면 몇 단계까지 와 있어야 하나」.
// level 0 은 언제나 채워 보이고(상품·점수·시장), verdict 그룹은 막히지 않는다.
const GROUP_LEVEL = { 상품: 0, 점수: 0, 시장: 0, 공급: 1, 스펙: 2, 원가: 3, 판정: 4 }

// 밀도는 행 높이가 아니라 하위 컬럼 개수를 바꾼다(정본 §2).
const DENSITY_COLS = {
  compact: {
    상품: ['nm'],
    점수: ['sc'],
    시장: ['rpos', 'ipos', 'opp'],
    공급: ['cny'],
    스펙: ['vol'],
    원가: ['sell', 'ppos', 'mgr', 'roi'],
    판정: ['act']
  },
  normal: {
    상품: ['nm'],
    점수: ['sc'],
    시장: ['pie', 'rpos', 'ipos', 'opp'],
    공급: ['cny', 'off'],
    스펙: ['vol', 'wt'],
    원가: ['sell', 'ppos', 'mg', 'mgr', 'roi', 'opts'],
    판정: ['act']
  },
  detail: {
    상품: ['nm', 'kw', 'cat', 'src', 'bench'],
    점수: ['sc'],
    시장: ['pie', 'sv', 'rpi', 'rpos', 'imp', 'ipos', 'opp', 'sal', 'ctr', 'cvr', 'mkt', 'bp'],
    공급: ['cny', 'off'],
    스펙: ['vol', 'wt', 'mr'],
    원가: ['buy', 'sell', 'ppos', 'frate', 'feeC', 'mg', 'mgr', 'roi', 'rev', 'opts'],
    판정: ['act', 'stg', 'memo']
  }
}

const DENSITIES = [
  { key: 'compact', text: '압축', hint: '핵심만 — 점수·기회·마진' },
  { key: 'normal', text: '기본', hint: '가로 스크롤 없이 한 화면' },
  { key: 'detail', text: '상세', hint: '원가 분해 · CTR/CVR · 밀크런까지' }
]

/* ------------------------------------------------------------------ *
 * 로켓그로스 보드 — 「점수」·「시장」 그룹을 통째로 뺀다
 *
 * 두 보드가 같은 컴포넌트를 쓰는데 담기 경로가 다르다. 담기 3화면(키워드수집기·
 * 키워드소싱분석·카테고리소싱분석)은 전부 boardType 없이 저장해 **판매/분석 보드로만**
 * 간다. 로켓그로스 보드는 행을 직접 만들고 1688·쿠팡 링크로 채우는 게 전부라
 * `keywordRaw` 에서 오는 값(검색량·노출·RPI 분포·시장파이…)이 **영구히 안 들어온다.**
 *
 * 영원히 빌 칸을 남겨두면 「아직 안 채운 칸」으로 읽혀 사용자가 채울 길을 찾다 만다.
 * ⇒ 로켓그로스는 **이미 정한 상품의 원가를 맞추는 작업대**로 두고 시장 지표는 걷어낸다.
 *
 * `rev`(월수익)도 같이 뺀다 — purchaseCost.recalcRow 가 `마진 × (월매출 / 판매가)` 로
 * 내는데 그 월매출이 곧 시장파이라, 남겨두면 영구히 null 인 컬럼이 하나 더 생긴다.
 * `kw`(키워드)는 남긴다 — 1페이지 가격대 조회의 입력이다(pricePosition 참고).
 * ------------------------------------------------------------------ */
const GROWTH_HIDDEN_COLS = [
  'sc',                                          // 점수
  'pie', 'sv', 'rpi', 'rpos', 'imp', 'ipos',     // 시장
  'opp', 'sal', 'ctr', 'cvr', 'mkt',
  'rev'                                          // 원가 — 시장파이 파생
]

// 벤치마킹가는 원래 「시장」에 있었는데 그 그룹이 통째로 빠진다. 버리지 않고 「공급」으로
// 옮긴다 — 1688 구매가 바로 옆에서 봐야 "얼마에 사서 얼마에 파나" 가 한 줄로 읽힌다.
const GROWTH_SUPPLY_COLS = ['bp']

// 로켓그로스 기본 정렬. 기본값 'sc'(진입점수)는 이 보드에 없는 컬럼이라 그대로 두면
// **보이지도 않는 컬럼으로 정렬된 채 바꿀 방법이 없다**(헤더가 없으니 클릭할 자리가 없다).
const GROWTH_SORT_KEY = 'roi'

// 로켓그로스에서만 넓히는 컬럼. 1p 가격위치 칸에는 막대·다이얼·조회 버튼이 같이 들어간다.
const GROWTH_COL_W = { ppos: '128px' }

/**
 * 밀도 스펙을 로켓그로스용으로 고친다. 원본을 건드리지 않고 새 객체를 만든다 —
 * DENSITY_COLS 는 모듈 상수라 한 번 깎으면 같은 탭의 분석 보드까지 같이 깎인다.
 * 그룹 순서는 입력 스펙의 키 순서를 그대로 물려받는다.
 *
 * `kw`(키워드)는 어느 밀도에서나 넣는다. 판매/분석 보드에서 키워드는 담기가 실어 보낸
 * 꼬리표라 상세에서만 봐도 되지만, 이 보드에서는 **사람이 직접 넣는 작업 입력**이다 —
 * 1페이지 가격대를 이 키워드로 조회한다. 상세 밀도에만 두면 압축·기본에서 조회 버튼만
 * 보이고 채울 칸이 없다.
 *
 * @param {object} spec DENSITY_COLS[density]
 * @returns {object} 같은 모양의 스펙. 빈 그룹은 groups() 가 걸러낸다
 */
function growthDensitySpec (spec) {
  const out = {}
  const moved = []
  Object.keys(spec).forEach(name => {
    out[name] = spec[name].filter(k => {
      if (GROWTH_HIDDEN_COLS.indexOf(k) >= 0) return false
      if (name !== '공급' && GROWTH_SUPPLY_COLS.indexOf(k) >= 0) { moved.push(k); return false }
      return true
    })
  })
  out['공급'] = moved.concat(out['공급'] || [])
  if (out['상품'] && out['상품'].indexOf('kw') < 0) out['상품'] = out['상품'].concat('kw')
  return out
}

// 깔때기 세그먼트. 순서가 곧 단계 순서다.
// 보류·반려는 맨 뒤 — 진행이 아니라 **빠져나간** 자리라 level 을 최고로 둔다.
// 그래야 앞 단계 그룹이 「앞이 먼저」로 덮이지 않고 판정 당시 값이 그대로 보인다.
const BUCKETS = [
  { key: 'no1688', text: '1688 못 찾음', level: 1 },
  { key: 'noSpec', text: '스펙 없음', level: 2 },
  { key: 'noMargin', text: '마진 대기', level: 3 },
  { key: 'toJudge', text: '확정 대기', level: 4 },
  { key: 'toReg', text: '등록 대기', level: 5 },
  { key: 'registered', text: '등록 완료', level: 6 },
  { key: 'held', text: '보류', level: 7 },
  { key: 'dropped', text: '반려', level: 8 }
]
const BUCKET_LEVEL = BUCKETS.reduce((m, b) => { m[b.key] = b.level; return m }, {})

// 막힌 그룹에 찍는 행동 문구. 「무엇이 없다」가 아니라 「무엇을 하라」로 쓴다.
const BLOCK_TEXT = { 공급: '1688 찾기', 스펙: '스펙 넣기', 원가: '마진 내기' }

// 판정 칸 — 깔때기 위치를 그대로 말로 옮긴다.
// **이 칸이 심사 펼침 트리거다.** 앞 단계가 비어 있어도 열린다 — 1688 을 못 찾은 행도
// 반려할 수 있어야 사유가 쌓이고, 그게 조건서를 키우는 재료다(정본 §5).
const VERDICT = {
  no1688: { tone: 'wait', text: '심사', hint: '앞 단계가 비어 있어요. 그래도 보류·반려는 지금 할 수 있어요.' },
  noSpec: { tone: 'wait', text: '심사', hint: '앞 단계가 비어 있어요. 그래도 보류·반려는 지금 할 수 있어요.' },
  noMargin: { tone: 'wait', text: '심사', hint: '앞 단계가 비어 있어요. 그래도 보류·반려는 지금 할 수 있어요.' },
  toJudge: { tone: 'go', text: '심사하기', hint: '필수 단계를 다 채웠어요. 클릭하면 심사를 열어요.' },
  toReg: { tone: 'fin', text: '확정 · 등록 대기', hint: '클릭하면 심사를 열어 등록 표시를 하거나 판정을 바꿀 수 있어요.' },
  registered: { tone: 'fin', text: '등록 완료', hint: '클릭하면 심사를 열어요.' },
  held: { tone: 'hold', text: '보류', hint: '보류한 행이에요. 클릭하면 심사를 다시 열어요.' },
  dropped: { tone: 'drop', text: '반려', hint: '반려한 행이에요. 클릭하면 심사를 다시 열어요.' }
}

/* ------------------------------------------------------------------ *
 * 심사 — 반려 사유와 규칙 승격
 *
 * 사유는 자유 텍스트가 아니라 버튼이다. 자유 텍스트만 두면 아무도 안 쓰고 학습 루프가
 * 안 돈다. 버튼 키가 곧 승격 카운터 키라 표현 차이로 카운터가 갈릴 일도 없다(정본 §5).
 * ------------------------------------------------------------------ */

// 조건 — 조건서로 승격될 수 있다. [사유, 승격되면 들어갈 규칙 문장]
const REJECT_COND = [
  ['마진 부족', '마진율 30% 이상'],
  ['부피·무게 과다', '부피 세변합 800mm 이하'],
  ['인증 필요', 'KC인증 필요 품목 제외'],
  ['경쟁 과다', '리뷰 1,000개 이상 위너가 있는 시장 제외'],
  ['공급 불안', '1688 공급처 5곳 이상 · MOQ 50 이하'],
  ['지재권 위험', '캐릭터·브랜드 카피 제외']
]

// 품질 신고 — 조건서로 **가지 않는다**. [사유, 고쳐야 할 곳]
// 「다른 상품임」을 조건서에 넣으면 "다른 상품 가져오지 마" 같은 의미 없는 문장이 쌓인다.
// 그건 비전 대조를 고칠 신호지 조건이 아니다(정본 §5).
const REJECT_QA = [
  ['다른 상품임', '비전 대조'],
  ['데이터 틀림', '수집']
]

// 같은 조건 사유가 몇 건 쌓이면 규칙 승격을 제안하나(정본 §5 「같은 취지 3번」).
const PROMOTE_AT = 3

/* ------------------------------------------------------------------ *
 * 조건서 — 문장 규칙에 붙는 **선택적** 구조 조건(`cond`)
 *
 * 규칙의 `text` 는 사람이 읽는 문장이라 기계가 「마진율 25% 이상」을 해석할 수 없다.
 * 그래서 구조 필드를 하나 더 둔다. 서버 save 가 `$set` 패스스루라 스키마 변경은 없다.
 *
 *   cond: { field: '마진율', op: 'gte', value: 25 }
 *
 *   있으면 → 보드 표를 거른다
 *   없으면 → 표는 못 거르고 「에이전트만 읽음」으로 적는다. 그게 정직하다
 *
 * ⚠ `field` 는 **행에 실제로 있는 키**여야 한다. 지어내면 아무 행도 안 걸리는데
 *    화면에는 규칙이 켜진 것처럼 보여 조용히 틀린다. 그래서 아래 목록은
 *    `SORT_KEYS` 가 행 필드를 그대로 읽는 숫자 컬럼에서만 뽑았고, 라벨도 `COLS` 에서
 *    가져온다 — 컬럼 이름이 바뀌면 여기 라벨도 같이 바뀐다.
 * ------------------------------------------------------------------ */
const COND_FIELDS = [
  ['mgr', '마진율', '%'],
  ['roi', 'ROI', '%'],
  ['mg', '마진', '원'],
  ['rev', '월수익', '원'],
  ['sell', '판매가', '원'],
  ['buy', '매입가', '원'],
  ['cny', '구매가', '元'],
  ['frate', '수수료율', '%'],
  ['wt', 'weight', 'g'],
  ['mr', '밀크런예상', '원'],
  ['pie', '월매출', '원'],
  ['sv', '평균월조회수', '회'],
  ['sal', '평균월판매량', '개'],
  ['imp', '상품_impressions', '회'],
  ['mkt', '쿠팡중간판매가', '원'],
  ['bp', '벤치마킹판매가', '원']
].map(([k, field, unit]) => ({ key: k, field, unit, label: COLS[k].text }))
const COND_FIELD_MAP = COND_FIELDS.reduce((m, f) => { m[f.field] = f; return m }, {})

// 숫자 비교만 넣는다. 문자열 포함 같은 건 이번 범위가 아니다 —
// 열 필터가 메우던 구멍(「마진율 N% 이상만 보기」)은 이 넷으로 닫힌다.
const COND_OPS = [
  { op: 'gte', text: '이상' },
  { op: 'lte', text: '이하' },
  { op: 'eq', text: '같음' },
  { op: 'neq', text: '아님' }
]
const COND_OP_TEXT = COND_OPS.reduce((m, o) => { m[o.op] = o.text; return m }, {})

// 규칙 종류(정본 §5). **hard 만 표를 거른다** — soft 는 순위인데 아직 순위에 안 쓰고,
// calc 는 후보를 떨어뜨리지 않는 계산 기준이다.
const RULE_KINDS = [
  { kind: 'hard', text: '하드 · 탈락', hint: '조건을 못 넘으면 후보에서 뺀다' },
  { kind: 'soft', text: '소프트 · 순위', hint: '순위에만 쓴다 — 아직 순위 계산에 안 넣었다' },
  { kind: 'calc', text: '계산 기준', hint: '후보를 떨어뜨리지 않는다' }
]
const RULE_KIND_MAP = RULE_KINDS.reduce((m, k) => { m[k.kind] = k; return m }, {})

// 범위 — 「항상」·「이번 탐색만」은 보이는 행 전부에, 그 밖의 값은 카테고리 이름으로 본다.
const SCOPE_ALL = '항상'
const SCOPE_RUN = '이번 탐색만'

/**
 * calc 규칙이 말하는 임계값의 **지금 값**.
 * 숫자를 여기 적어두면 `sourcingSignals` 가 바뀌는 순간 화면이 거짓말을 한다.
 * 그래서 하드코딩하지 않고 그 파일이 export 한 상수를 그대로 읽어 보여주기만 한다.
 * 편집은 이번 범위가 아니다(읽기 전용).
 */
const CALC_CONSTANTS = [
  { label: '기회계수', value: Object.keys(OPP_WEIGHT).map(k => k + ' ' + OPP_WEIGHT[k]).join(' · ') },
  { label: '라벨 없는 행의 기회계수', value: String(DEFAULT_OPP_WEIGHT) },
  { label: 'ROI 상한', value: ROI_CAP + '%' },
  { label: '시장파이 로그 기준', value: MARKET_PIE_LOG_BASE.toLocaleString() + '원' },
  { label: '진입점수 표시 배율', value: '×' + SCORE_SCALE }
]

// 밀크런비가 마진의 몇 %를 넘으면 심사에서 경고하나. 표시 임계일 뿐 판정이 아니다 —
// 부피가 큰 상품은 마진·ROI 숫자만 봐서는 안 드러나고 여기서만 보인다.
const MILKRUN_WARN_PCT = 25

// 신호 맵에 아직 행이 없을 때(렌더 타이밍 차이) 쓰는 기본값.
// 여기서 undefined 를 흘리면 깔때기 집계가 통째로 NaN 이 된다.
const EMPTY_SIGNAL = {
  score: null, opp: null, rpos: null, ipos: null, ppos: null, pdist: null,
  bucket: 'no1688', level: 1, verdict: VERDICT.no1688
}

// 기회 배지 — 보드는 판정하지 않고 담기가 실어 보낸 라벨을 색만 입혀 보여준다(정본 §4).
const OPP_TONE = {
  [OPP.GAP]: 'gap', [OPP.SAT]: 'sat', [OPP.WEAK]: 'weak',
  [OPP.THIN]: 'thin', [OPP.UNKNOWN]: 'unknown'
}
const OPP_HINT = {
  [OPP.GAP]: 'RPI 는 1페이지 상위인데 노출은 하위예요 — 노출만 더 주면 팔릴 자리예요.',
  [OPP.SAT]: 'RPI 도 노출도 상위예요 — 이미 잘 돌고 있어 뺏어야 해요.',
  [OPP.WEAK]: 'RPI 가 1페이지 하위예요 — 노출을 줘도 전환이 안 되는 상품이에요.',
  [OPP.THIN]: '클릭·판매 표본이 적어 CTR·CVR 을 믿을 수 없어요.',
  [OPP.UNKNOWN]: '대표 키워드로 안 뜬 상품이라 노출수를 모르는 상태예요.'
}

// 위치 막대가 비어 있을 때의 설명. 「빈칸」과 「모른다」를 구분해 준다.
const POS_EMPTY = {
  rpos: 'RPI 분포가 안 실린 경로에서 담긴 행이에요.',
  ipos: '노출수·노출 분포가 아직 담기에 실리지 않아요.',
  ppos: '판매가를 넣고 1페이지 가격범위가 실려야 위치를 잴 수 있어요.'
}

// 막대 색 임계(백분위). 표시 규칙일 뿐 판정이 아니다 — 기회 판정 임계값은 보드에 없다.
const POS_HIGH = 70
const POS_LOW = 30
const PRICE_EDGE_HI = 80
const PRICE_EDGE_LO = 20

// 진입점수 색 구간(표시용).
const SCORE_T3 = 20
const SCORE_T2 = 9

// 정렬 가능한 컬럼 → 정렬 키 추출기. 여기 없는 컬럼의 헤더는 비활성이다.
const SORT_KEYS = {
  nm: (r) => r.상품명 || '',
  kw: (r) => r.키워드 || '',
  cat: (r) => r.카테고리 || '',
  src: (r) => r.진도출처 || '',
  sc: (r, s) => s.score && s.score.value,
  pie: (r) => r.월매출,
  sv: (r) => r.평균월조회수,
  rpi: (r) => (r.상품_calculatedRPI != null ? r.상품_calculatedRPI : r.avgRpi),
  rpos: (r, s) => s.rpos && s.rpos.pct,
  imp: (r) => r.상품_impressions,
  ipos: (r, s) => s.ipos && s.ipos.pct,
  opp: (r, s) => (s.opp ? s.opp.rank : null),
  sal: (r) => r.평균월판매량,
  ctr: (r) => r.상품_ctr,
  cvr: (r) => r.상품_cvr,
  mkt: (r) => r.쿠팡중간판매가,
  bp: (r) => r.벤치마킹판매가,
  cny: (r) => r.구매가,
  off: (r) => r.offerId || '',
  wt: (r) => r.weight,
  mr: (r) => r.밀크런예상,
  buy: (r) => r.매입가,
  sell: (r) => r.판매가,
  ppos: (r, s) => s.ppos && s.ppos.pct,
  frate: (r) => r.수수료율,
  feeC: (r) => r.수수료,
  mg: (r) => r.마진,
  mgr: (r) => r.마진율,
  roi: (r) => r.ROI,
  rev: (r) => r.월수익,
  act: (r, s) => s.level
}

// 기회 정렬 순위 — 배지의 우선순위(노출여력이 제일 위).
const OPP_RANK = {
  [OPP.GAP]: 4, [OPP.SAT]: 3, [OPP.UNKNOWN]: 2, [OPP.THIN]: 1, [OPP.WEAK]: 0
}

const ROWS_PER_PAGE = 20

/**
 * 표 설정 localStorage 키.
 *
 * 컬럼 구성이 통째로 바뀌어 예전 CDataTable 캐시(`cdt:v1:<storageKey>`)의
 * 「숨긴 컬럼」 목록이 더 이상 의미가 없다. 그래서 **새 네임스페이스**를 쓰고
 * 옛 키는 mounted 에서 지워 무효화한다(§8-③).
 */
const PREF_NS = 'sbv2:'
const LEGACY_NS = 'cdt:v1:'

export default {
  name: 'SourcingBoardView',
  components: { ApprovalPendingPanel, ProductWizardModal, RocketOptionsModal, CategorySelectModal },
  // AI 등록 초안 — 문서 만들기는 AI 로켓등록 화면과 같은 메서드(등록번호·옵션·가격·쿠팡 문서)
  mixins: [rocketRegDraftMixin],
  props: {
    // 보드 격리 키. '' = boardType 이 없는 기존(분석) 행. 서버도 같은 규칙으로 가른다.
    boardType: { type: String, default: '' },
    // CDataTable 컬럼 표시/순서 localStorage 키. 보드마다 달라야 화면이 안 섞인다.
    storageKey: { type: String, required: true },
    description: { type: String, default: '' },
    // 옵션별 마진 컬럼·모달
    showOptions: { type: Boolean, default: true },
    // 예상 밀크런비 컬럼
    showMilkrun: { type: Boolean, default: true }
  },
  data () {
    return {
      // 쿠팡 등록 승인 대기 건의 이름 키 [{ it, full, prefix }]. ApprovalPendingPanel 이 불러온 목록에서 만든다
      approvalKeys: [],
      STAGES,
      FEE_INSPECT,
      FEE_BARCODE,
      COLS,
      DENSITIES,
      SORT_KEYS,
      POS_EMPTY,
      REJECT_COND,
      REJECT_QA,
      COND_FIELDS,
      COND_OPS,
      RULE_KINDS,
      CALC_CONSTANTS,
      SCOPE_ALL,
      // 인라인 심사 — 한 번에 하나만 연다. 행 객체에 넣으면 서버까지 저장되므로 밖에 둔다.
      expandedUid: null,
      negoSavingUid: null,  // 1688 협상 메모 «정리 저장» 중인 행
      rejectOpenUid: null,   // 반려 사유 버튼을 펼친 행
      rejectEtc: '',         // 「기타」 직접 입력
      imgFail: {},           // 후보 타일 이미지 로드 실패. key = `${_uid}|${idx}`
      ruleSheet: null,       // 지금 보고 있는 조건서(rulesheet/list 가 준다. 없으면 서버가 기본 한 벌을 시딩)
      ruleSheets: [],        // list 가 돌려준 조건서 전부. 셀렉터가 이 중에서 고른다
      sheetLoading: false,   // 조건서 로드 중
      ruleSaving: false,     // 규칙 추가/수정/삭제/토글 저장 중
      ruleDraft: null,       // 규칙 추가·수정 폼. null 이면 폼이 닫혀 있다
      specOpen: false,       // 조건서 시트 — 평소엔 닫혀 있다(정본 §1)
      showMasked: false,     // 조건서가 가린 행을 회색으로 같이 보여줄까
      sheetUsed: false,      // 이 브라우저가 조건서를 쓴 적이 있나(prefs 에 남는다)
      promo: null,           // 승격 배너 { reason, rule, scope, count }
      promoSaving: false,
      notice: '',            // 조건서 API 실패 등 사람이 읽어야 할 메시지
      // 표 상태 — 밀도/정렬/단계필터는 localStorage 에 남는다(PREF_NS)
      density: 'normal',
      stage: 'all',
      query: '',
      // 진입점수(sc)는 로켓그로스 보드에 없는 컬럼이다 — 보이지 않는 컬럼으로 정렬돼 있으면
      // 헤더가 없어 되돌릴 방법이 없다. 그래서 보드별로 기본값을 가른다.
      sortKey: this.boardType === 'growth' ? GROWTH_SORT_KEY : 'sc',
      sortDesc: true,
      page: 1,
      // 막힌 그룹을 클릭해 그 행만 입력칸을 연 상태. key = `${_uid}|${그룹명}`.
      // 행 객체에 넣으면 서버까지 저장되므로 컴포넌트 밖에 두지 않는다.
      revealed: {},
      // 정렬 순서 스냅샷(_uid 배열). 값을 입력할 때마다 행이 튀지 않도록
      // 정렬은 명시적인 시점(로드·헤더 클릭·필터 변경)에만 다시 잡는다.
      orderedUids: [],
      selected: [],
      saving: false,
      tmpSeq: 0,
      integratedRate: null,  // 통합통관환율 (ExchangeRates). 못 받으면 매입가 계산을 막는다
      rateMissing: false,    // 저장된 통합통관환율 없음(서버 integratedRateFound false — integratedRate 300 은 채운 기본값)
      currentFeeUid: null,   // wing 수수료 응답을 받을 대상 행 _uid
      feeLoadingUid: null,   // 수수료 요청 진행 중인 행 _uid (버튼 로딩)
      benchLoadingUid: null, // 벤치마킹 상품 조회 중인 행 _uid
      currentBenchUid: null,   // 벤치마킹 응답을 받을 대상 행 _uid
      alibabaLoadingUid: null, // 1688 상품정보 조회 중인 행 _uid
      priceRangeLoadingUid: null, // 1페이지 가격대 조회 중인 행 _uid
      currentPriceRangeUid: null, // 1페이지 가격대 응답을 받을 대상 행 _uid
      // 확장 요청별 타임아웃 타이머. 확장은 실패하면 아예 응답을 안 보내는 경로가 있어
      // (예: functionGetProductInfo 는 결과가 없으면 sendMessage 자체를 안 한다,
      //  fetchCommission 은 wing 탭이 없으면 alertPage 후 return) 스피너가 영원히 돈다.
      extTimers: {},
      detailItem: null,      // 상세 모달 대상 행
      // AI 등록 초안(수강생 보드만). 초안 = RocketReg 문서 · 행에는 rocketRegId(등록번호)로 잇는다
      newProduct: null,      // rocketRegDraftMixin.buildRocketRegProduct 가 읽는 입력(link·keyword·brand…)
      regDraft: null,        // 상세 모달 행에 이어진 초안 — store.rocketProducts 안의 그 객체(단계 부품이 고치는 것)
      regRow: null,          // 초안을 만드는 중인 행
      regCategoryRow: null,  // 카테고리를 고르는 중인 행
      regPendingData: null,  // 확장 응답을 기다리는 1688 상품정보
      regRequestId: null,    // 기다리는 확장 요청 id — 다른 응답·두 번째 응답은 버린다
      regBusy: '',           // 진행 중 글(비면 쉼)
      regError: '',
      showRegOptions: false,
      optionItem: null,      // 옵션 모달 대상 행
      pickerItem: null,      // 1688 picker 대상 행
      pickerLink: '',        // picker 수동 링크 입력
      pickerPrice: null,     // picker 수동 구매가 입력
      rows: []
    }
  },
  computed: {
    // 콘솔 로그 접두사 — 어느 보드에서 난 오류인지 구분한다
    logPrefix () {
      return '[' + this.storageKey + ']'
    },
    // 로켓그로스 보드인가. 컬럼 구성·기본 정렬·조건서 필드가 여기서 갈린다.
    isGrowth () {
      return this.boardType === 'growth'
    },
    // AI 등록 초안을 쓸 수 있나 — 서버 canUseRocketReg 와 같은 칸(showRocketReg · showdeveloper)
    canRocketReg () {
      const auth = (this.$store.state.user && this.$store.state.user.auth) || {}
      return !!(auth.showRocketReg || auth.showdeveloper)
    },
    // AI 등록 초안 칸 — 수강생 보드만(권한 없으면 칸 안에 개통 안내)
    showRegSection () {
      return !this.isGrowth
    },
    // 카테고리 표시 — 저장값은 쿠팡 원본 꼴(ROOT>…) 그대로 두고(가드들이 그 꼴을 읽는다) 화면에서만 떼고 코드를 붙인다
    regCategoryText () {
      const d = this.regDraft
      if (!d) return ''
      // 확장이 만든 초안은 categoryPath 끝에 « (코드)» 가 이미 붙어 있다(background.js 카테고리 꼴) — 떼고 한 번만 붙인다
      const code = String(d.categoryCode || '')
      let path = String(d.categoryPath || '').replace(/^ROOT>/, '')
      if (code) path = path.replace(new RegExp(`\\s*\\(${code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\)$`), '')
      return code ? `${path} (${code})` : path
    },
    // AI 로켓등록 기본 설정(브랜드·제조사·A/S·환율·마진) — 화면과 같은 곳(store settings)
    defaultSettings () {
      const s = this.$store.state.settings && this.$store.state.settings[0]
      return (s && s.defaultSettings) || {}
    },
    // 초안 단계 — AI 로켓등록 마법사 차례 그대로(사이즈표는 양식이 요구할 때만)
    regSteps () {
      const steps = [
        { workType: 'seo', label: '상품명(SEO)', statusKey: 'seoStatus' },
        { workType: 'pricing', label: '가격', statusKey: 'pricingStatus' },
        { workType: 'mainImage', label: '대표 사진', statusKey: 'mainImageStatus' },
        { workType: 'additionalImage', label: '추가 사진', statusKey: 'additionalImageStatus' },
        { workType: 'detailImage', label: '상세페이지', statusKey: 'detailImageStatus' },
        { workType: 'sizeChart', label: '사이즈표', statusKey: 'sizeChartStatus' },
        { workType: 'koreanLabel', label: '한글 표시사항', statusKey: 'koreanLabelStatus' },
        { workType: 'quotation', label: '속성·고시정보(견적서)', statusKey: 'quotationStatus' }
      ]
      return this.regDraft && this.isSizeChartRequired(this.regDraft) ? steps : steps.filter(s => s.workType !== 'sizeChart')
    },
    // 행 _uid → { it } | { ambiguous:true }. 같은 이름 행이 여럿이거나 승인 건이 여럿 맞으면 모호(칩만, 패널만 열기)
    approvalByUid () {
      const out = {}
      if (!this.approvalKeys.length) return out
      const nameCount = {}
      const named = []
      this.rows.forEach(r => {
        if (this.isRocketDeliveryRow(r)) return
        const n = this.normApprovalName(r.상품명)
        if (!n) return
        nameCount[n] = (nameCount[n] || 0) + 1
        named.push({ r, n })
      })
      // 앞부분 비교 건은 한 건이 여러 이름에 걸릴 수 있다 — 건마다 걸린 행 수도 센다
      const hitRows = {}
      const hits = named.map(({ r, n }) => {
        const ms = this.approvalMatches(n)
        ms.forEach(it => { hitRows[it.id] = (hitRows[it.id] || 0) + 1 })
        return { r, n, ms }
      })
      hits.forEach(({ r, n, ms }) => {
        if (!ms.length) return
        const ambiguous = ms.length > 1 || nameCount[n] > 1 || hitRows[ms[0].id] > 1
        out[r._uid] = ambiguous ? { ambiguous: true } : { it: ms[0] }
      })
      return out
    },
    // 현재 밀도의 그룹 배열. 보드별로 끄는 컬럼(옵션·밀크런)은 여기서 걸러진다.
    groups () {
      const base = DENSITY_COLS[this.density] || DENSITY_COLS.normal
      const spec = this.isGrowth ? growthDensitySpec(base) : base
      return Object.keys(spec).map(name => ({
        name,
        level: GROUP_LEVEL[name],
        verdict: name === '판정',
        keys: spec[name].filter(k => {
          if (k === 'opts') return this.showOptions
          if (k === 'mr') return this.showMilkrun
          return true
        })
      })).filter(g => g.keys.length)
    },
    // grid-template-columns. 선택 체크박스 한 칸이 맨 앞에 붙는다.
    // minmax(w, w) 로 고정폭을 주되 마지막 그룹만 남는 폭을 먹어 우측이 안 빈다.
    gridTemplate () {
      const cells = ['34px']
      const flat = this.groups.reduce((a, g) => a.concat(g.keys), [])
      flat.forEach((k, i) => {
        const w = (this.isGrowth && GROWTH_COL_W[k]) || COLS[k].w
        cells.push(i === flat.length - 1 ? `minmax(${w}, 1fr)` : w)
      })
      return cells.join(' ')
    },
    // aria-colcount — 선택 체크박스 한 칸 + 현재 밀도의 컬럼 수.
    // colspan 으로 합친 덩어리가 있어도 열 개수 자체는 변하지 않는다.
    colCount () {
      return 1 + this.groups.reduce((n, g) => n + g.keys.length, 0)
    },
    // 행별 신호 묶음. 점수·위치막대·기회·깔때기 위치를 한 번만 계산해 둔다
    // (템플릿에서 호출할 때마다 다시 계산하면 정렬 한 번에 수백 번 돈다).
    signals () {
      const map = {}
      this.rows.forEach(r => { map[r._uid] = this.buildSignal(r) })
      return map
    },
    // 깔때기 세그먼트 — 폭이 건수에 비례한다
    funnelSegments () {
      const count = {}
      BUCKETS.forEach(b => { count[b.key] = 0 })
      this.rows.forEach(r => { count[this.sig(r).bucket] += 1 })
      return BUCKETS.map(b => ({ key: b.key, text: b.text, count: count[b.key] }))
    },
    // 단계 필터 + 검색어까지 걸러낸 행 (정렬 전)
    filteredRows () {
      const q = (this.query || '').trim().toLowerCase()
      return this.rows.filter(r => {
        if (this.stage !== 'all' && this.sig(r).bucket !== this.stage) return false
        if (!q) return true
        return [r.상품명, r.키워드, r.카테고리, r.offerId, r.벤치마킹상품]
          .some(v => v != null && String(v).toLowerCase().indexOf(q) >= 0)
      })
    },
    /* ── 조건서가 표를 거르는 자리 ─────────────────────────────── *
     * CDataTable 의 열 필터 패널이 빠진 구멍을 여기서 메운다(정본 §5).
     * **행을 없애지 않고 접는다** — 조용히 빠지면 왜 안 보이는지 영영 모른다. */

    // 지금 표를 거르는 규칙 = hard · on · cond 셋을 다 갖춘 것만.
    activeCondRules () {
      const rules = (this.ruleSheet && this.ruleSheet.rules) || []
      return rules.filter(r => r && r.on && r.kind === 'hard' && this.condOf(r))
    },
    // 규칙마다 「지금 보드에서 몇 건을 떨어뜨리나」. 규칙 하나만 켠 셈 치고 각각 센다 —
    // 겹쳐 세면 어느 규칙이 목을 조르는지 안 보인다(정본 §5).
    ruleDrops () {
      const out = {}
      const rules = (this.ruleSheet && this.ruleSheet.rules) || []
      rules.forEach(r => {
        if (!r || r.kind !== 'hard' || !this.condOf(r)) return
        out[r.id] = this.rows.filter(row => this.ruleDropsRow(r, row)).length
      })
      return out
    },
    maskedUids () {
      const out = {}
      if (!this.activeCondRules.length) return out
      this.rows.forEach(row => {
        if (this.activeCondRules.some(r => this.ruleDropsRow(r, row))) out[row._uid] = true
      })
      return out
    },
    maskedCount () {
      return this.filteredRows.filter(r => this.maskedUids[r._uid]).length
    },
    // 단계·검색을 통과한 행에서 조건서가 가린 것을 뺀 목록. 「보기」를 누르면 다시 들어온다.
    visibleRows () {
      if (this.showMasked) return this.filteredRows
      return this.filteredRows.filter(r => !this.maskedUids[r._uid])
    },
    /**
     * 0건 경고 — 보이는 행이 없고 **그게 규칙 때문일 때만** 뜬다.
     * 단계 필터 탓으로 비었을 때 규칙을 탓하면 엉뚱한 규칙을 끄게 된다.
     * 승격만 있고 되돌리는 길이 없으면 조건서는 반드시 과적합한다(정본 §5) — 그 안전장치다.
     */
    chokeRule () {
      if (this.viewRows.length || !this.maskedCount) return null
      let top = null
      this.activeCondRules.forEach(r => {
        const n = this.ruleDrops[r.id] || 0
        if (!top || n > top.dropped) top = { rule: r, dropped: n }
      })
      return top && top.dropped ? top : null
    },
    // 상단 바 칩 — 조건서를 아직 안 받았으면 숫자를 지어내지 않는다.
    specChipText () {
      if (!this.ruleSheet) return ''
      const on = ((this.ruleSheet.rules || []).filter(r => r && r.on)).length
      return '규칙 ' + on + (this.maskedCount ? ' · 가려짐 ' + this.maskedCount : '')
    },
    // 정렬 스냅샷 순서대로. 스냅샷에 없는 행(방금 담은 행)은 맨 앞에 둔다.
    viewRows () {
      const pos = {}
      this.orderedUids.forEach((uid, i) => { pos[uid] = i })
      return this.visibleRows.slice().sort((a, b) => {
        const pa = pos[a._uid] === undefined ? -1 : pos[a._uid]
        const pb = pos[b._uid] === undefined ? -1 : pos[b._uid]
        return pa - pb
      })
    },
    pageCount () {
      return Math.max(1, Math.ceil(this.viewRows.length / ROWS_PER_PAGE))
    },
    pageRows () {
      const start = (this.page - 1) * ROWS_PER_PAGE
      return this.viewRows.slice(start, start + ROWS_PER_PAGE)
    },
    allChecked () {
      return this.pageRows.length > 0 && this.pageRows.every(r => this.isSelected(r))
    },
    detailTitle () {
      const it = this.detailItem
      if (!it) return '상세'
      return (it.키워드 || it.카테고리 || it.상품명 || '상세') + ' 상세'
    },
    optionTitle () {
      const it = this.optionItem
      if (!it) return '옵션별 마진'
      // 행 이름이 아직 없으면 접두사 없이 — 안 그러면 "옵션별 마진 — 옵션별 마진" 이 된다
      const name = it.상품명 || it.벤치마킹상품 || it.키워드
      return name ? name + ' — 옵션별 마진' : '옵션별 마진'
    },
    // 현재 선택된 1688 후보
    pickerCand () {
      const it = this.pickerItem
      if (!it || !it.상품매칭 || !it.상품매칭.length) return {}
      return it.상품매칭[it.currentindex || 0] || {}
    }
  },
  watch: {
    // 필터가 바뀌면 보이는 목록이 달라지므로 페이지를 처음으로 되돌린다
    stage () { this.page = 1 },
    query () { this.page = 1 },
    pageCount (n) { if (this.page > n) this.page = n }
  },
  mounted () {
    this.loadPrefs()
    // 환율을 먼저 받아야 매입가가 맞는다. 순서를 지키려고 await 로 묶는다.
    this.init()
    window.addEventListener('message', this.handleExtensionMessage)
  },
  beforeDestroy () {
    window.removeEventListener('message', this.handleExtensionMessage)
    Object.keys(this.extTimers).forEach(k => clearTimeout(this.extTimers[k]))
  },
  methods: {
    // ── 등록 대기 행 ↔ 승인 대기 ──
    // 연결 키 = 상품명. 승인표에는 소싱관리 행 id 가 없어서 이름으로 잇는다(근본 해결 = 승인 기록에 행 id — 서버 후속).
    // 이름 출처: plan.request.body.sellerProductName → 요청이 커서 body 가 잘렸으면({truncated,json}) plan.title 의
    //   «상품 「이름 앞 40자」 을 새로 등록합니다» 에서 앞부분만 꺼내 앞부분 비교.
    // 쿠팡 등록(Open API, marketplace.sourcing.registerProduct) 건만 — 로켓배송 등록(탈환의깃발 길)은 승인 대기와 엮지 않는다.
    normApprovalName (v) {
      return String(v == null ? '' : v).normalize('NFKC').replace(/\s+/g, '').toLowerCase()
    },
    onApprovalsLoaded (items) {
      const list = Array.isArray(items) ? items : []
      this.approvalKeys = list.map(it => {
        if (!it || it.source !== 'openapi' || it.tool !== 'marketplace.sourcing.registerProduct') return null
        const plan = it.plan || {}
        const body = plan.request && plan.request.body
        const full = this.normApprovalName(body && (body.sellerProductName || body.displayProductName))
        if (full) return { it, full, prefix: '' }
        const m = /「([^」]*)」/.exec(String(plan.title || ''))
        const prefix = this.normApprovalName(m && m[1])
        return prefix ? { it, full: '', prefix } : null
      }).filter(Boolean)
    },
    // 로켓배송 행은 칩 대상이 아니다. 지금 보드(분석 · 로켓그로스) 행에는 채널 칸이 없지만, 생기면 여기서 확실히 뺀다
    isRocketDeliveryRow (row) {
      if (!row) return false
      if (String(row.boardType || '') === 'rocket') return true
      return /로켓배송|^rocket$/i.test(String(row.채널 || row.channel || ''))
    },
    approvalMatches (name) {
      if (!name) return []
      return this.approvalKeys.filter(k => (k.full ? name === k.full : name.startsWith(k.prefix))).map(k => k.it)
    },
    approvalFor (row) {
      return this.approvalByUid[row && row._uid] || null
    },
    approvalLabel (a) {
      if (!a) return ''
      if (a.ambiguous) return '승인 대기 확인'
      if (a.it.status === 'approved') return '승인됨'
      if (a.it.status === 'reapprove') return '다시 승인'
      return '승인 대기'
    },
    openApproval (row) {
      const a = this.approvalFor(row)
      const panel = this.$refs.aprPanel
      if (!a || !panel || typeof panel.openFor !== 'function') return
      // 모호하면 그 건을 찍지 않고 시트만 연다(엉뚱한 건 강조 금지)
      panel.openFor(a.ambiguous ? '' : a.it.id)
    },
    async init () {
      await this.loadExchangeRate()
      await this.loadRows()
      // 조건서는 lazy 다 — `list` 는 없으면 **만드는** 엔드포인트라 화면을 열 때마다 부르면
      // 조건서를 안 쓰는 사용자에게까지 만들어진다. 다만 한 번이라도 쓴 브라우저는
      // 받아둬야 한다. 안 그러면 켜둔 hard 규칙이 새로고침 뒤 조용히 안 걸린다.
      if (this.sheetUsed) await this.loadSheets()
    },

    /* ── 표 설정(밀도·정렬·단계) 영속화 ───────────────────────────
     * 컬럼 구성이 통째로 바뀌어 예전 CDataTable 캐시는 의미가 없다.
     * 새 네임스페이스(`sbv2:`)를 쓰고 옛 키(`cdt:v1:`)는 여기서 지워 무효화한다.
     * localStorage 는 시크릿창·차단 설정에서 접근 자체가 던지므로 전부 try/catch. */
    loadPrefs () {
      try {
        localStorage.removeItem(LEGACY_NS + this.storageKey)
        const raw = localStorage.getItem(PREF_NS + this.storageKey)
        if (!raw) return
        const p = JSON.parse(raw)
        if (DENSITY_COLS[p.density]) this.density = p.density
        // 이 보드에 없는 컬럼으로 저장돼 있으면 무시한다 — 컬럼이 빠지기 전에 저장된
        // 'sc' 가 그대로 들어오면 헤더가 없어 정렬을 되돌릴 수 없다.
        if (p.sortKey && this.sortKeyAllowed(p.sortKey)) this.sortKey = p.sortKey
        if (typeof p.sortDesc === 'boolean') this.sortDesc = p.sortDesc
        if (p.stage === 'all' || BUCKET_LEVEL[p.stage]) this.stage = p.stage
        if (typeof p.sheetUsed === 'boolean') this.sheetUsed = p.sheetUsed
      } catch (e) {
        console.error(this.logPrefix, '표 설정을 읽지 못했어요', e)
      }
    },
    savePrefs () {
      try {
        localStorage.setItem(PREF_NS + this.storageKey, JSON.stringify({
          density: this.density,
          sortKey: this.sortKey,
          sortDesc: this.sortDesc,
          stage: this.stage,
          sheetUsed: this.sheetUsed
        }))
      } catch (e) {
        console.error(this.logPrefix, '표 설정을 저장하지 못했어요', e)
      }
    },

    /* ── 신호 계산 ────────────────────────────────────────────── */
    // 행 하나 → 점수·위치막대·기회·깔때기 위치. 계산식은 전부 sourcingSignals 가 갖는다.
    buildSignal (r) {
      // ROI 가 「미산출」이면 null 로 온다 — `purchaseCost.js` 의 recalcRow 가 보장한다(PR #628).
      // 매입가를 모를 때 ROI 를 0 으로 뭉개면 entryScore 가 「ROI 0%」로 읽어
      // 1688·스펙 전 행이 전부 0점으로 깔린다. 그 보장이 있으니 행을 그대로 넘긴다.
      const score = entryScore(r)
      const label = opportunityOf(r)
      const bucket = this.bucketOf(r)
      return {
        score: score === null ? null : {
          value: score.value,
          text: score.value.toFixed(1),
          firm: score.firm,
          // 인라인 심사가 세 축을 사람 말로 펴려면 계수 원본이 필요하다.
          // 툴팁 문자열을 되파싱하지 않도록 그대로 들려 보낸다.
          raw: score,
          tone: score.value >= SCORE_T3 ? 't3' : score.value >= SCORE_T2 ? 't2' : 't1',
          // 미산출 축은 계수 1.0(중립)이 들어간다 — 어느 쪽이 미산출인지 둘 다 보여야
          // "왜 잠정인지" 를 툴팁만 보고 안다. ROI 만 표시하던 때는 시장파이가 없는
          // 로켓그로스 행이 왜 잠정인지 알 길이 없었다.
          hint: '기회 ' + score.w.toFixed(2) +
            ' × ROI ' + score.roiK.toFixed(2) + (score.missing.includes('roi') ? ' (미산출)' : '') +
            ' × 시장 ' + score.pieK.toFixed(2) +
            (score.missing.includes('pie') ? ' (미산출)' : ' (월 ' + this.formatNumber(r.월매출) + '원)')
        },
        opp: label === null ? null : {
          label, tone: OPP_TONE[label], rank: OPP_RANK[label], hint: OPP_HINT[label]
        },
        rpos: this.makeBar(rpiPosition(r), 'rpi', r),
        ipos: this.makeBar(impressionPosition(r), 'imp', r),
        // 가격 분포의 출처가 보드마다 다르다 — 로켓그로스는 1페이지 min~max(구독 불필요),
        // 판매/분석은 비즈니스인사이트의 25~75% 구간. 같은 칸이지만 같은 자가 아니다.
        ppos: this.makeBar(pricePosition(r, { onePage: this.isGrowth }), 'price', r),
        // 분포만 따로 든다. 판매가가 아직 없어도 다이얼(%→판매가)은 돌아야 한다 —
        // ppos 는 판매가가 있어야 생기므로 그것만 보면 「가격대는 아는데 값을 안 정한」
        // 상태에서 다이얼이 사라진다. 그때가 다이얼이 제일 필요한 순간이다.
        pdist: priceDist(r, { onePage: this.isGrowth }),
        bucket,
        level: BUCKET_LEVEL[bucket],
        verdict: VERDICT[bucket]
      }
    },
    // 위치 막대. 백분위 공간(0~100)에 그린다 — 눈금이 분포마다 달라(가격은 IQR 2점,
    // RPI·노출은 min/median/max 3점) 값 공간에 그리면 세 막대가 서로 다른 자를 쓴다.
    makeBar (pos, kind, row) {
      if (!pos) return null
      const pct = Math.max(0, Math.min(100, pos.pct))
      const d = pos.dist || {}
      const box = d.q1 != null && d.q3 != null
      let tone = 'flat'
      if (kind === 'rpi') tone = pct >= POS_HIGH ? 'up' : pct <= POS_LOW ? 'dn' : 'mid'
      else if (kind === 'price') tone = (pct >= PRICE_EDGE_HI || pct <= PRICE_EDGE_LO) ? 'edge' : 'mid'
      return {
        pct,
        x: Math.round((1 + 42 * pct / 100) * 10) / 10,
        box,
        tone,
        label: pct >= 50 ? '상위 ' + Math.round(100 - pct) + '%' : '하위 ' + Math.round(pct) + '%',
        hint: this.barHint(kind, d, row)
      }
    },
    barHint (kind, d, r) {
      if (kind === 'rpi') {
        const top = (d.topRankMin != null && d.topRankMax != null)
          ? '\n상위권 RPI ' + this.formatNumber(d.topRankMin) + '~' + this.formatNumber(d.topRankMax) : ''
        return '1페이지 RPI — 최소 ' + this.formatNumber(d.min) +
          ' / 중간 ' + this.formatNumber(d.median) + ' / 최대 ' + this.formatNumber(d.max) + top
      }
      if (kind === 'imp') {
        return '노출수 ' + this.formatNumber(r.상품_impressions) +
          '\n1페이지 노출 — 최소 ' + this.formatNumber(d.min) +
          ' / 중간 ' + this.formatNumber(d.median) + ' / 최대 ' + this.formatNumber(d.max) +
          '\n노출수는 그 자체로 좋고 나쁨이 없어요. 판단은 「기회」 배지가 해요.'
      }
      // 분포 출처가 둘이라 설명도 갈린다. 25~75% 구간에만 맞는 문구를 min~max 막대에
      // 붙이면 "구간 밖은 잘려요" 가 거짓말이 된다 — 눈금 모양으로 가른다.
      if (d.min != null && d.max != null) {
        const n = r[ONE_PAGE_PRICE_FIELD] && r[ONE_PAGE_PRICE_FIELD].count
        return '판매가 ' + this.formatNumber(r.판매가) +
          '원\n1페이지 가격대 ' + this.formatNumber(d.min) + '~' + this.formatNumber(d.max) + '원' +
          (n ? ' (상품 ' + n + '개)' : '') +
          '\n키워드 검색 1페이지 상품들의 최저~최고가예요. 광고 상품은 빼고 봤어요.'
      }
      return '판매가 ' + this.formatNumber(r.판매가) +
        '원\n1페이지 가격대(25~75%) ' + this.formatNumber(d.q1) + '~' + this.formatNumber(d.q3) + '원' +
        '\n눈금이 사분위 둘뿐이라 구간 밖은 상·하위 25% 로 잘려요.'
    },
    // 행이 깔때기 어디에 있나. 단계 판정은 STAGE_DEFS 하나만 쓴다(새 판정 로직 금지).
    bucketOf (r) {
      // 심사 판정이 먼저다 — 판정하는 순간 깔때기 건수가 움직여야 한다.
      // 확정은 기존 `confirmed` 가 정본이라 여기서 다시 보지 않는다(judge 가 그걸 세운다).
      if (r.심사판정 === '보류') return 'held'
      if (r.심사판정 === '반려') return 'dropped'
      if (r.registered) return 'registered'
      if (r.confirmed) return 'toReg'
      if (!STAGE_DEFS[2].filled(r)) return 'no1688'
      if (!STAGE_DEFS[3].filled(r)) return 'noSpec'
      if (!STAGE_DEFS[4].filled(r)) return 'noMargin'
      return 'toJudge'
    },
    sig (row) {
      return this.signals[row._uid] || EMPTY_SIGNAL
    },

    /* ── 표 렌더 ─────────────────────────────────────────────── */
    /**
     * 이 그룹을 덮을까. 막힌 단계는 그룹 통째로 색 덩어리가 되고,
     * 그 뒤 단계는 빈칸이 아니라 「앞이 먼저」로 이유가 적힌다.
     * 사용자가 덩어리를 클릭해 연 그룹(revealed)은 그대로 입력칸을 보여준다.
     */
    blockOf (row, g) {
      if (!g.level || g.verdict) return null
      if (this.revealed[row._uid + '|' + g.name]) return null
      const lv = this.sig(row).level
      if (lv === g.level) {
        return { tone: 'todo', text: BLOCK_TEXT[g.name], hint: '클릭하면 이 칸을 열어 직접 채울 수 있어요.' }
      }
      if (lv < g.level) {
        return { tone: 'wait', text: '앞이 먼저', hint: '앞 단계를 먼저 채워야 여기 값이 의미가 있어요.' }
      }
      return null
    },
    onBlockClick (row, g) {
      const b = this.blockOf(row, g)
      // 「앞이 먼저」는 안내지 행동이 아니다 — 열어줘 봐야 채울 근거가 없다
      if (!b || b.tone !== 'todo') return
      this.$set(this.revealed, row._uid + '|' + g.name, true)
      // 공급 단계의 본 행동은 1688 매칭이다 — 칸만 열어두면 한 번 더 클릭해야 한다
      if (g.name === '공급') this.openPicker(row)
    },
    // 계산이 필요 없는 표시 전용 칸
    cellText (row, k) {
      switch (k) {
        case 'kw': return row.키워드 || '-'
        case 'cat': return row.카테고리 || '-'
        case 'src': return row.진도출처 || '-'
        case 'pie': return this.compactNum(row.월매출)
        case 'sv': return this.compactNum(row.평균월조회수)
        case 'rpi': {
          const v = row.상품_calculatedRPI != null ? row.상품_calculatedRPI : row.avgRpi
          return v != null ? this.formatNumber(Math.round(v)) : '-'
        }
        case 'imp': return this.compactNum(row.상품_impressions)
        case 'sal': return this.compactNum(row.평균월판매량)
        case 'ctr': return row.상품_ctr != null ? row.상품_ctr + '%' : '-'
        case 'cvr': return row.상품_cvr != null ? row.상품_cvr + '%' : '-'
        case 'mkt': return this.compactNum(row.쿠팡중간판매가)
        case 'bp': return this.compactNum(row.벤치마킹판매가)
        case 'rev': return this.compactNum(row.월수익)
        default: return row[k] == null ? '-' : String(row[k])
      }
    },
    // 억/만 단위로 접는다. 시장 파이·검색량은 자릿수가 길어 그대로 두면 칸을 다 먹는다.
    compactNum (n) {
      if (n == null || n === '') return '-'
      const v = Number(n)
      if (!Number.isFinite(v)) return '-'
      const a = Math.abs(v)
      if (a >= 1e8) return (Math.round(v / 1e7) / 10).toLocaleString() + '억'
      if (a >= 1e4) return Math.round(v / 1e4).toLocaleString() + '만'
      return v.toLocaleString()
    },

    /* ── 정렬 · 필터 · 선택 ──────────────────────────────────── */
    // 정렬은 명시적인 시점에만 다시 잡는다. 값을 입력할 때마다 행이 튀면 못 쓴다.
    resort () {
      const get = SORT_KEYS[this.sortKey]
      if (!get) { this.orderedUids = this.rows.map(r => r._uid); return }
      const dir = this.sortDesc ? -1 : 1
      this.orderedUids = this.rows.slice().sort((a, b) => {
        const va = get(a, this.sig(a))
        const vb = get(b, this.sig(b))
        // 값 없음은 방향과 무관하게 항상 아래. 「모른다」가 1등이 되면 안 된다.
        const ea = va == null || va === ''
        const eb = vb == null || vb === ''
        if (ea && eb) return 0
        if (ea) return 1
        if (eb) return -1
        if (typeof va === 'string' || typeof vb === 'string') {
          return String(va).localeCompare(String(vb), 'ko') * dir
        }
        return (va - vb) * dir
      }).map(r => r._uid)
    },
    // aria-sort — 정렬 중인 컬럼만 방향을 말하고, 나머지는 「정렬 가능하지만 지금은 아님」.
    // 정렬 불가 컬럼(헤더가 disabled)은 아예 속성을 안 붙인다.
    ariaSort (k) {
      if (!SORT_KEYS[k]) return null
      if (this.sortKey !== k) return 'none'
      return this.sortDesc ? 'descending' : 'ascending'
    },
    // 정렬 가능한 컬럼이면서 **이 보드에 실제로 있는** 컬럼인가.
    // SORT_KEYS 는 두 보드가 같이 쓰는 표라 여기서 한 번 더 거른다.
    sortKeyAllowed (k) {
      if (!SORT_KEYS[k]) return false
      return !(this.isGrowth && GROWTH_HIDDEN_COLS.indexOf(k) >= 0)
    },
    setSort (k) {
      if (!this.sortKeyAllowed(k)) return
      if (this.sortKey === k) this.sortDesc = !this.sortDesc
      else {
        this.sortKey = k
        this.sortDesc = !!COLS[k].num || k === 'sc'   // 숫자는 큰 값부터가 기본
      }
      this.page = 1
      this.resort()
      this.savePrefs()
    },
    setDensity (d) {
      if (!DENSITY_COLS[d]) return
      this.density = d
      this.savePrefs()
    },
    setStage (s) {
      this.stage = s
      this.savePrefs()
    },
    isSelected (row) {
      return this.selected.indexOf(row) >= 0
    },
    toggleRow (row, on) {
      const i = this.selected.indexOf(row)
      if (on && i < 0) this.selected.push(row)
      else if (!on && i >= 0) this.selected.splice(i, 1)
    },
    toggleAll (on) {
      this.pageRows.forEach(r => this.toggleRow(r, on))
    },
    /* ── 인라인 심사 ─────────────────────────────────────────── *
     * 판정 칸이 트리거고, 펼쳐지는 자리는 표 안(행 바로 아래)이다.
     * 패널을 새로 만들면 정본 §1 이 실패한 그 모양(패널 sprawl)으로 돌아간다. */

    // 한 번에 하나만 연다. 열린 행이 바뀌면 반려 사유 목록도 같이 접는다.
    toggleExpand (row) {
      this.expandedUid = this.expandedUid === row._uid ? null : row._uid
      this.rejectOpenUid = null
      this.rejectEtc = ''
    },
    // 1688 후보 = picker 가 쓰는 배열 그대로. 가져오는 경로를 새로 만들지 않는다.
    candidatesOf (row) {
      return Array.isArray(row.상품매칭) ? row.상품매칭 : []
    },
    /**
     * 비전 대조 일치도. **실데이터에 아직 없다** — 있으면 보여주고 없으면 아예 안 그린다.
     * 없는 값을 0% 로 그리면 「안 닮았다」로 읽힌다(정본 §8-④).
     */
    fitOf (cand) {
      if (!cand) return null
      const v = cand.일치도 != null ? cand.일치도 : cand.fit
      const n = Number(v)
      return Number.isFinite(n) ? Math.round(n) : null
    },
    // 이미지가 죽어도 타일을 빼지 않는다 — 번호가 밀리면 사람이 다른 후보를 고른다.
    markImgFail (uid, idx) {
      this.$set(this.imgFail, uid + '|' + idx, true)
    },
    /**
     * 「왜 이 점수인가」 — 계산은 하지 않는다. sourcingSignals·purchaseCost 가 이미 낸
     * 값을 문장으로 펼 뿐이다. 미산출 축은 0 으로 뭉개지 말고 **(미산출)** 로 적는다.
     */
    whyLines (row) {
      const s = this.sig(row)
      const out = []
      if (s.score && s.score.raw) {
        const r = s.score.raw
        out.push({
          text: '진입점수 ' + s.score.text +
            ' = 기회 ' + r.w.toFixed(2) +
            ' × ROI ' + (r.missing.indexOf('roi') >= 0 ? '(미산출)' : r.roiK.toFixed(2)) +
            ' × 시장 ' + (r.missing.indexOf('pie') >= 0
              ? '(미산출)'
              : r.pieK.toFixed(2) + ' (월 ' + this.formatNumber(row.월매출) + '원)')
        })
      }
      out.push({
        text: '1p 안에서  ' + [['rpos', 'RPI'], ['ipos', '노출'], ['ppos', '가격']]
          .map(([k, nm]) => nm + ' ' + (s[k] ? s[k].label : '(미산출)')).join('  ·  ')
      })
      if (s.opp) out.push({ text: '기회 ' + s.opp.label + ' — ' + s.opp.hint })
      // 부피가 마진을 먹는 정도. 마진·ROI 숫자만 봐서는 안 드러난다.
      if (row.밀크런예상 > 0 && row.마진 > 0) {
        const share = Math.round(row.밀크런예상 / row.마진 * 100)
        if (share >= MILKRUN_WARN_PCT) {
          out.push({
            warn: true,
            text: '⚠ 예상 밀크런비 ' + this.formatNumber(row.밀크런예상) +
              '원이 마진의 ' + share + '% 를 먹어요.'
          })
        }
      }
      // 표본 기준은 담기(keywordDataCollect)가 판정해 라벨로 보낸 값을 읽기만 한다.
      // 여기서 클릭·판매 수를 다시 세면 임계값이 두 층으로 갈린다(정본 §4).
      if (opportunityOf(row) === OPP.THIN) {
        out.push({ warn: true, text: '⚠ 표본 부족 — 클릭 11 · 판매 3 미만이라 CTR·CVR 을 믿을 수 없어요.' })
      }
      return out
    },
    // 심사 결과는 행에 인라인으로 붙는다 — `confirmed`/`registered` 와 같은 관례라
    // save 가 `$set` 패스스루라서 스키마 변경이 없다(정본 §7 「심사 기록」).
    setVerdict (row, verdict, reason, kind) {
      this.$set(row, '심사판정', verdict)
      this.$set(row, '심사사유', reason || '')
      this.$set(row, '심사사유종류', kind || '')
      this.$set(row, '심사시각', Date.now())
    },
    /**
     * 소싱확정 / 보류.
     * **확정은 새 게이트를 만들지 않고 기존 `toggleConfirm()`(= `essentialsMet()`)을 탄다.**
     * 게이트에 막히면 판정도 남기지 않는다 — 안 그러면 「확정인데 깔때기는 마진 대기」가 된다.
     */
    judge (row, verdict) {
      if (verdict === '확정') {
        if (!row.confirmed) {
          this.toggleConfirm(row)
          if (!row.confirmed) return
        }
        this.setVerdict(row, '확정', '', '')
      } else {
        // 보류는 확정·등록을 되돌린다. 남겨두면 깔때기에 확정으로 계속 잡힌다.
        if (row.confirmed) this.$set(row, 'confirmed', false)
        if (row.registered) this.$set(row, 'registered', false)
        this.setVerdict(row, verdict, '', '')
      }
      this.expandedUid = null
      this.rejectOpenUid = null
    },
    toggleReject (row) {
      this.rejectOpenUid = this.rejectOpenUid === row._uid ? null : row._uid
      this.rejectEtc = ''
    },
    /**
     * 반려. `kind` 로 사유의 무리가 갈린다(정본 §5).
     *   cond — 조건 문제. 카운터를 쌓고 3건이면 조건서 승격을 제안한다
     *   qa   — 탐색 품질 문제. **조건서로 보내지 않는다**
     *   etc  — 직접 입력. 여기 쌓인 게 다음에 버튼이 된다(지금은 행에만 남긴다)
     */
    async reject (row, reason, kind) {
      if (!reason) return
      if (row.confirmed) this.$set(row, 'confirmed', false)
      if (row.registered) this.$set(row, 'registered', false)
      this.setVerdict(row, '반려', reason, kind)
      this.expandedUid = null
      this.rejectOpenUid = null
      this.rejectEtc = ''
      if (kind === 'cond') await this.bumpReason(reason)
    },
    /**
     * 조건서를 처음 필요할 때 한 번만 받아온다.
     * 화면을 열 때마다 부르지 않는 이유 — `list` 는 조건서가 없으면 **만드는** 엔드포인트라,
     * 반려를 한 번도 안 하는 사용자에게까지 조건서를 만들어 둔다.
     */
    async ensureRuleSheet () {
      if (this.ruleSheet) return this.ruleSheet
      const res = await axios.post('/api/growth/rulesheet/list', {})
      const sheets = Array.isArray(res.data) ? res.data.filter(s => s && s._id) : []
      if (!sheets.length) throw new Error('조건서를 받지 못했습니다')
      this.ruleSheets = sheets
      this.ruleSheet = sheets[0]
      // 「이 브라우저는 조건서를 쓴다」 표식. 다음 방문에 hard 규칙이 조용히 빠지지 않게
      // init 이 이걸 보고 미리 받아온다. 쓴 적 없는 사용자에게는 여전히 안 만든다.
      if (!this.sheetUsed) { this.sheetUsed = true; this.savePrefs() }
      return this.ruleSheet
    },
    // 조건서 시트를 열 때/재방문 때 쓰는 진입점. `ensureRuleSheet` 는 중복 호출을
    // 스스로 막으므로(이미 받았으면 그대로 돌려준다) 여기서 또 부르지 않는다.
    async loadSheets () {
      if (this.ruleSheet || this.sheetLoading) return
      this.sheetLoading = true
      try {
        await this.ensureRuleSheet()
      } catch (e) {
        console.error(this.logPrefix, '조건서 로드 실패', e)
        this.notice = '조건서를 불러오지 못했어요. 잠시 후 다시 열어 주세요.'
      } finally {
        this.sheetLoading = false
      }
    },
    async openSpec () {
      this.specOpen = true
      await this.loadSheets()
    },
    selectSheet (id) {
      const hit = this.ruleSheets.find(s => String(s._id) === String(id))
      if (!hit) return
      this.ruleSheet = hit
      this.ruleDraft = null
    },

    /* ── 조건서 규칙 — 읽기·판정 ──────────────────────────────── */
    /**
     * 구조 조건을 꺼낸다. 모양이 안 맞으면 **조건이 없는 것으로 본다** —
     * 행에 없는 키나 모르는 연산자를 그대로 쓰면 아무 행도 안 걸리는데
     * 화면에는 규칙이 켜진 것처럼 보여 조용히 틀린다.
     */
    condOf (rule) {
      const c = rule && rule.cond
      if (!c || !c.field || !c.op) return null
      if (!COND_FIELD_MAP[c.field] || !COND_OP_TEXT[c.op]) return null
      const v = Number(c.value)
      return Number.isFinite(v) ? { field: c.field, op: c.op, value: v } : null
    },
    // 이 규칙의 범위가 이 행에 닿나. 「항상」·「이번 탐색만」은 보이는 행 전부,
    // 그 밖의 값은 카테고리 이름으로 보고 그 카테고리 행에만 건다.
    ruleInScope (rule, row) {
      const sc = String((rule && rule.scope) || '').trim()
      if (!sc || sc === SCOPE_ALL || sc === SCOPE_RUN) return true
      return String(row.카테고리 || '').indexOf(sc) >= 0
    },
    /**
     * 이 규칙이 이 행을 떨어뜨리나.
     *
     * ⚠ 값이 없는 행(`null`/`undefined`/빈 문자열)은 **절대 떨어뜨리지 않는다.**
     *   아직 마진을 모르는 행을 「마진율 25% 이상」으로 거르면 「모른다」가
     *   「조건 미달」로 둔갑한다(정본 §8-④).
     */
    ruleDropsRow (rule, row) {
      const c = this.condOf(rule)
      if (!c || !this.ruleInScope(rule, row)) return false
      const raw = row[c.field]
      if (raw === null || raw === undefined || raw === '') return false
      const v = Number(raw)
      if (!Number.isFinite(v)) return false
      switch (c.op) {
        case 'gte': return !(v >= c.value)
        case 'lte': return !(v <= c.value)
        case 'eq': return !(v === c.value)
        case 'neq': return !(v !== c.value)
        default: return false
      }
    },
    // 가려진 행에 왜 가려졌는지 붙여둔다. 이유 없이 회색이면 표시가 아니라 버그로 읽힌다.
    maskedReason (row) {
      if (!this.maskedUids[row._uid]) return null
      const hit = this.activeCondRules.filter(r => this.ruleDropsRow(r, row))
      return '조건서로 가려진 행 — ' + hit.map(r => '「' + r.text + '」').join(' · ')
    },
    condText (rule) {
      const c = this.condOf(rule)
      if (!c) return null
      const f = COND_FIELD_MAP[c.field]
      return f.label + ' ' + c.value.toLocaleString() + (f.unit || '') + ' ' + COND_OP_TEXT[c.op]
    },
    kindText (rule) {
      const k = RULE_KIND_MAP[rule && rule.kind]
      return k ? k.text : '종류 없음'
    },
    kindHint (rule) {
      const k = RULE_KIND_MAP[rule && rule.kind]
      return k ? k.hint : '종류가 없어 표에도 순위에도 안 쓰여요.'
    },
    scopeHint (rule) {
      const sc = String((rule && rule.scope) || '').trim()
      if (!sc || sc === SCOPE_ALL) return '모든 행에 걸려요.'
      if (sc === SCOPE_RUN) return '지금 화면에 올라온 행 전부에 걸려요.'
      return '카테고리가 「' + sc + '」 인 행에만 걸려요.'
    },

    /* ── 조건서 규칙 — 쓰기 ───────────────────────────────────── */
    /**
     * 규칙 배열을 통째로 저장한다. **추가·수정·삭제·토글이 전부 여기를 탄다.**
     * ⚠ `rulesheet/delete` 는 조건서 **문서를 통째로** 지우는 엔드포인트다.
     *   규칙 하나를 지우는 데 쓰면 조건서가 사라진다 — 배열에서 빼고 save 하는 게 맞다.
     * 조건서 문서 전체가 아니라 `rules` 만 보낸다 — save 가 `$set` 이라
     * 나머지 필드(`reasonCounters`·`name`)는 서버 값이 그대로 남는다.
     */
    async saveRules (rules, failMsg) {
      const sheet = this.ruleSheet
      if (!sheet) return false
      // ★ 배열이 아니면 서버에 보내기 전에 막는다. save 는 `rules` 를 통째로 $set 하므로
      //   `undefined` 를 그대로 보내면 서버는 OK 를 주고, 조건서의 규칙이 전부 사라진다.
      //   빈 배열 `[]` 은 정상이다 — 규칙을 전부 지우는 경우라 막지 않는다.
      if (!Array.isArray(rules)) {
        console.error(this.logPrefix, '조건서 저장을 막았습니다 — rules 가 배열이 아닙니다', rules)
        this.notice = (failMsg || '조건서를 저장하지 못했어요.') + ' 잠시 후 다시 시도해 주세요.'
        return false
      }
      this.ruleSaving = true
      try {
        const res = await axios.post('/api/growth/rulesheet/save', [{ _id: sheet._id, rules }])
        if (!res.data || res.data.res !== 'OK') throw new Error('조건서가 OK 를 주지 않았습니다')
        this.$set(sheet, 'rules', rules)
        return true
      } catch (e) {
        console.error(this.logPrefix, failMsg, e)
        this.notice = failMsg + ' 잠시 후 다시 시도해 주세요.'
        return false
      } finally {
        this.ruleSaving = false
      }
    },
    // 켜고 끄기는 표가 즉시 반응해야 쓸모가 있다. 먼저 바꾸고, 서버가 거절하면 되돌린다.
    async toggleRule (rule) {
      const sheet = this.ruleSheet
      if (!sheet) return
      const prev = sheet.rules || []
      const rules = prev.map(r => (r.id === rule.id ? { ...r, on: !r.on } : r))
      this.$set(sheet, 'rules', rules)
      const ok = await this.saveRules(rules, '규칙을 켜고 끄지 못했어요.')
      if (!ok) this.$set(sheet, 'rules', prev)
    },
    startAddRule () {
      this.ruleDraft = {
        id: null, text: '', scope: SCOPE_ALL, kind: 'hard',
        useCond: true, field: COND_FIELDS[0].field, op: 'gte', value: null
      }
    },
    startEditRule (rule) {
      const c = this.condOf(rule)
      this.ruleDraft = {
        id: rule.id,
        text: rule.text || '',
        scope: rule.scope || SCOPE_ALL,
        kind: rule.kind || 'hard',
        useCond: !!c,
        field: c ? c.field : COND_FIELDS[0].field,
        op: c ? c.op : 'gte',
        value: c ? c.value : null
      }
    },
    cancelRuleDraft () {
      this.ruleDraft = null
    },
    /**
     * 규칙 추가·수정 저장.
     * 수정일 때 `on`·`droppedCount`·`origin` 은 원래 값을 그대로 둔다 — 문장을 고쳤다고
     * 「N건 반려에서 승격」 같은 출처가 사라지면 이 규칙이 어디서 왔는지 알 수 없게 된다.
     */
    async submitRuleDraft () {
      const d = this.ruleDraft
      if (!d) return
      const text = (d.text || '').trim()
      if (!text) { this.notice = '규칙 문장을 적어 주세요.'; return }
      const wantCond = d.kind === 'hard' && d.useCond
      const value = Number(d.value)
      if (wantCond && (d.value === null || d.value === '' || !Number.isFinite(value))) {
        this.notice = '조건 값을 숫자로 적어 주세요.'
        return
      }
      const base = (this.ruleSheet && this.ruleSheet.rules) || []
      const old = d.id ? base.find(r => r.id === d.id) : null
      const next = {
        ...(old || { on: true, droppedCount: 0, origin: '손으로 씀 · ' + this.todayText() }),
        id: d.id || 'r' + Date.now(),
        text,
        scope: (d.scope || '').trim() || SCOPE_ALL,
        kind: d.kind
      }
      if (wantCond) next.cond = { field: d.field, op: d.op, value }
      else delete next.cond
      const rules = d.id ? base.map(r => (r.id === d.id ? next : r)) : base.concat([next])
      const ok = await this.saveRules(rules, d.id ? '규칙을 고치지 못했어요.' : '규칙을 추가하지 못했어요.')
      if (ok) this.ruleDraft = null
    },
    async deleteRule (rule) {
      if (!confirm('「' + rule.text + '」 규칙을 지울까요?')) return
      const rules = ((this.ruleSheet && this.ruleSheet.rules) || []).filter(r => r.id !== rule.id)
      const ok = await this.saveRules(rules, '규칙을 지우지 못했어요.')
      if (ok && this.ruleDraft && this.ruleDraft.id === rule.id) this.ruleDraft = null
    },
    // 같은 조건 사유가 몇 번째인지 서버가 원자적으로($inc) 세어 돌려준다.
    // 여러 행을 연달아 반려해도 카운트가 새지 않는다.
    async bumpReason (reason) {
      try {
        const sheet = await this.ensureRuleSheet()
        const res = await axios.post('/api/growth/rulesheet/bumpReason', {
          sheetId: sheet._id, reason
        })
        const d = res.data || {}
        if (d.res !== 'OK') throw new Error(d.alert || '조건서가 OK 를 주지 않았습니다')
        if (d.count >= PROMOTE_AT) {
          const hit = REJECT_COND.find(r => r[0] === reason)
          this.promo = { reason, rule: hit ? hit[1] : reason, scope: '항상', count: d.count }
        }
      } catch (e) {
        // 반려 자체는 행에 남았다. 조용히 넘기면 사유는 쌓이는데 승격이 영원히 안 뜬다.
        console.error(this.logPrefix, '반려 사유 누적 실패', e)
        this.notice = '반려는 이 화면에 기록했지만 조건서에 사유를 쌓지 못했어요. ' +
          '규칙 제안이 안 뜨면 새로고침한 뒤 다시 반려해 주세요.'
      }
    },
    /**
     * 「추가」 — 조건서에 규칙 한 줄을 넣는다.
     * 승격된 규칙에는 **`cond` 를 붙이지 않는다.** 문장("마진율 30% 이상")에서 숫자를
     * 추측해 조건을 만들면 사람이 확인하지 않은 필터가 표를 거르게 된다.
     * 조건서 시트에서 「수정」으로 직접 넣게 하고, 그 전까지는 「에이전트만 읽음」으로 적는다.
     */
    async acceptPromo () {
      const p = this.promo
      if (!p || this.promoSaving) return
      this.promoSaving = true
      try {
        const sheet = await this.ensureRuleSheet()
        const rules = Array.isArray(sheet.rules) ? sheet.rules.slice() : []
        rules.push({
          id: 'r' + Date.now(),
          text: p.rule,
          scope: p.scope,
          kind: 'hard',
          on: true,
          droppedCount: 0,
          origin: p.count + '건 반려에서 승격 · ' + this.todayText()
        })
        const res = await axios.post('/api/growth/rulesheet/save', [{ _id: sheet._id, rules }])
        if (!res.data || res.data.res !== 'OK') throw new Error('조건서 저장이 실패했습니다')
        this.$set(sheet, 'rules', rules)
        this.promo = null
        this.notice = '조건서에 「' + p.rule + '」를 추가했어요. ' +
          '표에서도 거르려면 조건서를 열어 이 규칙에 숫자 조건을 넣어 주세요.'
      } catch (e) {
        console.error(this.logPrefix, '조건서 규칙 추가 실패', e)
        this.notice = '조건서에 규칙을 추가하지 못했어요. 잠시 후 다시 시도해 주세요.'
      } finally {
        this.promoSaving = false
      }
    },
    todayText () {
      const d = new Date()
      const p = n => (n < 10 ? '0' : '') + n
      return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate())
    },
    // 통합통관환율 — logisreqpurchaseV0 와 같은 원천(ExchangeRates).
    // 못 받으면 매입가를 임의값으로 계산하지 않고 비워 둔다(틀린 마진을 보여주느니 안 보여준다).
    async loadExchangeRate () {
      try {
        const res = await axios.post('/api/getExchangeRate', {})
        const d = res.data && res.data.data
        // integratedRateFound false = 저장 0 이라 서버가 기본값 300 을 채운 것 — 환율로 쓰지 않는다(판 #505)
        //   칸이 없는 옛 서버는 integratedRate 그대로(하위호환)
        if (d && d.integratedRateFound === false) {
          this.rateMissing = true
          console.error(this.logPrefix, '저장된 통합통관환율 없음(기본값 안 씀)', d.integratedRate)
        } else if (d && d.integratedRate) this.integratedRate = Number(d.integratedRate)
        else console.error(this.logPrefix, '통합통관환율이 응답에 없음', res.data)
      } catch (e) {
        console.error(this.logPrefix, '환율 조회 실패', e)
      }
    },
    async loadRows () {
      try {
        const body = this.boardType ? { boardType: this.boardType } : {}
        const res = await axios.post('/api/sourcing/board/list', body)
        // ★ 서버가 안 걸러줘도 여기서 한 번 더 거른다.
        //   프론트만 먼저 배포되면(deploy_target=frontend 는 백엔드 스텝을 skip 한다)
        //   서버가 다른 보드 행까지 돌려주는데, 그 상태로 저장하면 saveRows 가 그 행들에
        //   이 보드의 boardType 을 찍어버려 남의 보드 행을 통째로 뺏는다.
        const rows = (res.data || []).filter(r => (r.boardType || '') === this.boardType)
        // _uid: CDataTable itemKey용 클라 식별자 (_id 그대로 사용, 신규행은 'new-N')
        this.rows = rows.map(r => this.normalizeRow({ ...r, _uid: r._id }))
        this.rows.forEach(r => this.changeCost(r))   // 환율·계수가 바뀌었을 수 있어 표시 전 재계산
        this.selected = []
        this.revealed = {}   // 열어둔 막힘 칸은 로드 기준으로 초기화
        // 행 객체가 통째로 갈리므로 _uid 로 잡아둔 화면 상태도 같이 버린다.
        this.expandedUid = null
        this.rejectOpenUid = null
        this.imgFail = {}
        this.resort()
      } catch (e) {
        console.error(this.logPrefix, '목록 로드 실패', e)
      }
    },
    async saveRows () {
      this.saving = true
      try {
        // _uid는 클라 전용 — 서버 저장 전 제거. boardType 은 반드시 실어야 목록에서 다시 보인다.
        // 저장 대상은 이 보드 행(신규행은 아직 boardType 이 붙은 상태)으로 한정한다 —
        // 어떤 경로로든 남의 보드 행이 섞여 들어왔을 때 그 행에 이 보드 값을 찍어 뺏지 않도록.
        // boardType 이 '' 인 보드(분석)는 키 자체를 넣지 않는다 — 서버가 $exists:false 로
        // 찾기 때문에 빈 문자열을 저장하면 그 행이 목록에서 사라진다.
        const payload = this.rows
          .filter(r => !r.boardType || r.boardType === this.boardType)
          .map(r => {
            const copy = { ...r }
            if (this.boardType) copy.boardType = this.boardType
            delete copy._uid
            // 협상(negotiation)은 보내지 않는다 — 화면이 받을 때의 낡은 협상·대화로 그 사이 도구가 쓴 것을 되쓰지 않게.
            // 협상 정리는 «정리 저장»(saveNegoSummary)이 그 칸만 따로 보낸다(cpl-nego-negomemo-1)
            delete copy.negotiation
            Object.keys(copy).forEach(k => { if (k.startsWith('_nego')) delete copy[k] })
            return copy
          })
        const res = await axios.post('/api/sourcing/board/save', payload)
        if (res.data && res.data.res === 'OK') {
          await this.loadRows()
        } else {
          alert('저장에 실패했어요. 잠시 후 다시 시도해 주세요.')
        }
      } catch (e) {
        console.error(this.logPrefix, '저장 실패', e)
        alert('저장 중 오류가 발생했어요.')
      } finally {
        this.saving = false
      }
    },
    async deleteSelected () {
      if (!this.selected.length) return
      if (!confirm(`선택한 ${this.selected.length}건을 삭제할까요?`)) return
      const saved = this.selected.filter(r => r._id)
      try {
        if (saved.length) {
          await axios.post('/api/sourcing/board/delete', saved.map(r => ({ _id: r._id })))
        }
        // 미저장 임시행까지 로컬에서 제거
        const delUids = new Set(this.selected.map(r => r._uid))
        this.rows = this.rows.filter(r => !delUids.has(r._uid))
        this.selected = []
      } catch (e) {
        console.error(this.logPrefix, '삭제 실패', e)
        alert('삭제 중 오류가 발생했어요.')
      }
    },
    // 입력/계산 키를 기본값으로 채워 Vue2 반응성 확보
    normalizeRow (r) {
      const numKeys = [
        '월매출', '평균월판매량', '평균전환율', '구매가', '중국내운임', '발주수량', '매입가',
        'sizeX', 'sizeY', 'sizeZ', 'weight', '판매가', '수수료율', '수수료',
        '마진', '마진율', 'ROI', '월수익', '밀크런예상', 'categoryId',
        'fullfillmentFee', 'warehouseFee', 'commisionRate'  // wing 실수수료(VAT 포함)
      ]
      numKeys.forEach(k => { if (r[k] === undefined) r[k] = null })
      if (r.수수료율 == null) r.수수료율 = 10.8  // 임시 수수료율(%) — wing 실측 전 기본
      if (r.confirmed == null) r.confirmed = false
      if (r.registered == null) r.registered = false
      // 심사 결과는 행에 인라인으로 붙는다. Vue2 반응성을 위해 키를 미리 세워 둔다 —
      // v-model 이 붙는 심사메모는 키가 없으면 입력이 화면에 안 반영된다.
      if (r.심사판정 == null) r.심사판정 = ''
      if (r.심사사유 == null) r.심사사유 = ''
      if (r.심사사유종류 == null) r.심사사유종류 = ''
      if (r.심사메모 == null) r.심사메모 = ''
      if (!Array.isArray(r.옵션)) r.옵션 = []
      // 1688 협상 메모 — 정리 입력칸·저장본·대화 펼침(화면 전용 _nego* · 저장 때 빠짐)
      const negoSummary = r.negotiation && typeof r.negotiation.summary === 'string' ? r.negotiation.summary : ''
      r._negoSummary = negoSummary
      r._negoSummarySaved = negoSummary
      r._negoOpen = false
      r._negoMore = false
      return r
    },
    // 협상 정리만 그 행 하나로 저장 — 서버가 negotiation 을 키 단위로 합쳐 9칸·대화 원본은 그대로 둔다
    async saveNegoSummary (row) {
      const summary = String(row._negoSummary || '').replace(/\r\n?/g, '\n').trim()
      if (!summary || summary === row._negoSummarySaved) return
      // 새 행(_id 없음)에 보내면 서버가 행을 새로 만든다 — 보드 «저장» 먼저
      if (!row._id) { alert('새 행은 먼저 보드를 저장한 뒤 협상 정리를 저장해 주세요.'); return }
      if (summary.length > 2000) { alert(`협상 정리는 2000자까지예요(지금 ${summary.length}자).`); return }
      this.negoSavingUid = row._uid
      try {
        const res = await axios.post('/api/sourcing/board/save', [{ _id: row._id, negotiation: { summary, summaryBy: 'user' } }])
        if (res.data && res.data.res === 'OK') {
          this.$set(row, 'negotiation', { ...(row.negotiation || {}), summary, summaryBy: 'user', summaryAt: new Date().toISOString() })
          row._negoSummary = summary
          row._negoSummarySaved = summary
        } else {
          alert('협상 정리 저장에 실패했어요. 잠시 후 다시 시도해 주세요.')
        }
      } catch (e) {
        console.error(this.logPrefix, '협상 정리 저장 실패', e)
        alert('협상 정리 저장 중 오류가 발생했어요.')
      } finally {
        this.negoSavingUid = null
      }
    },
    // 대화 원본 — 판매자별 묶음 · 시각순 · 처음엔 최근 20건(«더 보기» 면 전부)
    negoThreadGroups (row) {
      const thread = row && row.negotiation && Array.isArray(row.negotiation.thread) ? row.negotiation.thread : []
      const by = {}
      thread.forEach(m => {
        if (!m || typeof m.text !== 'string') return
        const sid = m.sellerId || '판매자'
        ;(by[sid] = by[sid] || []).push(m)
      })
      return Object.keys(by).map(sellerId => {
        const all = by[sellerId].slice().sort((a, b) => String(a.ts).localeCompare(String(b.ts)))
        const msgs = row._negoMore ? all : all.slice(-20)
        return { sellerId, total: all.length, hidden: all.length - msgs.length, msgs }
      })
    },
    negoThreadCount (row) {
      return row && row.negotiation && Array.isArray(row.negotiation.thread) ? row.negotiation.thread.length : 0
    },
    // 9칸 칩 — 값 있는 것만
    negoChips (row) {
      const n = (row && row.negotiation) || {}
      const L = [['priceRange', '가격'], ['moq', 'MOQ'], ['leadTime', '납기'], ['cert', '인증'], ['logistics', '물류'], ['payment', '결제'], ['sample', '샘플'], ['responseQuality', '응답']]
      return L.filter(([k]) => n[k] != null && n[k] !== '').map(([k, label]) => ({ k, label, v: String(n[k]) }))
    },
    hasNego (row) {
      const n = row && row.negotiation
      return !!(n && typeof n === 'object' && Object.keys(n).length)
    },
    negoTime (iso) {
      if (!iso) return ''
      const d = new Date(iso)
      if (isNaN(d)) return ''
      const p = (x) => String(x).padStart(2, '0')
      return `${d.getMonth() + 1}/${d.getDate()} ${p(d.getHours())}:${p(d.getMinutes())}`
    },
    /**
     * 구매가(CNY) → 매입가 → 마진.
     *
     * 매입가를 단순 환산비율(구 매입가율 195)로 잡지 않고 실제 부대비용까지 분해한다.
     *   매입가 = round(CNY × 통합통관환율 × 1.1) + 검수 200 + 바코드 100 + 예상 밀크런비
     * 통합통관 공식은 쿠패스 구매요청(logisreqpurchaseV0 / server.js createQuotation1688)과 같다.
     * 환율을 못 받았으면 임의값으로 계산하지 않고 비워 둔다 — 틀린 마진을 보여주는 것보다 낫다.
     */
    changeCost (item) {
      recalcRow(item, this.integratedRate)
      // 옵션이 있으면 같은 전제(수수료율·밀크런)로 옵션도 다시 계산한다
      if (Array.isArray(item.옵션)) item.옵션.forEach(o => this.changeOptionCost(item, o, true))
    },
    // ── 옵션별 마진 ────────────────────────────────────────────
    // 1688 옵션마다 구매가(CNY)가 달라 매입가부터 다시 계산한다.
    // 수수료율·풀필먼트·보관·밀크런은 같은 상품이라 행 값을 공유한다.
    changeOptionCost (item, o, silent) {
      if (!o) return
      recalcOption(item, o, this.integratedRate)
      // 유틸이 직접 대입하므로 신규 키는 Vue2 가 못 잡는다 — 반응성을 붙여준다
      ;['매입가', '수수료', '마진', 'ROI', '마진율'].forEach(k => this.$set(o, k, o[k]))
      if (!silent) this.$forceUpdate()
    },
    openOptions (item) {
      if (!Array.isArray(item.옵션)) this.$set(item, '옵션', [])
      this.optionItem = item
      this.$bvModal.show('sb-option-modal')
    },
    addOption (item) {
      item.옵션.push({ 옵션명: '', 구매가: item.구매가 || null, 판매가: item.판매가 || null })
      this.changeOptionCost(item, item.옵션[item.옵션.length - 1])
    },
    removeOption (item, idx) {
      item.옵션.splice(idx, 1)
    },
    // 매칭된 1688 후보의 옵션 배열을 옵션 행으로 가져온다.
    // 1688 응답의 옵션 키가 소스마다 달라(options/skus/옵션/sku) 폴백으로 훑는다.
    importOptionsFrom1688 (item) {
      const cand = (item.상품매칭 || [])[item.currentindex || 0]
      if (!cand) {
        alert('먼저 1688 상품을 매칭해 주세요.')
        return
      }
      const opts = cand.options || cand.skus || cand.옵션 || cand.sku
      if (!Array.isArray(opts) || !opts.length) {
        alert('이 상품에는 가져올 옵션 정보가 없어요.')
        return
      }
      const rows = opts.map(o => {
        if (o && typeof o === 'object') {
          return {
            옵션명: String(o.옵션명 || o.name || o.title || o.sku || o.properties_name || ''),
            구매가: Number(o.가격 || o.price || cand.price) || null,
            판매가: item.판매가 || null
          }
        }
        return { 옵션명: String(o), 구매가: Number(cand.price) || null, 판매가: item.판매가 || null }
      })
      this.$set(item, '옵션', rows)
      rows.forEach(o => this.changeOptionCost(item, o, true))
      this.$forceUpdate()
    },
    // ── 매입가/밀크런 표시 ─────────────────────────────────────
    costBreakdownShort (item) {
      if (!item.매입가) return ''
      const 통관 = this.customsOnly(item.구매가)
      const 운임 = this.shipKrw(item)
      return `통관 ${this.formatNumber(통관)} + ${FEE_INSPECT_VAT + FEE_BARCODE_VAT}${운임 ? ` + 운임 ${this.formatNumber(운임)}` : ''} + 밀크런 ${this.formatNumber(item.밀크런예상 || 0)}`
    },
    costBreakdownText (item) {
      if (!item.매입가) {
        if (this.integratedRate) return '구매가(CNY)를 입력해 주세요.'
        return this.rateMissing ? '저장된 통합통관환율이 없어 매입가를 계산하지 않아요.' : '통합통관환율을 불러오지 못했어요.'
      }
      const 통관 = this.customsOnly(item.구매가)
      return [
        `통합통관 ${this.formatNumber(item.구매가)} CNY × ${this.integratedRate} × 1.1 = ${this.formatNumber(통관)}원`,
        `검수포장비 ${this.formatNumber(FEE_INSPECT)} × 1.1 = ${this.formatNumber(FEE_INSPECT_VAT)}원`,
        `바코드비 ${this.formatNumber(FEE_BARCODE)} × 1.1 = ${this.formatNumber(FEE_BARCODE_VAT)}원`,
        ...(this.shipKrw(item) ? [`중국 내 운임 ${this.formatNumber(item.중국내운임)} CNY ÷ ${this.shipQty(item)}개 = ${this.formatNumber(Math.round(perUnitShipCny(item.중국내운임, item.발주수량) * 100) / 100)} CNY/개 → ${this.formatNumber(this.shipKrw(item))}원(환율·부가세 포함)`] : ['중국 내 운임 입력 없음(0원으로 계산)']),
        `예상 밀크런비 ${this.formatNumber(item.밀크런예상 || 0)}원`,
        `합계 ${this.formatNumber(item.매입가)}원`
      ].join('\n')
    },
    // 개당 중국 내 운임의 원화 환산(환율·부가세 포함). 운임이 없으면 0
    shipKrw (item) {
      return customsOnly(perUnitShipCny(item.중국내운임, item.발주수량), this.integratedRate)
    },
    shipQty (item) {
      const q = Number(item.발주수량)
      return Number.isInteger(q) && q >= 1 ? q : 2
    },
    customsOnly (cny) {
      return customsOnly(cny, this.integratedRate)
    },
    milkrunTitle (item) {
      if (item.밀크런예상 == null) return '사이즈(가로·세로·높이)를 입력하면 계산돼요.'
      const base = `${item.밀크런박스} 박스에 ${this.formatNumber(item.밀크런적재)}개 적재 기준 개당 ${this.formatNumber(item.밀크런예상)}원`
      return item.밀크런계수추정
        ? base + '\n이 카테고리는 밀크런 실적이 적어 평균값으로 추정했어요.'
        : base + '\n같은 카테고리의 실제 밀크런 적재 실적을 반영했어요.'
    },
    // wing 실수수료 요청 — extension의 getCommission 파이프라인 재사용 (saveitemM과 동일)
    // categoryId를 실어 보내면 extension이 그 값을 쓰고, 없을 때만 keyword/link로 추론한다
    getCommission (item) {
      if (this.feeLoadingUid) return
      if (!item.판매가) {
        alert('판매가를 먼저 입력해 주세요.')
        return
      }
      this.currentFeeUid = item._uid
      this.feeLoadingUid = item._uid
      // 확장이 실패를 알려오면 아래 handleExtensionMessage 가 사유를 띄운다.
      // 이 타임아웃은 "응답 자체가 없는" 경우만 — 원인을 모르니 로그인 탓으로 단정하지 않는다.
      this.armExtTimeout('feeLoadingUid', 60, '수수료 응답이 오지 않았어요. 쿠플러스 확장이 켜져 있는지 확인한 뒤 다시 시도해 주세요.')
      // categoryId 가 있으면 넘긴다 — 없으면 확장이 키워드/링크로 추론한다(덜 정확).
      const info = {
        x: item.sizeX, y: item.sizeY, z: item.sizeZ, w: item.weight,
        categoryId: item.categoryId || '', price: item.판매가,
        type: item.type || 'trendFollower',
        keyword: item.키워드, link: item.벤치마킹링크 || item.판매링크 || ''
      }
      window.postMessage({
        greeting: 'getCommission',
        companyid: this.$store.state.user.companyid,
        suppliercode: this.$store.state.user.suppliercode,
        info: info
      }, '*')
    },
    // 확장 응답이 안 올 때를 대비한 타임아웃. key = 로딩 상태 필드명.
    armExtTimeout (key, seconds, message) {
      clearTimeout(this.extTimers[key])
      this.extTimers[key] = setTimeout(() => {
        if (!this[key]) return          // 이미 응답이 와서 풀렸으면 아무 것도 안 한다
        this[key] = null
        console.error(this.logPrefix, '확장 응답 없음 —', key)
        alert(message)
      }, seconds * 1000)
    },
    clearExtTimeout (key) {
      clearTimeout(this.extTimers[key])
      this.extTimers[key] = null
    },
    handleExtensionMessage (event) {
      const data = event.data
      if (!data) return

      // AI 등록 초안 — 쿠팡 카테고리·양식 응답(AI 로켓등록 화면과 같은 GET_PRODUCT_INFO)
      // 확장이 쿠팡 양식을 못 받으면 응답 대신 알림을 보낸다(CoupangRegAlert) — 기다리던 초안이면 그 글 그대로
      if (data.msg === 'CoupangRegAlert' && this.regRequestId) {
        console.error(this.logPrefix, '확장 알림', data.res)
        this.clearExtTimeout('regBusy')
        this.regRequestId = null
        this.regPendingData = null
        this.regRow = null
        this.regBusy = ''
        this.regError = data.res || '쿠팡 카테고리 정보를 가져오지 못했어요.'
        return
      }
      if (data.type === 'PRODUCT_INFO_RESPONSE') {
        // 기다리지 않은 응답·지난 요청 응답은 버린다 — 같은 응답으로 초안이 두 번 만들어지면 등록 한도를 두 칸 쓴다
        if (!this.regRequestId || (data.id && data.id !== this.regRequestId)) return
        this.finishRegDraft(data)
        return
      }

      // wing 실수수료 응답
      if (data.res && data.msg === 'getCommission') {
        this.clearExtTimeout('feeLoadingUid')
        this.feeLoadingUid = null
        // ★ 확장은 실패도 응답으로 보낸다(res:'fail'). 사유는 확장이 준 문구를 그대로 쓴다 —
        //   여기서 로그인 탓으로 뭉뚱그리면 카테고리를 못 찾은 경우에 엉뚱한 안내가 나간다.
        if (data.res !== 'success') {
          console.error(this.logPrefix, '수수료 조회 실패', data.message)
          alert(data.message || '수수료를 불러오지 못했어요.')
          return
        }
        const item = this.rows.find(r => r._uid === this.currentFeeUid)
        if (!item || !data.commission) return
        // VAT(×1.1) 포함 — margin.js 공식과 동일
        item.fullfillmentFee = Math.round((parseInt(data.commission.배송비) || 0) * 1.1)
        item.warehouseFee = Math.round((parseInt(data.commission.입출고비) || 0) * 1.1)
        item.commisionRate = Math.round((data.commission.판매수수료율 || 0) * 1.1 * 100) / 100
        item.수수료율 = item.commisionRate   // 수수료율 입력칸에 실측값 반영
        this.changeCost(item)
        return
      }

      // 키워드 1페이지 가격대 응답
      if (data.msg === 'getKeywordPriceRange') {
        this.clearExtTimeout('priceRangeLoadingUid')
        this.priceRangeLoadingUid = null
        const item = this.rows.find(r => r._uid === this.currentPriceRangeUid)
        if (!item) return
        // ★ 확장은 실패도 응답으로 보낸다(res:'fail'). 실패하면 **아무 것도 쓰지 않는다** —
        //   0 이나 추측값을 넣으면 그 가격대로 잰 위치가 전부 거짓이 된다.
        if (data.res !== 'success' || !data.range) {
          console.error(this.logPrefix, '1페이지 가격대 조회 실패', data.message)
          alert(data.message || '1페이지 가격을 불러오지 못했어요.')
          return
        }
        this.$set(item, ONE_PAGE_PRICE_FIELD, {
          min: data.range.min,
          max: data.range.max,
          count: data.count || null,
          // 어느 키워드로 잰 값인지 남긴다. 키워드를 바꾸면 옛 가격대가 남아 조용히 틀린다.
          키워드: data.keyword || item.키워드,
          조회일시: new Date().toISOString()
        })
        return
      }

      // 벤치마킹 쿠팡 상품 상세 응답
      // ★ 확장은 API 가 실패해도 Complete 로 보낸다(result.success=false). 그대로 두면
      //   아무 일도 안 일어난 것처럼 보이므로 여기서 갈라 안내한다.
      if (data.res === 'productDetailFromLinkComplete') {
        this.clearExtTimeout('benchLoadingUid')
        this.benchLoadingUid = null
        const item = this.rows.find(r => r._uid === this.currentBenchUid)
        if (!item) return
        const r = data.result || {}
        if (r.success === false || !r.data) {
          console.error(this.logPrefix, '벤치마킹 조회 실패', r.error || r)
          alert(this.benchFailMessage(r.error))
          return
        }
        this.applyBenchmark(item, data)
        return
      }
      if (data.res === 'productDetailFromLinkError') {
        // 확장이 요청을 보내기도 전에 난 예외 — 주소에서 상품번호를 못 뽑은 경우가 대부분이다.
        this.clearExtTimeout('benchLoadingUid')
        this.benchLoadingUid = null
        console.error(this.logPrefix, '벤치마킹 상품 조회 실패', data.error)
        alert(this.benchFailMessage(data.error))
        return
      }
      // 1688 상품정보는 확장을 거치지 않는다 — fetchAlibaba 가 서버에서 직접 받는다
    },
    /* ── 1페이지 가격대 · 가격 다이얼 ─────────────────────────── */
    /**
     * 키워드 1페이지 상품 가격의 min~max 를 확장에서 받아 행에 담는다.
     *
     * 왜 확장인가 — 화면은 쿠팡 검색을 직접 못 부른다(CORS·Akamai). 서버의
     * `/api/recipe/keyword-search` 는 CLI 가 uTLS 로 실행할 **레시피**를 주는 것이라
     * 브라우저에서 못 쓴다. 확장은 이미 `fetchkeywordb` 로 1페이지 HTML 을 파싱하고
     * 있고 그 결과에 `price` 가 들어 있다(background.js parseHTML). 전 구간 pub 이라
     * webstore 빌드에서도 돈다.
     *
     * ⚠ 비즈니스인사이트의 `priceRange25to75` 와 **다른 값**이라 다른 필드에 담는다.
     */
    fetchPriceRange (item) {
      if (!item || this.priceRangeLoadingUid) return
      const kw = String(item.키워드 || '').trim()
      if (!kw) {
        alert('키워드를 먼저 넣어 주세요. 그 키워드의 쿠팡 1페이지 가격으로 가격대를 잡아요.')
        return
      }
      this.currentPriceRangeUid = item._uid
      this.priceRangeLoadingUid = item._uid
      // 1페이지 1회 조회지만 확장이 모바일 판정에 걸려 재시도를 돌 수 있어 넉넉히 준다.
      this.armExtTimeout('priceRangeLoadingUid', 90,
        '1페이지 가격을 불러오지 못했어요. 쿠플러스 확장이 켜져 있는지 확인한 뒤 다시 시도해 주세요.')
      window.postMessage({ greeting: 'getKeywordPriceRange', keyword: kw }, '*')
    },
    /**
     * 다이얼 정방향 — % 를 움직이면 그 자리 가격이 판매가로 들어간다.
     *
     * 역방향(판매가 → %)은 `pricePosition` 이 이미 하고 있어 새로 만들 게 없다.
     * 이쪽도 `pctIn` 의 역함수(`valueAtPct`) 하나로 끝난다 — 두 방향이 같은 눈금·같은
     * 구간을 쓰므로 왕복해도 값이 안 어긋난다.
     *
     * 마진·마진율·ROI 는 판매가를 손으로 고치는 자리와 똑같이 `changeCost` 가 다시 낸다.
     */
    setPriceByPct (item, pct) {
      const dist = this.sig(item).pdist
      if (!dist) return
      const v = valueAtPct(pct, dist)
      if (v === null) return
      // 10원 단위로 맞춘다. 1% 눈금에서 나오는 8,913원 같은 값은 실제로 안 쓰는 가격이다.
      this.$set(item, '판매가', Math.round(v / 10) * 10)
      this.changeCost(item)
    },
    /**
     * 담긴 가격대가 **지금 키워드**의 것인가.
     *
     * 키워드를 고쳐도 옛 가격대는 그대로 남는다. 지우면 사용자가 잡아둔 판매가 위치가
     * 이유 없이 사라지고, 그냥 두면 다른 키워드의 자로 잰 위치를 맞는 값처럼 보여준다.
     * ⇒ 남겨두되 **틀릴 수 있다고 말한다.** 다시 불러오면 딱지가 사라진다.
     */
    priceRangeStale (row) {
      const d = row && row[ONE_PAGE_PRICE_FIELD]
      if (!d || !d.키워드) return false
      const kw = String(row.키워드 || '').trim()
      if (!kw) return false
      return String(d.키워드).trim() !== kw
    },
    // 다이얼 툴팁. 판매가가 있으면 막대 설명(barHint)을 그대로 쓰고,
    // 아직 없으면 「가격대는 이만큼이고, 끌면 값이 들어간다」만 말한다.
    pposTitle (row) {
      const s = this.sig(row)
      if (s.ppos) return s.ppos.hint
      const d = s.pdist
      if (!d) return this.pposEmptyHint()
      const lo = d.min != null ? d.min : d.q1
      const hi = d.max != null ? d.max : d.q3
      return '1페이지 가격대 ' + this.formatNumber(lo) + '~' + this.formatNumber(hi) + '원' +
        '\n판매가를 아직 안 정했어요. 막대를 끌면 그 자리 가격이 판매가로 들어가요.'
    },
    // 분포가 아예 없을 때의 설명. 보드마다 채우는 길이 달라 문구도 다르다.
    pposEmptyHint () {
      return this.isGrowth
        ? '키워드를 넣고 1페이지 가격대를 불러오면 여기서 판매가를 잡을 수 있어요.'
        : POS_EMPTY.ppos
    },
    // ── 1688 상품정보 불러오기 ─────────────────────────────────
    // 서버(/api/sourcing/board/offer-info)가 링크를 읽어 1688 오픈API 상세를 준다 — PC·모바일 주소, 앱 공유 단축 링크,
    // 공유 문구 통째, 상품번호만 넣어도 된다. 예전에는 확장이 PC 상세 페이지를 탭으로 열어 긁어서
    // PC 상세 주소가 아니면 아무 응답 없이 90초 뒤 실패했다(#60).
    async fetchAlibaba (item) {
      if (!item || this.alibabaLoadingUid) return
      const link = (this.pickerLink || item.alibabaLink || '').trim()
      if (!link) {
        alert('1688 링크를 입력해 주세요.')
        return
      }
      this.alibabaLoadingUid = item._uid
      try {
        const res = await axios.post('/api/sourcing/board/offer-info', { link })
        const r = res.data || {}
        if (r.res !== 'OK') {
          alert(r.alert || '1688 상품정보를 가져오지 못했어요. 구매가와 옵션은 직접 입력해 주세요.')
          return
        }
        this.applyAlibaba(item, r)
      } catch (error) {
        console.error(this.logPrefix, '1688 상품정보 조회 실패', error)
        alert('1688 상품정보를 가져오지 못했어요. 잠시 뒤 다시 시도해 주세요.')
      } finally {
        this.alibabaLoadingUid = null
      }
    },
    /**
     * 서버가 준 1688 상품정보를 행에 반영한다.
     *   r = { offerId, link(정규 PC 상세 주소), image, price(옵션 원가 최저), options:[{ 옵션명, 구매가 }] }
     * 구매가를 못 정한 상품(price null)은 비워 둔다(사용자가 직접 넣게). 추측값을 넣으면 마진이 조용히 틀어진다.
     */
    applyAlibaba (item, r) {
      this.$set(item, 'alibabaLink', r.link)
      this.pickerLink = r.link
      if (r.offerId) this.$set(item, 'offerId', String(r.offerId))
      if (r.image) this.$set(item, 'alibabaImage', r.image)
      const price = (typeof r.price === 'number' && r.price > 0) ? r.price : null
      if (price != null) {
        item.구매가 = price
        this.pickerPrice = price
      }

      // 옵션마다 원가가 다를 수 있어 옵션 행에 그 옵션 값을 넣는다(없으면 행 구매가)
      const rows = (Array.isArray(r.options) ? r.options : []).map(o => ({
        옵션명: String(o.옵션명 || ''),
        구매가: (typeof o.구매가 === 'number' && o.구매가 > 0) ? o.구매가 : (price != null ? price : (item.구매가 || null)),
        판매가: item.판매가 || null
      })).filter(o => o.옵션명)
      if (rows.length) {
        this.$set(item, '옵션', rows)
        rows.forEach(o => this.changeOptionCost(item, o, true))
      }

      // picker 후보로도 넣어 좌우 토글·미리보기가 그대로 동작하게 한다
      const cand = {
        detail_url: r.link,
        pic_url: r.image || '',
        price: price != null ? price : '',
        options: rows.map(o => ({ 옵션명: o.옵션명, 가격: o.구매가 }))
      }
      this.$set(item, '상품매칭', [cand])
      this.$set(item, 'currentindex', 0)

      this.changeCost(item)
      this.$forceUpdate()
      if (price == null) {
        alert('옵션과 이미지는 가져왔어요. 구매가(CNY)는 1688 이 알려주지 않아 직접 입력해 주세요.')
      }
    },
    // ── 벤치마킹 (쿠팡 상품링크) ───────────────────────────────
    fetchBenchmark (item) {
      if (this.benchLoadingUid) return
      const link = (item.벤치마킹링크 || '').trim()
      if (!link) {
        alert('쿠팡 상품링크를 입력해 주세요.')
        return
      }
      this.currentBenchUid = item._uid
      this.benchLoadingUid = item._uid
      // 확장이 실패를 알려오면 handleExtensionMessage 가 사유를 갈라 띄운다(benchFailMessage).
      // 이 타임아웃은 "응답 자체가 없는" 경우만 — 원인을 모르니 로그인 탓으로 단정하지 않는다.
      this.armExtTimeout('benchLoadingUid', 60, '상품정보 응답이 오지 않았어요. 쿠플러스 확장이 켜져 있는지 확인한 뒤 다시 시도해 주세요.')
      window.postMessage({
        greeting: 'getProductDetailFromLink',
        productLink: link,
        keyword: item.키워드 || ''
      }, '*')
    },
    /**
     * 벤치마킹 조회 실패 사유를 「화면에서 할 수 있는 행동」으로 옮긴다.
     *
     * ★ 이 함수가 생긴 이유: 실패를 전부 "주소 탓" 으로 단정하고 있었다. 2026-09-18 E2E 실측
     *   실패는 쿠팡 인증 거절(HTTP 401)이었는데 주소는 멀쩡했고, 사용자는 멀쩡한 주소를
     *   계속 고쳐 넣게 된다.
     *
     * 확장이 올려주는 사유는 문자열 하나뿐이라(background.js `getProductDetailFromLink` 의 error →
     * 서버 도구·CLI 가 던진 메시지) 아래 표식으로 가른다. 표식이 안 걸리면 **주소 탓으로
     * 단정하지 말고** 일반 오류로 돌린다.
     *
     * ⚠ 인증 판정을 주소 판정보다 먼저 한다. 쿠팡이 4xx 를 줄 때 couplus-tls 가 응답 본문을
     *   통째로 stderr 에 실어 보내는데, 그 본문에 상품번호 필드명이 섞여 있으면 주소 탓으로
     *   잘못 갈린다 — 고치려던 바로 그 오진이 된다.
     */
    benchFailMessage (err) {
      const msg = String(err == null ? '' : (err.message || err))
      // ⓪ 쿠팡 쇼핑 계정 미등록·로그인 필요 — 서버 도구가 「멀티계정관리」 안내 문구를 만들어 준다. 그 말을 그대로 보인다
      if (/쇼핑 계정/.test(msg)) {
        return '상품정보를 불러오려면 쿠팡 쇼핑 계정 로그인이 필요해요.\n' + msg
      }
      // ① 수집 프로그램이 안 떠 있음 (couplus-cli / couplus-tls)
      if (/CLI_NOT_FOUND|couplus-tls|curl\.exe|쿠플러스 서버에 연결하지 못했습니다/i.test(msg)) {
        return '상품정보를 불러오지 못했어요.\ncouplus-cli 가 실행 중인지 확인한 뒤 다시 시도해 주세요.'
      }
      // ② 인증·권한 거절 — 쿠팡 4xx, 레시피 401, 로그인 페이지(HTML) 회신,
      //    그리고 **로그인 페이지로 가는 302**. 2026-09-20 E2E 실측에서 윙 세션이 풀렸을 때
      //    실제로 온 것은 401 이 아니라 `HTTP 302 redirect: http://wing.coupang.com/login?…`
      //    이었다. 여기 302 가 없으면 ④ 로 떨어져 「잠시 뒤 다시 시도해 주세요」가 나가고,
      //    셀러는 로그인하러 가는 대신 같은 버튼을 계속 누른다 — 이 함수가 생긴 이유 그대로다.
      if (/HTTP 30[12]|sso\/login|HTTP 40[13]|RET9999|COUPANG_BLOCKED|로그인이 필요|HTML 응답/i.test(msg)) {
        return '쿠팡 로그인이 만료됐거나 권한이 없어요.\n새 탭에서 쿠팡에 로그인한 뒤 다시 시도해 주세요.'
      }
      // ③ 주소 형식 — 확장이 링크에서 상품번호를 못 뽑았을 때만
      if (/productId 또는 vendorItemId|Invalid URL|유효한 URL/i.test(msg)) {
        return '상품정보를 불러오지 못했어요.\n주소에 itemId·vendorItemId 가 포함돼 있는지 확인해 주세요.'
      }
      // ④ 그 밖 — 원인을 모르니 어느 쪽으로도 단정하지 않는다
      return '상품정보를 불러오지 못했어요. 잠시 뒤 다시 시도해 주세요.'
    },
    // 확장이 돌려준 상품 상세를 행에 반영.
    // 응답 스키마가 수집 경로마다 조금씩 달라 있는 키만 골라 채운다.
    applyBenchmark (item, data) {
      // 응답 래퍼: { success, data } — data = 서버 도구(쇼핑 상품상세) 해석 결과
      //   { productName, imageUrl, salePrice, categoryId(쿠팡 전시 카테고리), ... } (background.js getProductDetailFromLink).
      // 옛 응답 모양(rData)도 폴백으로 훑는다.
      const r = data.result || {}
      const body = r.rData || (r.data && r.data.rData) || r.data || {}
      const p = body.productInfo || body.product || body
      const name = p.productName || p.상품명 || p.title || p.name
      if (name) this.$set(item, '벤치마킹상품', String(name))
      const img = p.imageUrl || p.imgUrl || p.thumbnail || p.썸네일
      if (img) this.$set(item, '벤치마킹썸네일', String(img))
      const price = p.salePrice || p.price || p.판매가
      if (price != null) this.$set(item, '벤치마킹판매가', Number(price))
      // 상품명이 비어 있으면 벤치마킹 상품명을 그대로 쓴다(행 식별용)
      if (!item.상품명 && name) this.$set(item, '상품명', String(name))
      if (!item.썸네일 && img) this.$set(item, '썸네일', String(img))
      // categoryId 는 밀크런 압축계수를 고르는 축이라 꼭 잡아둔다
      const cat = p.categoryId || p.leafCategoryCode || r.categoryId
      if (cat) {
        this.$set(item, 'categoryId', Number(cat))
        this.changeCost(item)
      }
      if (data.vendorItemId) this.$set(item, '상품_vendorItemId', String(data.vendorItemId))
    },
    formatNumber (n) {
      return (n || n === 0) ? Number(n).toLocaleString() : '-'
    },
    thumbStyle (url) {
      return url
        ? { backgroundImage: `url(${url})` }
        : { backgroundColor: '#e6eaef' }
    },
    signClass (v) {
      if (v == null) return 'text-muted'
      return v >= 0 ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'
    },
    // 필수 단계(1688·스펙·마진) 모두 충족 여부 — 확정 게이트.
    essentialsMet (item) {
      return STAGE_DEFS.every(d => !d.essential || d.filled(item))
    },
    // 단계 상태(순서 무관 독립 판정):
    //  데이터 단계 — 채움 done / 필수 미충족 required / 선택 미충족 optional
    //  액션 단계(소싱확정·상품등록) — done / 충족돼 활성 current / 잠김 future
    stageState (item, idx) {
      if (idx <= STAGE_DEFS.length - 1) {
        const d = STAGE_DEFS[idx]
        if (d.filled(item)) return 'done'
        return d.essential ? 'required' : 'optional'
      }
      if (idx === STAGES.length - 2) {            // 소싱확정
        if (item.confirmed) return 'done'
        return this.essentialsMet(item) ? 'current' : 'future'
      }
      // 상품등록
      if (item.registered) return 'done'
      return item.confirmed ? 'current' : 'future'
    },
    // 액션 칩(소싱확정/상품등록) 클릭 라우팅 + 툴팁
    onStageClick (item, idx) {
      if (idx === STAGES.length - 1) this.toggleRegister(item)
      else if (idx === STAGES.length - 2) this.toggleConfirm(item)
    },
    stageTitle (idx) {
      if (idx === STAGES.length - 1) return '클릭하여 상품등록 토글'
      if (idx === STAGES.length - 2) return '클릭하여 소싱확정 토글'
      return STAGES[idx]
    },
    // '소싱확정' 칩 → 확정 토글 (필수 단계 충족 행만). 확정 해제 시 등록도 해제.
    toggleConfirm (item) {
      if (!item.confirmed && !this.essentialsMet(item)) {
        alert('필수 단계(1688·스펙·마진)를 채운 뒤 확정할 수 있어요.')
        return
      }
      const next = !item.confirmed
      this.$set(item, 'confirmed', next)
      if (!next) this.$set(item, 'registered', false)
    },
    // '상품등록' 칩 → 등록 토글 (소싱확정된 행만). 추후 등록 파이프라인 연동 시 자동화.
    toggleRegister (item) {
      if (!item.registered && !item.confirmed) {
        alert('소싱확정 후 상품등록할 수 있어요.')
        return
      }
      this.$set(item, 'registered', !item.registered)
    },
    addRow () {
      // 신규 임시행 — 저장 전까지 _id 없음. boardType 으로 보드끼리 갈린다.
      this.tmpSeq += 1
      const row = { _uid: 'new-' + this.tmpSeq, 카테고리: '', 키워드: '', 상품명: '' }
      if (this.boardType) row.boardType = this.boardType
      this.rows.unshift(this.normalizeRow(row))
    },
    // ── 상세 모달 ──────────────────────────────────────────────
    openDetail (item) {
      this.detailItem = item
      this.regDraft = null
      this.regError = ''
      if (!this.isGrowth && this.canRocketReg && item.rocketRegId) this.loadRegDraft(item)
      this.$bvModal.show('sb-detail-modal')
    },
    // ── AI 등록 초안 (수강생 보드) ─────────────────────────────
    // 행에 이어진 초안을 서버에서 다시 읽어 store 에 둔다. 단계 부품은 store.rocketProducts 안의 객체를 고친다.
    async loadRegDraft (item) {
      this.regBusy = '초안 불러오는 중…'
      try {
        const res = await axios.post('/api/aiRocketReg/getlist', {
          start: 0, end: Date.now() + 24 * 60 * 60 * 1000, limit: 1,
          filter: { registrationId: { in: [String(item.rocketRegId)] } }
        })
        const data = res.data || {}
        if (data['요청결과'] !== 'ok' || !Array.isArray(data.products)) {
          throw new Error(data.error || data.alert || '초안을 불러오지 못했어요. 잠시 뒤 다시 열어 주세요.')
        }
        const doc = data.products[0]
        if (!doc) {
          this.regError = `이어진 초안(등록번호 ${item.rocketRegId})을 찾지 못했어요. AI 로켓등록에서 지워졌을 수 있어요.`
          return
        }
        // store searchRocketProducts 와 같은 가공 — 상세이미지 원본 칸
        doc.originDetailImages = (doc.detailImages || []).map(img => {
          const url = typeof img === 'string' ? img : (img.img || img.url || '')
          return { originalUrl: url, img: url }
        })
        const list = this.$store.state.rocketProducts
        if (list.some(p => p.registrationId === doc.registrationId)) this.$store.commit('UPDATE_ROCKET_PRODUCT', doc)
        else this.$store.commit('ADD_ROCKET_PRODUCT', doc)
        if (this.detailItem !== item) return   // 그 사이 다른 행을 열었다
        this.regDraft = list.find(p => p.registrationId === doc.registrationId) || null
      } catch (e) {
        console.error(this.logPrefix, '초안 불러오기 실패', e)
        this.regError = e.message
      } finally {
        this.regBusy = ''
      }
    },
    // 오늘 만든 등록건 수 < 등록 한도 — AI 로켓등록 화면 addProduct 「슬롯 제한과 비교」와 같은 기준(분리 건 _N 은 안 셈 · 한도 없음 = 0 칸)
    async checkRegSlot () {
      const maxSlot = Number(this.$store.state.user.rocketRegSlot) || 0
      if (!Number.isFinite(maxSlot) || maxSlot <= 0) throw new Error('오늘 추가 가능한 상품 한도가 없습니다. 이용 중인 요금제나 한도를 확인해주세요.')
      const start = new Date(); start.setHours(0, 0, 0, 0)
      const res = await axios.post('/api/aiRocketReg/getlist', {
        start: start.getTime(), end: start.getTime() + 24 * 60 * 60 * 1000 - 1, limit: 200, fields: ['registrationId']
      })
      const data = res.data || {}
      if (data['요청결과'] !== 'ok' || !Array.isArray(data.products)) throw new Error(data.error || '오늘 등록 수를 확인하지 못했어요. 잠시 뒤 다시 시도해 주세요.')
      const today = data.products.filter(p => !String(p.registrationId || '').includes('_')).length
      if (today >= maxSlot) throw new Error(`오늘 추가 가능한 상품 수(${maxSlot}개)를 초과했습니다. 내일 다시 시도해주세요.`)
    },
    // 초안 만들기 ⓪ 쿠팡 카테고리 고르기 — 확장은 키워드로 카테고리를 고르지 않는다(빈 코드면 양식 없음 · CoupangRegAlert)
    pickRegCategory (item) {
      this.regError = ''
      this.regCategoryRow = item
      this.regBusy = '쿠팡 카테고리 목록 받는 중…'
      this.armExtTimeout('regBusy', 60, '쿠팡 카테고리 목록이 오지 않았어요. 쿠플러스 확장과 쿠팡 서플라이어허브 로그인을 확인한 뒤 다시 시도해 주세요.')
      this.$refs.regCategory.open()
    },
    onRegCategoryResponded () {
      this.clearExtTimeout('regBusy')
      this.regBusy = ''
    },
    onRegCategorySelected (category) {
      const item = this.regCategoryRow
      this.regCategoryRow = null
      if (item) this.startRegDraft(item, category)
    },
    // 초안 만들기 ① 한도·기본 설정 → ② 1688 상품정보 → ③ 확장에 쿠팡 양식 요청(고른 카테고리 · 응답은 finishRegDraft)
    async startRegDraft (item, category) {
      this.regError = ''
      const ds = this.defaultSettings
      if (!ds.brand || !ds.manufacturer || !ds.asInfo) {
        this.regError = 'AI 로켓등록 「기본 설정」에서 브랜드·제조사·A/S 정보를 먼저 넣어 주세요.'
        return
      }
      const link = item.alibabaLink || `https://detail.1688.com/offer/${item.offerId}.html`
      const productNo = String(item.offerId || ((link.match(/offer\/(\d+)/) || [])[1] || ''))
      if (!productNo) {
        this.regError = '1688 상품번호를 읽지 못했어요. 1688 상품을 다시 매칭해 주세요.'
        return
      }
      this.regBusy = '오늘 등록 한도 확인 중…'
      try {
        await this.checkRegSlot()
        this.regBusy = '1688 상품정보 불러오는 중…'
        const res = await axios.post('/api/aiPage/getProductInfo', [{ productId: parseInt(productNo) }])
        if (res.data && res.data.loginRequired) throw new Error('쿠플러스 로그인이 풀렸어요 — 새로고침하고 다시 로그인해 주세요.')
        const data = res.data && res.data.result && res.data.result[0] && res.data.result[0].result && res.data.result[0].result.result
        if (!data) throw new Error('1688 상품정보를 받지 못했어요. 잠시 뒤 다시 시도해 주세요.')
        this.newProduct = {
          link: link, keyword: item.키워드, brand: '',
          categoryPath: category.categoryPath, categoryCode: category.categoryCode,
          categoryPathString: category.categoryPathString, productFeature: ''
        }
        this.regRow = item
        this.regPendingData = data
        this.regRequestId = 'product_info_' + Date.now()
        this.regBusy = '쿠팡 카테고리·등록 양식 받는 중…'
        this.armExtTimeout('regBusy', 120, '쿠팡 카테고리 응답이 오지 않았어요. 쿠플러스 확장과 쿠팡 서플라이어허브 로그인을 확인한 뒤 다시 시도해 주세요.')
        // AI 로켓등록 requestProductInfoFromExtension 과 같은 요청
        window.postMessage({
          type: 'GET_PRODUCT_INFO',
          id: this.regRequestId,
          data: {
            keyword: item.키워드,
            productName: data.subject,
            productNameTrans: data.subjectTrans,
            productFeature: '',
            supplierCode: this.$store.state.user.suppliercode,
            categoryCode: category.categoryCode,
            categoryPathString: category.categoryPathString,
            isOption: false
          }
        }, '*')
      } catch (e) {
        console.error(this.logPrefix, '초안 만들기 실패', e)
        this.regError = e.message
        this.regBusy = ''
      }
    },
    // 초안 만들기 ④ 문서(AI 로켓등록과 같은 메서드) → 저장 → 행에 등록번호 잇기 → 다시 읽기
    async finishRegDraft (ext) {
      const productData = this.regPendingData
      const row = this.regRow
      this.regRequestId = null
      this.regPendingData = null
      this.regRow = null
      this.clearExtTimeout('regBusy')
      if (!ext.createResult || !ext.rocketRegInfo || !ext.rocketRegInfo.schemaString) {
        console.error(this.logPrefix, '쿠팡 양식 없는 응답', ext)
        this.regBusy = ''
        this.regError = '쿠팡 카테고리 양식을 가져오지 못했어요. 쿠팡 서플라이어허브 탭을 새로고침한 뒤 다시 시도해 주세요.'
        return
      }
      try {
        this.regBusy = '초안 저장 중…'
        const doc = await this.buildRocketRegProduct(productData, ext)
        const saveRes = await axios.post('/api/rocketReg/saveItems', [doc])
        if (!saveRes.data || saveRes.data.res !== 'success') throw new Error((saveRes.data && saveRes.data.alert) || '초안을 저장하지 못했어요.')
        const linkRes = await axios.post('/api/sourcing/board/save', [{ _id: row._id, rocketRegId: doc.registrationId }])
        if (!linkRes.data || linkRes.data.res !== 'OK') {
          throw new Error(`초안(등록번호 ${doc.registrationId})은 저장했지만 이 행과 잇지 못했어요. AI 로켓등록에서 이어 작업할 수 있어요.`)
        }
        this.$set(row, 'rocketRegId', doc.registrationId)
        await this.loadRegDraft(row)
      } catch (e) {
        console.error(this.logPrefix, '초안 저장 실패', e)
        this.regError = e.message
      } finally {
        this.regBusy = ''
      }
    },
    // AI 로켓등록 마법사를 그 단계에서 연다(옵션은 첫 옵션 — 옵션별은 「옵션별 작업 보기」)
    openRegWizard (workType) {
      this.$store.commit('SET_SELECTED_ROCKET_PRODUCT', this.regDraft)
      this.$store.commit('SET_SELECTED_OPTION', (this.regDraft.options || [])[0] || null)
      this.$store.commit('SET_SELECTED_WORK_TYPE', workType)
      this.$nextTick(() => this.$refs.regWizard.open())
    },
    openRegOptions () {
      this.$store.commit('SET_SELECTED_ROCKET_PRODUCT', this.regDraft)
      this.showRegOptions = true
    },
    // 옵션 모달이 옵션·단계를 store 에 정한 뒤 부른다
    openRegWizardFromOptions () {
      this.$nextTick(() => this.$refs.regWizard.open())
    },
    // 닫으면 단계 선택을 비우고(같은 단계를 다시 열 때 watch 가 돌게) 서버 값으로 다시 읽는다
    onRegWizardClosed () {
      this.$store.commit('SET_SELECTED_WORK_TYPE', '')
      if (this.detailItem && this.detailItem.rocketRegId) this.loadRegDraft(this.detailItem)
    },
    async onRegOptionDeleted (product) {
      try {
        await this.$store.dispatch('updateRocketProduct', product)
      } catch (e) {
        console.error(this.logPrefix, '옵션 삭제 저장 실패', e)
        this.regError = '옵션 삭제를 저장하지 못했어요: ' + e.message
      }
    },
    // 값 표시 포맷 — 배열은 줄바꿈 나열, 숫자는 천단위, 그 외 문자열
    formatVal (val) {
      if (val == null || val === '') return ''
      if (Array.isArray(val)) {
        return val.map(v => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join('\n')
      }
      if (typeof val === 'number') return val.toLocaleString()
      return String(val)
    },
    // 행에 존재하는 raw 필드만 클러스터 섹션으로 묶음 (값/필드 없는 섹션 제외)
    clusterSections (item) {
      if (!item) return []
      return CLUSTERS.map(c => {
        const fields = c.keys
          .filter(k => item[k] != null && item[k] !== '' && !(Array.isArray(item[k]) && !item[k].length))
          .map(k => ({ label: LABELS[k] || k, value: this.formatVal(item[k]) }))
        return { title: c.title, fields }
      }).filter(c => c.fields.length)
    },
    // ── 1688 picker ───────────────────────────────────────────
    openPicker (item) {
      this.pickerItem = item
      this.pickerLink = item.alibabaLink || ''
      this.pickerPrice = item.구매가 != null ? item.구매가 : null
      if (item.상품매칭 && item.상품매칭.length && item.currentindex == null) {
        this.$set(item, 'currentindex', 0)
      }
      this.$bvModal.show('sb-picker-modal')
    },
    pickerPrev () {
      const it = this.pickerItem
      if (!it || !it.상품매칭 || !it.상품매칭.length) return
      const cur = it.currentindex || 0
      this.$set(it, 'currentindex', cur <= 0 ? it.상품매칭.length - 1 : cur - 1)
    },
    pickerNext () {
      const it = this.pickerItem
      if (!it || !it.상품매칭 || !it.상품매칭.length) return
      const cur = it.currentindex || 0
      this.$set(it, 'currentindex', cur >= it.상품매칭.length - 1 ? 0 : cur + 1)
    },
    // 후보 옵션 배열 → 옵션명/가격 줄바꿈 (필드명 불명확 시 단순 렌더)
    pickerOptions (cand) {
      if (!cand) return ''
      const opts = cand.options || cand.skus || cand.옵션 || cand.sku
      if (!Array.isArray(opts) || !opts.length) return ''
      return opts.map(o => {
        if (o && typeof o === 'object') {
          const name = o.옵션명 || o.name || o.title || o.sku || ''
          const price = o.가격 || o.price || ''
          return [name, price].filter(v => v !== '' && v != null).join(' ')
        }
        return String(o)
      }).join('\n')
    },
    // URL에서 offerId 추출 (1688 detail URL 패턴)
    parseOfferId (url) {
      if (!url) return ''
      const s = String(url)
      const m = s.match(/offer\/(\d+)/) || s.match(/(\d{6,})/)
      return m ? m[1] : ''
    },
    /**
     * 후보 하나를 행에 반영 + 마진 갱신.
     * picker 의 「적용」과 인라인 심사의 타일 클릭이 **같은 경로**를 쓴다 — 두 벌이 되면
     * 한쪽만 고치는 사고가 난다.
     */
    applyCandidate (item, idx) {
      const cand = this.candidatesOf(item)[idx]
      if (!cand) return
      this.$set(item, 'currentindex', idx)
      if (cand.detail_url) this.$set(item, 'alibabaLink', cand.detail_url)
      const oid = this.parseOfferId(cand.detail_url || '')
      if (oid) this.$set(item, 'offerId', oid)
      if (cand.pic_url) this.$set(item, 'alibabaImage', cand.pic_url)
      if (cand.price != null && cand.price !== '') item.구매가 = Number(cand.price)
      this.changeCost(item)
    },
    // 선택 후보 → 행에 반영 + 마진 갱신
    applyPicker () {
      const item = this.pickerItem
      if (!item) return
      this.applyCandidate(item, item.currentindex || 0)
    },
    // 수동 1688 링크 적용 + 마진 갱신
    applyManualLink (item) {
      if (!item) return
      const link = (this.pickerLink || '').trim()
      if (!link) {
        alert('1688 링크를 입력해 주세요.')
        return
      }
      this.$set(item, 'alibabaLink', link)
      const oid = this.parseOfferId(link)
      if (oid) this.$set(item, 'offerId', oid)
      if (this.pickerPrice != null && this.pickerPrice !== '') {
        item.구매가 = Number(this.pickerPrice)
      }
      this.changeCost(item)
      this.$bvModal.hide('sb-picker-modal')
    }
  }
}
</script>

<style lang="scss" scoped>
/* 소싱관리 보드 — 작업 큐.
   컬럼 그룹 = 깔때기 단계이고, 막힌 단계는 그룹 통째로 색 덩어리가 된다.
   행은 1줄(29px)로 고정하고 정보량은 밀도 토글(하위 컬럼 개수)로 조절한다. */
.sourcing-board {
  $line: #e6eaef;
  $line2: #f0f2f6;
  $ink: #34405a;
  $ink2: #56627a;
  $ink3: #8b95a6;
  $mut: #c3cad6;
  $panel: #f6f8fb;
  $accent: #2f6fed;
  $accent-soft: #f0f5ff;
  $ok: #1f7d4d;
  $ok-soft: #eaf7f0;
  $warn: #b1740a;
  $warn-soft: #fff6e2;
  $bad: #b4452f;

  // ── ① 상단 바 ─────────────────────────────────────────────
  .sb-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: flex-end;
    justify-content: space-between;
    padding: 10px 0 8px;

    &__title { margin: 0 0 1px; font-size: 17px; font-weight: 700; }
    &__desc { margin: 0; font-size: 11.5px; color: $ink3; }
    &__tools { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
  }
  .sb-rate {
    margin: 2px 0 0;
    font-size: 11.5px;
    color: $ink2;

    b { color: $accent; }
    &__sep { color: $mut; margin: 0 4px; }
  }
  .sb-search {
    width: 190px;
    padding: 3px 9px;
    font-size: 12px;
    border: 1px solid #dfe3e8;
    border-radius: 6px;

    &:focus { outline: none; border-color: $accent; }
  }
  .sb-dens {
    display: inline-flex;
    border: 1px solid #dfe3e8;
    border-radius: 6px;
    overflow: hidden;

    &__b {
      padding: 3px 10px;
      font-size: 11.5px;
      white-space: nowrap;
      border: 0;
      background: #fff;
      color: $ink3;
      cursor: pointer;

      & + & { border-left: 1px solid #dfe3e8; }
      &--on { background: $accent; color: #fff; font-weight: 700; }
    }
  }
  // 조건서 칩 — 시트를 여는 유일한 입구. chrome 에 한 칸만 더 붙인다
  .sb-spec {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    white-space: nowrap;
    font-size: 11.5px;
    font-weight: 600;
    color: $ink2;
    background: #fff;
    border: 1px solid #dfe3e8;
    border-radius: 6px;
    cursor: pointer;

    &:hover { border-color: $ink3; color: $ink; }
    b { margin-left: 6px; font-weight: 700; font-size: 10.5px; color: $accent; }
  }

  // ── ② 깔때기 한 줄 — 폭이 건수에 비례해 병목이 즉시 보인다 ──
  .sb-funnel {
    display: flex;
    gap: 4px;
    margin-bottom: 8px;

    &__track { display: flex; flex: 1; gap: 3px; min-width: 0; }
    &__seg {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 5px;
      min-width: 0;
      overflow: hidden;
      padding: 4px 6px;
      font-size: 11px;
      white-space: nowrap;
      color: $ink3;
      background: $panel;
      border: 1px solid $line;
      border-radius: 6px;
      cursor: pointer;

      b { font-weight: 700; color: $ink; font-variant-numeric: tabular-nums; }
      &--all { flex: none; padding: 4px 12px; }
      &--on { border-color: $accent; background: $accent-soft; color: $accent; }
      // 건수 0 인 단계는 자리만 남기고 라벨을 접는다 (폭 비례를 해치지 않게)
      &--empty { flex: 0 0 24px; padding: 4px 0; opacity: .45; }
      &--empty b { font-weight: 400; color: $mut; }
      &--empty .sb-funnel__t { display: none; }
    }
    &__t { overflow: hidden; text-overflow: ellipsis; }
  }

  // ── ③ 표 ─────────────────────────────────────────────────
  // 바깥이 가로 스크롤, 안쪽이 max-content 라 넓어져도 행 배경·테두리가 끝까지 칠해진다.
  // 표가 자체 스크롤 영역이어야 헤더 sticky 가 산다(페이지가 스크롤되면 안 붙는다).
  // 예전 CDataTable 이 `fitHeight` 로 하던 것과 같은 동작이다.
  .sb-scroll {
    overflow: auto;
    max-height: calc(100vh - 260px);
    min-height: 200px;
    border: 1px solid $line;
    border-radius: 8px;
    background: #fff;
  }
  .sb-table { width: max-content; min-width: 100%; }

  // 헤더 2단을 한 덩어리로 붙인다. 두 줄을 따로 sticky 하면 1단 높이를 px 로
  // 하드코딩해야 하고, 폰트가 바뀌는 순간 2단이 1단을 덮는다.
  .sb-head {
    position: sticky;
    top: 0;
    z-index: 3;
  }

  .sb-hg,
  .sb-hf,
  .sb-row {
    display: grid;
    grid-template-columns: var(--sb-tmpl);
    align-items: center;
  }
  .sb-hg {
    background: $panel;
    border-bottom: 1px solid $line2;

    &__c {
      padding: 4px 8px;
      font-size: 10px;
      font-weight: 700;
      line-height: 1.35;
      letter-spacing: .06em;
      color: $ink3;
      white-space: nowrap;
      overflow: hidden;
    }
  }
  .sb-hf {
    background: $panel;
    border-bottom: 1px solid $line;

    &__c {
      display: flex;
      align-items: center;
      min-width: 0;
      overflow: hidden;
      padding: 3px 8px;
      font-size: 10.5px;
      line-height: 1.35;
      color: $ink3;
    }
    &__b {
      width: 100%;
      border: 0;
      background: none;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      &:not(:disabled) { cursor: pointer; }
      &:not(:disabled):hover { color: $ink; }
      &:disabled { cursor: default; }
      &--on { color: $accent; font-weight: 700; }
      &.sb-c--n { justify-content: flex-end; }
    }
  }

  .sb-row {
    min-height: 29px;
    background: #fff;
    border-bottom: 1px solid $line2;

    &:last-child { border-bottom: 0; }
    &:hover { background: #f8fafd; }
    &--on { background: $accent-soft; }
    // 심사를 연 행 — 아래 펼친 영역과 같은 바탕이라 한 덩어리로 읽힌다
    &--open { background: $accent-soft; border-bottom-color: transparent; }
    // 조건서가 가린 행. 「보기」로 불러온 상태라 회색으로 남기고, 지우지 않는다
    &--masked {
      background: #fbfcfd;
      color: $ink3;
      box-shadow: inset 3px 0 0 $mut;

      .sb-c { opacity: .55; }
      &:hover .sb-c { opacity: 1; }
    }
  }

  .sb-c {
    display: flex;
    align-items: center;
    gap: 5px;
    min-width: 0;
    padding: 2px 7px;
    font-size: 12px;
    // 테마 전역 line-height(29px)가 그대로 오면 칸 하나가 35px 이 돼 1줄 행이 깨진다
    line-height: 1.35;
    color: $ink;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &--n { justify-content: flex-end; font-variant-numeric: tabular-nums; }
    &--sel { justify-content: center; padding: 0; }
  }
  // 그룹 경계 세로선 — 그룹의 첫 칸에만 붙는다
  .sb-gs { border-left: 1px solid $line; }

  // 좌측 고정: 체크박스와 상품명. 상세 밀도에서만 실제로 가로 스크롤이 생긴다.
  .sb-c--stick0,
  .sb-c--stick1 {
    position: sticky;
    z-index: 2;
    background: inherit;
  }
  .sb-c--stick0 { left: 0; }
  // 고정 컬럼의 오른쪽 모서리. 없으면 가로 스크롤 때 뒤 컬럼이 상품명 밑으로
  // 아무 경계 없이 미끄러져 들어와 두 값이 한 칸처럼 읽힌다.
  .sb-c--stick1 {
    left: 34px;
    box-shadow: 3px 0 4px -3px rgba(52, 64, 90, .35);
  }

  // 막힌 단계 = 그룹 통째로 덮는 색 덩어리. 뒤 단계는 「앞이 먼저」
  .sb-blk {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 2px 8px;
    font-size: 11.5px;
    line-height: 1.35;
    white-space: nowrap;
    overflow: hidden;

    &--todo {
      background: $warn-soft;
      color: $warn;
      font-weight: 600;
      cursor: pointer;

      &::before { content: "\25C6"; font-size: 8px; }
      &:hover { background: #ffeec6; }
    }
    &--wait { color: $mut; opacity: .8; cursor: default; }
  }

  .sb-mut { color: $mut; }
  .sb-strong { font-weight: 700; }
  .sb-thumb {
    width: 20px;
    height: 20px;
    border-radius: 4px;
    background-size: cover;
    background-position: center;
    flex: none;
  }
  .sb-nm { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; font-weight: 500; }
  .sb-off { color: #e8730c; font-size: 11px; overflow: hidden; text-overflow: ellipsis; }

  // 진입점수 — ROI 미산출이면 점선 + 「잠정」
  .sb-score {
    display: inline-flex;
    align-items: baseline;
    gap: 3px;
    font-variant-numeric: tabular-nums;

    b { font-size: 12.5px; font-weight: 700; }
    s {
      text-decoration: none;
      font-size: 9px;
      color: $warn;
      background: $warn-soft;
      border-radius: 6px;
      padding: 0 4px;
    }
    &--t3 b { color: $ok; }
    &--t2 b { color: $accent; }
    &--t1 b { color: $ink3; }
    &--prov b { border-bottom: 1px dashed currentColor; }
  }

  // 1p 내 위치 막대 — 막대의 굵은 눈금이 currentColor 를 쓴다
  .sb-pos {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #9aa3b2;

    svg { flex: none; }
    b { font-size: 10px; font-weight: 600; color: $ink2; }
    s {
      text-decoration: none;
      font-size: 9px;
      color: $warn;
      background: $warn-soft;
      border-radius: 6px;
      padding: 0 4px;
    }
    &--up { color: #1f9d57; }
    &--dn { color: #d64b3c; }
    &--edge { color: #e08b18; }
    // 가격대는 아는데 판매가를 아직 안 정한 상태. 눈금선이 없으니 라벨로만 말한다
    &--unset b { color: $ink3; font-weight: 500; }
  }

  // 가격 다이얼 — 막대 위에 range 를 투명하게 겹친다.
  // range 를 쓰는 이유는 드래그·키보드(←/→)·포커스를 브라우저가 주기 때문이다.
  .sb-dial {
    position: relative;
    display: inline-flex;
    flex: none;
    line-height: 0;

    // range 자체는 투명이라 포커스링이 안 보인다 — 테두리는 감싼 쪽에 그린다
    &:focus-within {
      outline: 2px solid $accent;
      outline-offset: 1px;
      border-radius: 3px;
    }

    &__r {
      position: absolute;
      left: 0;
      top: 0;
      width: 44px;
      height: 14px;
      margin: 0;
      padding: 0;
      cursor: ew-resize;
      opacity: 0;               // 눈금은 아래 SVG 가 그린다. range 는 조작만 받는다
      -webkit-appearance: none;
      appearance: none;
      background: transparent;
    }
  }

  // 기회 배지 — 값 5종 + 없음. 임계값은 보드에 없다(담기가 판정한다)
  .sb-opp {
    font-size: 10px;
    font-weight: 700;
    padding: 1px 6px;
    border-radius: 8px;
    white-space: nowrap;

    &--gap { background: $ok-soft; color: $ok; }
    &--sat { background: #f1f3f6; color: #7a8699; }
    &--weak { background: #fdeeee; color: $bad; }
    &--thin,
    &--unknown { color: $ink3; border: 1px dashed #cfd6e0; }
  }

  // 판정 칩 = 심사 펼침 트리거. 모든 단계에서 눌린다(앞이 비어도 반려·보류는 된다)
  .sb-verdict {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 11px;
    font-weight: 700;
    padding: 2px 7px;
    border: 0;
    border-radius: 8px;
    background: none;
    white-space: nowrap;
    cursor: pointer;

    &--go { background: $accent-soft; color: $accent; }
    &--fin { background: $ok-soft; color: $ok; }
    &--wait { color: $ink3; font-weight: 400; }
    &--hold { background: $warn-soft; color: $warn; }
    &--drop { background: #fdeeee; color: $bad; }
  }
  // 등록 대기 행 옆 승인 상태 칩 — 누르면 승인 시트
  .sb-apr {
    margin-left: 4px;
    font-size: 10.5px;
    font-weight: 700;
    padding: 1px 6px;
    border: 1px solid #e8590c;
    border-radius: 8px;
    background: #fff4e6;
    color: #d9480f;
    white-space: nowrap;
    cursor: pointer;

    &--approved { border-color: $ok; background: $ok-soft; color: $ok; }
    &--reapprove { border-color: $bad; background: #fdeeee; color: $bad; }
    &--ambig { border-style: dashed; background: #fff; }
  }

  // 인라인 입력칸 (표 안)
  .sb-in {
    width: 100%;
    min-width: 0;
    padding: 1px 5px;
    font-size: 11.5px;
    border: 1px solid #e3e7ed;
    border-radius: 4px;
    background: #fff;

    &--n { text-align: right; }
    &--xs { text-align: center; padding: 1px 2px; }
    &--t { text-align: left; }
    // number 스피너가 칸 폭을 16px 씩 먹어 15,900 이 「1590」 으로 잘렸다.
    // 표 안에서는 스피너를 쓸 일이 없다.
    &[type='number'] { -moz-appearance: textfield; }
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
    &:focus {
      outline: none;
      border-color: $accent;
      box-shadow: 0 0 0 2px rgba(47, 111, 237, .12);
    }
  }
  .sb-mini {
    position: relative;
    flex: none;
    width: 20px;
    height: 20px;
    font-size: 10px;
    line-height: 1;
    color: $ink3;
    background: #fff;
    border: 1px solid #e3e7ed;
    border-radius: 4px;
    cursor: pointer;

    &:hover { color: $accent; border-color: #c5d6f8; }
    &:disabled { color: $mut; cursor: default; }
    &--1688 { color: #e8730c; }
    b {
      position: absolute;
      top: -6px;
      right: -6px;
      min-width: 13px;
      height: 13px;
      line-height: 13px;
      padding: 0 3px;
      font-size: 8.5px;
      font-weight: 700;
      color: #fff;
      background: #e8730c;
      border-radius: 7px;
    }
  }
  .sb-chipbtn {
    padding: 2px 8px;
    font-size: 10.5px;
    white-space: nowrap;
    color: $accent;
    background: $accent-soft;
    border: 1px solid #d6e2ff;
    border-radius: 9px;

    &:hover { background: #e1ecff; }
    &:disabled { color: $mut; background: #f7f8fa; border-color: #edf0f3; }
  }

  // ── 승격 배너 / 알림 / 가려짐 줄 — 전부 조건부 한 줄이다. 패널로 키우지 말 것 ──
  .sb-promo,
  .sb-notice,
  .sb-mask,
  .sb-choke {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    padding: 5px 10px;
    font-size: 12px;
    border-radius: 7px;
  }
  .sb-promo {
    color: #5a4a12;
    background: $warn-soft;
    border: 1px solid #ecd9a6;

    &__tag {
      flex: none;
      padding: 1px 7px;
      font-size: 10px;
      font-weight: 700;
      color: #fff;
      background: #c08a12;
      border-radius: 8px;
    }
    &__t { font-weight: 600; }
    &__w { color: #8a7433; font-size: 11px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    &__a { margin-left: auto; display: flex; gap: 5px; flex: none; }
  }
  // 조건서가 가린 행 — 「몇 건이 왜 안 보이는지」를 한 줄로 말하고 되돌릴 길을 준다
  .sb-mask {
    color: $ink2;
    background: $panel;
    border: 1px solid $line;

    &__t { font-weight: 600; }
    .sb-btn { margin-left: auto; flex: none; }
  }
  .sb-choke {
    color: #5a4a12;
    background: $warn-soft;
    border: 1px solid #ecd9a6;

    &__t { font-weight: 600; }
    &__a { margin-left: auto; display: flex; gap: 5px; flex: none; }
  }
  .sb-notice {
    color: $ink2;
    background: $panel;
    border: 1px solid $line;

    &__x {
      margin-left: auto;
      flex: none;
      border: 0;
      background: none;
      font-size: 15px;
      line-height: 1;
      color: $ink3;
      cursor: pointer;
    }
  }

  // ── ③-b 인라인 심사 — 표 안에서 행 아래로 펼쳐진다 ──────────
  .sb-exp {
    // ⚠ `.sb-table` 이 `width: max-content` 다. 그냥 두면 이 안의 긴 문장 한 줄이
    //    **표 전체를 넓혀** 기본 밀도에서도 가로 스크롤이 생긴다(실측으로 당했다).
    //    폭 기여를 0 으로 두고, 확정된 표 폭을 min-width 로 되받는다.
    width: 0;
    min-width: 100%;
    background: $accent-soft;
    border-bottom: 1px solid $line;
  }
  .sb-exp__cell { padding: 0 12px 12px; }
  .sb-exp__in {
    // 표가 가로로 넘칠 때 판정 버튼이 화면 밖으로 밀리지 않게 폭을 잡아둔다.
    // 넓은 화면에서도 1,300px 짜리 심사판은 읽기 나쁘다 — 위아래 두 목적이 같다.
    // 좁은 화면(사이드바 접힘)은 1040 이 화면보다 넓어 오른쪽(판정·협상 «정리 저장»·우리 대화)이 밖으로 밀렸다 —
    // 표 스크롤 칸(100vw − 36) 안 칸 여백(24) 만큼으로 줄인다(390 실측 · cpl-nego-negomemo-1)
    max-width: min(1040px, calc(100vw - 60px));
    background: #fff;
    border: 1px solid $line;
    border-radius: 7px;
    overflow: hidden;
  }
  // 위: 레퍼런스 ↔ 후보 / 아래: 근거 ↔ 판정. 좁으면 세로로 접힌다.
  .sb-exp__sheet { display: grid; grid-template-columns: 148px minmax(0, 1fr); }
  .sb-exp__vd {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 260px;
    border-top: 1px solid $line2;
  }
  @media (max-width: 860px) {
    .sb-exp__sheet,
    .sb-exp__vd { grid-template-columns: 1fr; }
  }
  .sb-exp__none { margin: 0; font-size: 11.5px; color: $ink3; }

  // 1688 협상 메모 — 정리(사람·도구) + 대화 원본(읽기만) · cpl-nego-negomemo-1
  .sb-nego-tag {
    display: inline-block;
    margin-left: 4px;
    padding: 0 5px;
    border: 1px solid $line;
    border-radius: 3px;
    font-size: 10.5px;
    line-height: 16px;
    color: $ink3;
    white-space: nowrap;
  }
  .sb-nego {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px;
    border-top: 1px solid $line2;

    &__hd { display: flex; align-items: baseline; gap: 8px; }
    &__at { font-size: 10.5px; color: $ink3; font-weight: 400; }
    &__chips { display: flex; flex-wrap: wrap; gap: 4px; }
    &__chip {
      padding: 1px 6px;
      border: 1px solid $line2;
      border-radius: 3px;
      font-size: 11.5px;
      max-width: 100%;
      overflow-wrap: anywhere;
      b { font-weight: 600; color: $ink3; margin-right: 2px; }
    }
    &__lb { margin: 0; font-size: 11.5px; font-weight: 600; display: flex; gap: 6px; align-items: baseline; }
    &__ta { width: 100%; min-height: 64px; resize: vertical; font-size: 12.5px; line-height: 1.45; }
    &__bar { display: flex; justify-content: flex-end; align-items: center; gap: 8px; }
    &__n { font-size: 10.5px; color: $ink3; }
    &__tg { align-self: flex-start; }
    &__th { display: flex; flex-direction: column; gap: 10px; max-height: 360px; overflow-y: auto; }
    &__grp { display: flex; flex-direction: column; gap: 4px; }
    &__seller { font-size: 11px; font-weight: 700; color: $ink3; }
    &__msg {
      display: flex;
      flex-direction: column;
      max-width: 78%;
      padding: 4px 8px;
      border-radius: 6px;
      font-size: 12px;
      line-height: 1.4;
      background: #f4f5f7;
      &--us { align-self: flex-end; background: #eaf2ff; }
      &--them { align-self: flex-start; }
    }
    &__who { font-size: 10px; color: $ink3; }
    &__tx { white-space: pre-wrap; overflow-wrap: anywhere; }
  }
  @media (max-width: 860px) {
    .sb-nego__msg { max-width: 92%; }
  }

  .sb-cap {
    display: block;
    font-size: 9.5px;
    letter-spacing: .07em;
    font-weight: 700;
    color: $ink3;
  }

  .sb-ref {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px;
    border-right: 1px solid $line2;

    &__img {
      width: 100%;
      aspect-ratio: 1;
      border: 1px solid $line;
      border-radius: 5px;
      background-size: cover;
      background-position: center;
    }
    &__m { font-size: 11px; color: $ink2; line-height: 1.5; b { color: $ink; font-size: 12px; } }
    &__lnk { font-size: 11px; color: $accent; }
  }

  .sb-cands {
    padding: 10px;

    &__cap {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 7px;
    }
    &__grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(78px, 1fr));
      gap: 6px;
    }
  }
  // 후보 타일 — 실패한 이미지도 자리를 지킨다. 번호가 밀리면 사람이 잘못 고른다
  .sb-cand {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 4px;
    background: #fcfdff;
    border: 1px solid $line;
    border-radius: 5px;
    cursor: pointer;

    &:hover { border-color: $ink3; }
    &--on { border-color: $accent; box-shadow: 0 0 0 2px rgba(47, 111, 237, .16); }
    &__im {
      width: 100%;
      aspect-ratio: 1;
      object-fit: cover;
      border-radius: 3px;
      background: #eef1f5;

      &--x {
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 9px;
        color: #97a0af;
        background: #e9ecf1;
      }
    }
    &__lb {
      display: flex;
      justify-content: space-between;
      font-size: 9.5px;
      color: $ink3;
      font-variant-numeric: tabular-nums;

      b { color: $ink; }
    }
    &__cf { font-size: 9.5px; font-weight: 700; text-align: center; color: $ink2; }
  }

  .sb-why {
    padding: 10px 12px;

    ul { margin: 5px 0 0; padding-left: 15px; }
    // 표 안이라 위쪽에서 white-space:nowrap 이 내려온다. 여기서는 접혀야 한다.
    li {
      font-size: 11.5px;
      line-height: 1.6;
      color: $ink2;
      white-space: normal;
    }
    &__fl { color: $warn; }
  }

  .sb-acts {
    display: flex;
    flex-direction: column;
    gap: 7px;
    padding: 10px 12px;
    border-left: 1px solid $line2;

    &__rw { display: flex; gap: 6px; }
    &__rw .sb-btn { flex: 1; }
    &__cur { margin: 0; font-size: 11px; color: $ink3; b { color: $ink; } }
  }
  @media (max-width: 860px) {
    .sb-acts { border-left: 0; border-top: 1px solid $line2; }
    // 좁은 화면 — 판정 버튼 줄도 협상 칸처럼 왼쪽 화면 폭 안에 두고, 넘치면 줄바꿈한다
    .sb-acts__rw { max-width: calc(100vw - 48px); flex-wrap: wrap; }
  }

  .sb-btn {
    padding: 5px 10px;
    font-size: 11.5px;
    font-weight: 600;
    color: $ink2;
    background: #fff;
    border: 1px solid #dfe3e8;
    border-radius: 6px;
    cursor: pointer;

    &:hover:not(:disabled) { border-color: $ink3; color: $ink; }
    &:disabled { color: $mut; background: #f7f8fa; cursor: default; }
    &--ok { color: $accent; background: $accent-soft; border-color: #d6e2ff; }
    &--bad { color: $bad; background: #fdeeee; border-color: #f0cec5; }
  }

  // 반려 사유 — 두 무리로 갈라 보인다. 이 구분이 보여야 품질 신고가 조건서로 안 샌다
  .sb-rs { display: flex; flex-direction: column; gap: 8px; }
  .sb-rs__etc { display: flex; gap: 5px; }
  .sb-gl {
    display: block;
    font-size: 9.5px;
    letter-spacing: .05em;
    font-weight: 700;
    color: $ink3;

    span { letter-spacing: 0; font-weight: 400; }
  }
  .sb-rbs { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 4px; }
  .sb-rb {
    padding: 2px 8px;
    font-size: 11px;
    color: $ink2;
    background: $panel;
    border: 1px solid $line;
    border-radius: 99px;
    cursor: pointer;

    &:hover { border-color: $ink3; color: $ink; }
    &--qa { border-style: dashed; }
    &__k { margin-left: 4px; font-size: 9.5px; color: $ink3; }
  }

  /* ── ④ 조건서 시트 ──────────────────────────────────────────
     평소엔 닫혀 있다. 열려도 표 위에 얹히기만 하고 chrome 을 밀지 않는다. */
  .sb-spec__in {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px 14px 20px;
    font-size: 12px;
    color: $ink;

    .sb-in { padding: 4px 7px; font-size: 12px; }
  }
  .sb-spec__note { margin: 0; font-size: 11.5px; color: $ink3; }
  .sb-spec__pick {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    font-size: 11.5px;
    color: $ink3;

    .sb-in { flex: 1; }
  }
  .sb-spec__add { align-self: flex-start; }

  .sb-rules { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 7px; }
  .sb-rule {
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: 8px 10px;
    background: $panel;
    border: 1px solid $line;
    border-radius: 7px;

    &--off { opacity: .5; }
    &__a { display: flex; align-items: flex-start; gap: 8px; }
    &__tx { flex: 1; font-size: 12.5px; font-weight: 600; line-height: 1.4; }
    &__b { display: flex; align-items: center; flex-wrap: wrap; gap: 4px; }
    &__c { display: flex; align-items: center; gap: 6px; }
    &__og { flex: 1; font-size: 10.5px; color: $ink3; }
    &__dp { margin-left: auto; font-size: 10.5px; font-weight: 700; color: $bad; }
    &__x {
      flex: none;
      padding: 1px 6px;
      font-size: 10.5px;
      color: $ink3;
      background: #fff;
      border: 1px solid $line;
      border-radius: 5px;
      cursor: pointer;

      &:hover:not(:disabled) { color: $ink; border-color: $ink3; }
      &:disabled { color: $mut; cursor: default; }
    }
  }
  // on/off 는 실제 button 이다. 모양만 스위치이고 aria-pressed 로 상태를 말한다
  .sb-sw {
    position: relative;
    flex: none;
    margin-top: 2px;
    width: 28px;
    height: 16px;
    padding: 0;
    border: 0;
    border-radius: 99px;
    background: $mut;
    cursor: pointer;

    s {
      position: absolute;
      top: 2px;
      left: 2px;
      width: 12px;
      height: 12px;
      border-radius: 99px;
      background: #fff;
      transition: transform .15s;
    }
    &--on { background: $accent; s { transform: translateX(12px); } }
    &:disabled { cursor: default; opacity: .6; }
  }
  .sb-rchip {
    padding: 1px 7px;
    font-size: 10px;
    font-weight: 600;
    color: $ink2;
    background: #fff;
    border: 1px solid $line;
    border-radius: 99px;

    &--hard { color: $bad; background: #fdeeee; border-color: #f0cec5; }
    &--soft { color: $ink2; }
    &--calc { color: $accent; background: $accent-soft; border-color: #d6e2ff; }
    &--cond { color: $ok; background: $ok-soft; border-color: #cfe9db; }
    // 표를 못 거르는 규칙은 흐리게. 거르는 척하면 사용자가 왜 안 걸리는지 모른다
    &--mute { color: $ink3; border-style: dashed; }
  }

  .sb-draft {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 9px 10px;
    background: $accent-soft;
    border: 1px solid #d6e2ff;
    border-radius: 7px;

    &__rw { display: flex; gap: 6px; align-items: flex-end; }
    &__f { flex: 1; margin: 0; font-size: 10.5px; color: $ink3; }
    &__op { flex: none; width: 74px; }
    &__use { margin: 0; font-size: 11.5px; color: $ink2; input { margin-right: 5px; } }
    &__h { margin: 0; font-size: 10.5px; line-height: 1.45; color: $ink3; }
  }

  .sb-calc {
    padding: 9px 10px;
    background: $panel;
    border: 1px solid $line;
    border-radius: 7px;

    &__dl {
      display: grid;
      grid-template-columns: 128px minmax(0, 1fr);
      gap: 3px 8px;
      margin: 6px 0 0;

      dt { font-size: 10.5px; font-weight: 600; color: $ink3; }
      dd { margin: 0; font-size: 10.5px; color: $ink2; overflow-wrap: anywhere; }
    }
  }

  // 키보드로 닿는 자리는 포커스가 보여야 한다. 마우스 클릭에는 안 뜬다.
  .sb-blk,
  .sb-verdict,
  .sb-cand,
  .sb-btn,
  .sb-rb,
  .sb-mini,
  .sb-chipbtn,
  .sb-spec,
  .sb-sw,
  .sb-rule__x,
  .sb-prog__step {
    &:focus-visible {
      outline: 2px solid $accent;
      outline-offset: 1px;
    }
  }

  .sb-empty { padding: 30px 12px; text-align: center; font-size: 12.5px; color: $ink3; }
  .sb-foot {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 10px 0;
    font-size: 12px;
    color: $ink2;
  }
  .sb-page {
    padding: 2px 10px;
    font-size: 11.5px;
    background: #fff;
    border: 1px solid #dfe3e8;
    border-radius: 6px;

    &:disabled { color: $mut; }
  }

  // 진도 체크리스트 (상세 밀도)
  .sb-prog {
    display: inline-flex;
    gap: 3px;
    align-items: center;
    white-space: nowrap;

    &__step {
      font-size: 10px;
      font-weight: 600;
      padding: 1px 6px;
      border-radius: 9px;
      border: 1px solid transparent;

      &--done { background: $ok-soft; color: $ok; }
      &--current { background: $warn-soft; color: #9c6f00; border-color: #d99a00; }
      &--future {
        background: repeating-linear-gradient(45deg, #fcfcfd, #fcfcfd 4px, #f3f4f6 4px, #f3f4f6 8px);
        color: #aab2c0;
      }
      &--optional { background: #f7f8fa; color: #b8c0cc; border-color: #edf0f3; }
      &--required { background: #fdeeee; color: $bad; border-color: #e9b4a8; }
      &--clickable { cursor: pointer; }
    }
  }

  // ── 모달 ─────────────────────────────────────────────────
  .sb-dt {
    margin-bottom: 14px;

    &__title {
      font-size: 12px;
      font-weight: 700;
      color: #2f4a7d;
      border-bottom: 2px solid $line;
      padding-bottom: 3px;
      margin-bottom: 6px;
    }
    &__table {
      width: 100%;
      font-size: 12px;

      th {
        width: 38%;
        color: #7a8699;
        font-weight: 600;
        padding: 3px 6px;
        vertical-align: top;
      }
      td {
        padding: 3px 6px;
        color: $ink;
        white-space: pre-line;
        word-break: break-word;
      }
      tr + tr { border-top: 1px solid #f1f3f6; }
    }
  }
  .sb-dtbench__row {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 12px;
    margin-bottom: 5px;

    b { margin-left: auto; white-space: nowrap; }
    // 표 안 입력칸을 그대로 쓰면 모달에서 폭이 접히고 테두리가 거의 안 보인다
    .sb-in {
      flex: 1;
      padding: 4px 8px;
      font-size: 12px;
      border-color: #dfe3e8;
    }
  }

  .sb-pick {
    display: flex;
    align-items: center;
    gap: 8px;

    &__body { flex: 1; text-align: center; }
    &__img { max-width: 160px; max-height: 160px; border-radius: 6px; }
    &__price { font-weight: 700; font-size: 13px; margin-top: 4px; }
    &__opt {
      font-size: 11px;
      color: $ink2;
      background: #f7f8fa;
      border-radius: 5px;
      padding: 5px 7px;
      margin-top: 5px;
      text-align: left;
      white-space: pre-line;
      max-height: 110px;
      overflow: auto;
    }
    &__link { display: block; font-size: 11.5px; color: #e8730c; margin-top: 4px; }
    &__idx { font-size: 10.5px; color: #7a8699; margin-top: 4px; }
    &__lbl { font-size: 11px; font-weight: 600; color: $ink2; }
  }

  // 옵션별 마진 모달 — 표 본체와 달리 일반 table 이라 입력칸 스타일이 따로 있다
  .sb-input {
    width: 100%;
    max-width: 90px;
    padding: 2px 6px;
    font-size: 11.5px;
    text-align: right;
    border: 1px solid #dfe3e8;
    border-radius: 5px;
    background: #fff;

    &:focus {
      outline: none;
      border-color: $accent;
      box-shadow: 0 0 0 2px rgba(47, 111, 237, .12);
    }
    &--sm { max-width: none; text-align: left; font-size: 11px; }
  }
  .sb-opt__table {
    width: 100%;
    font-size: 12px;

    th {
      background: $panel;
      padding: 6px 8px;
      font-weight: 600;
      color: $ink2;
      border-bottom: 1px solid $line;
    }
    td {
      padding: 4px 8px;
      border-bottom: 1px solid #f0f2f6;
      vertical-align: middle;
    }
  }
  .sb-opt__del {
    border: none;
    background: none;
    color: #b6bfcd;

    &:hover { color: #e05656; }
  }
}
</style>
